import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { jwtDecode } from 'jwt-decode';
import axios from 'axios';
import { browserSupportsWebAuthn, startRegistration, startAuthentication } from '@simplewebauthn/browser';
import CompanyAdministration from './CompanyAdministration';
import CompanySettingsManagement from './CompanySettingsManagement';
import WorkScheduleManagement from './WorkScheduleManagement';
import PersonalManagement from './PersonalManagement';
import LeaveManagement from './LeaveManagement';
import OvertimeManagement from './OvertimeManagement';
import AdministrationAdvanced from './AdministrationAdvanced';
import UserWorkSettings from './UserWorkSettings';
import { PERSONNEL_STATUS_LEGEND, resolvePersonnelStatus } from './personnelStatus';

// 🌐 URL CANÓNICA DE API DEL DASHBOARD
// Se declara a nivel de módulo para que todos los componentes, callbacks
// y efectos del archivo puedan utilizarla sin depender de otro componente.
const obtenerUrlBaseDashboard = (prefijo, subRuta = '') => {
  const p = String(prefijo || '').replace(/^\/+|\/+$/g, '');
  const s = String(subRuta || '').replace(/^\/+/, '');

  let rutaBase = p;
  if (p === 'attendance') rutaBase = 'api/attendance';
  else if (p === 'api') rutaBase = 'api';

  return `/${rutaBase}${s ? `/${s}` : ''}`.replace(/\/{2,}/g, '/');
};


// ================================================================
// 🕒 FECHA/HORA CORPORATIVA — MÉRIDA, YUCATÁN
// ================================================================
// Las asistencias se almacenan como instantes reales (DataTypes.DATE).
// Estas funciones convierten ese instante a la zona horaria corporativa
// únicamente al mostrarlo o calcular horas.
const ZONA_HORARIA_ASISTENCIA = 'America/Merida';

// Clasificación única de procedencia de una marcación.
// GPS no determina el origen: una marca puede ser WEB, TERMINAL, AJUSTE o
// generada automáticamente y cualquiera de ellas puede o no tener GPS.
const obtenerEtiquetaOrigenMarca = (marca) => {
  if (!marca) return { texto: 'ORIGEN DESCONOCIDO', color: '#718096' };

  if (marca.isAuditAdjustment || marca.source === 'AJUSTE') {
    return {
      texto: `AJUSTE${marca.auditReference ? ` · ${marca.auditReference}` : ''}`,
      color: '#b7791f'
    };
  }

  if (marca.supersededByAdjustmentId) {
    return {
      texto: `ORIGINAL · sustituido por ajuste ${marca.auditReference || marca.supersededByAdjustmentId}`,
      color: '#718096'
    };
  }

  switch (String(marca.source || '').toUpperCase()) {
    case 'TERMINAL':
      return { texto: 'ORIGINAL · TERMINAL', color: '#3182ce' };
    case 'AUTO_CIERRE':
      return { texto: 'GENERADA · CIERRE AUTOMÁTICO', color: '#7e22ce' };
    case 'WEB':
    case '':
      return { texto: 'ORIGINAL · WEB', color: '#38a169' };
    default:
      return { texto: `GENERADA · ${String(marca.source)}`, color: '#dd6b20' };
  }
};

const obtenerPartesTimestamp = (timestamp) => {
  if (!timestamp) return null;

  let fecha;
  if (timestamp instanceof Date) {
    fecha = timestamp;
  } else {
    const texto = String(timestamp).trim();
    if (!texto) return null;

    // Las marcas de AttendanceAdjustment pueden llegar como fecha/hora
    // sin zona (por ejemplo, "2026-09-29T23:55"). Ese valor es una hora
    // corporativa de America/Merida, no una hora UTC. Si lo convertimos a UTC
    // antes de mostrarlo/ordenarlo, una salida de 23:55 termina como 17:55 y
    // puede quedar en un lugar cronológicamente incorrecto frente a una marca
    // física. Por eso los timestamps sin zona se conservan como hora de pared
    // de Mérida. Los timestamps que sí traen Z u offset siguen representando
    // un instante real y se convierten normalmente.
    if (!/Z$/i.test(texto) && !/[+-]\d{2}:?\d{2}$/.test(texto)) {
      const match = texto.match(/^(\d{4})[-\/](\d{2})[-\/](\d{2})[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?$/);
      if (match) {
        const [, anio, mes, dia, hora, minuto, segundo = '00'] = match;
        const fechaPlana = `${anio}-${mes}-${dia}`;
        const horaPlana = `${hora}:${minuto}`;
        const horaCompleta = `${hora}:${minuto}:${segundo}`;
        const minutos = Number(hora) * 60 + Number(minuto);
        return {
          fechaPlana,
          horaPlana,
          horaCompleta,
          minutos,
          // Clave comparable de la hora de pared corporativa.
          milisegundos: Date.UTC(Number(anio), Number(mes) - 1, Number(dia), Number(hora), Number(minuto), Number(segundo))
        };
      }
    }

    fecha = new Date(texto);
  }

  if (isNaN(fecha.getTime())) return null;

  const partes = new Intl.DateTimeFormat('en-CA', {
    timeZone: ZONA_HORARIA_ASISTENCIA,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23'
  }).formatToParts(fecha);

  const valores = {};
  partes.forEach(({ type, value }) => {
    if (type !== 'literal') valores[type] = value;
  });

  const fechaPlana = `${valores.year}-${valores.month}-${valores.day}`;
  const horaPlana = `${valores.hour}:${valores.minute}`;
  const horaCompleta = `${valores.hour}:${valores.minute}:${valores.second}`;
  const minutos = Number(valores.hour) * 60 + Number(valores.minute);

  return { fechaPlana, horaPlana, horaCompleta, minutos, milisegundos: fecha.getTime() };
};

const formatearTimestampMerida = (timestamp) => {
  const partes = obtenerPartesTimestamp(timestamp);
  if (!partes) return 'Fecha no disponible';

  return `${partes.fechaPlana} ${partes.horaCompleta}`;
};

// Orden cronológico de la fecha/hora que realmente ve el usuario.
// No dependemos del parseo implícito del navegador ni del ID de inserción.
const obtenerClaveCronologicaTimestamp = (timestamp) => {
  const partes = obtenerPartesTimestamp(timestamp);
  if (!partes?.fechaPlana || !partes?.horaCompleta) return Number.NEGATIVE_INFINITY;
  const [anio, mes, dia] = partes.fechaPlana.split('-').map(Number);
  const [hora, minuto, segundo] = partes.horaCompleta.split(':').map(Number);
  const valor = Date.UTC(anio, mes - 1, dia, hora, minuto, segundo);
  return Number.isFinite(valor) ? valor : Number.NEGATIVE_INFINITY;
};

const compararMarcasCronologicamente = (a, b) => {
  const pa = obtenerPartesTimestamp(a?.timestamp);
  const pb = obtenerPartesTimestamp(b?.timestamp);

  // La prioridad absoluta es la fecha/hora que realmente se muestra en la
  // tabla. Así una aclaración sin zona y una marca física con zona nunca
  // pueden quedar en posiciones distintas de las que ve el usuario.
  const textoA = pa?.fechaPlana && pa?.horaCompleta
    ? `${pa.fechaPlana} ${pa.horaCompleta}`
    : '';
  const textoB = pb?.fechaPlana && pb?.horaCompleta
    ? `${pb.fechaPlana} ${pb.horaCompleta}`
    : '';

  if (textoA && textoB && textoA !== textoB) {
    return textoB.localeCompare(textoA);
  }

  const ta = obtenerClaveCronologicaTimestamp(a?.timestamp);
  const tb = obtenerClaveCronologicaTimestamp(b?.timestamp);
  return tb - ta || Number(b?.id || 0) - Number(a?.id || 0);
};


// FUNCIÓN AUXILIAR: Genera un color corporativo fijo y único basado en el ID del empleado
const obtenerColorPorId = (id, isDarkMode) => {
  const coloresLight = ['#3182ce', '#38a169', '#dd6b20', '#805ad5', '#319795', '#d69e2e', '#e53e3e', '#4a5568'];
  const coloresDark  = ['#63b3ed', '#68d391', '#f6ad55', '#b794f4', '#4fd1c5', '#f6e05e', '#fc8181', '#cbd5e0'];
  const lista = isDarkMode ? coloresDark : coloresLight;
  return lista[id % lista.length];
};

// ================================================================
// 📱 ESTILOS RESPONSIVOS GLOBALES DEL DASHBOARD
// Se mantienen fuera de AdminPanel para que estén disponibles
// tanto para administradores como para empleados.
// ================================================================
const responsiveStyles = `
    .dashboard-mobile-safe,
    .dashboard-mobile-safe * {
      box-sizing: border-box;
    }

    .dashboard-mobile-safe {
      width: 100%;
      max-width: 100%;
      min-width: 0;
    }

    .dashboard-mobile-safe * {
      min-width: 0;
    }

    .dashboard-mobile-safe img,
    .dashboard-mobile-safe input,
    .dashboard-mobile-safe select,
    .dashboard-mobile-safe textarea,
    .dashboard-mobile-safe button {
      max-width: 100%;
    }

    .dashboard-mobile-safe h1,
    .dashboard-mobile-safe h2,
    .dashboard-mobile-safe h3,
    .dashboard-mobile-safe h4,
    .dashboard-mobile-safe p,
    .dashboard-mobile-safe span,
    .dashboard-mobile-safe strong,
    .dashboard-mobile-safe small,
    .dashboard-mobile-safe label {
      overflow-wrap: anywhere;
    }

    .dashboard-calendar-grid {
      width: 100%;
      min-width: 0;
      grid-template-columns: repeat(7, minmax(0, 1fr)) !important;
    }

    .dashboard-calendar-grid > * {
      min-width: 0 !important;
      max-width: 100%;
    }

    .dashboard-calendar-grid > div > div {
      min-width: 0;
      max-width: 100%;
    }

    .dashboard-report-filters {
      width: 100%;
      min-width: 0;
    }

    .dashboard-report-filters > div {
      min-width: 0 !important;
      max-width: 100%;
    }

    .dashboard-personal-history {
      width: 100%;
      min-width: 0;
    }

    .dashboard-personal-history-table {
      width: 100%;
      table-layout: fixed;
    }

    .dashboard-admin-shell { align-items: flex-start !important; }

    .dashboard-mobile-topbar,
    .dashboard-mobile-bottomnav,
    .dashboard-mobile-overlay {
      display: none;
    }

    .dashboard-admin-desktop-alertbar {
      display: flex;
      align-items: center;
      gap: 10px;
      width: 100%;
      box-sizing: border-box;
      min-height: 58px;
      margin-bottom: 12px;
      padding: 9px 12px;
      border: 1px solid var(--dashboard-mobile-border, #e2e8f0);
      border-radius: 12px;
      background: var(--dashboard-mobile-bg, #ffffff);
      box-shadow: 0 3px 12px rgba(0,0,0,.05);
    }

    .dashboard-desktop-alert-button,
    .dashboard-desktop-profile-button {
      position: relative;
      min-height: 38px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 7px;
      border: 1px solid var(--dashboard-mobile-border, #e2e8f0);
      border-radius: 9px;
      padding: 7px 11px;
      background: transparent;
      color: inherit;
      font-weight: 700;
      cursor: pointer;
      white-space: nowrap;
    }

    .dashboard-desktop-alert-button:hover,
    .dashboard-desktop-profile-button:hover {
      background: rgba(49, 130, 206, .08);
    }

    .dashboard-desktop-alert-badge {
      position: absolute;
      top: -7px;
      right: -7px;
      min-width: 18px;
      height: 18px;
      padding: 0 4px;
      border-radius: 999px;
      background: #e53e3e;
      color: #fff;
      font-size: 9px;
      line-height: 18px;
      font-weight: 800;
      text-align: center;
      border: 2px solid var(--dashboard-mobile-bg, #fff);
      box-sizing: border-box;
    }

    .dashboard-alert-anchor { position: relative; display: inline-flex; flex: 0 0 auto; }
    .dashboard-alert-popover {
      position: absolute; top: calc(100% + 8px); right: 0; width: min(390px, calc(100vw - 24px)); max-height: min(480px, calc(100vh - 90px));
      overflow: auto; z-index: 1500; border: 1px solid var(--dashboard-mobile-border, #e2e8f0); border-radius: 12px;
      background: var(--dashboard-mobile-bg, #fff); color: inherit; box-shadow: 0 12px 32px rgba(0,0,0,.18);
    }
    .dashboard-alert-popover-header { display:flex; justify-content:space-between; gap:10px; align-items:center; padding:12px 13px; border-bottom:1px solid var(--dashboard-mobile-border, #e2e8f0); font-size:12px; }
    .dashboard-alert-popover-header span { font-size:10px; opacity:.7; }
    .dashboard-alert-popover-item { width:100%; display:flex; gap:9px; align-items:flex-start; text-align:left; padding:11px 13px; border:0; border-bottom:1px solid var(--dashboard-mobile-border, #e2e8f0); background:transparent; color:inherit; cursor:pointer; white-space:normal; box-sizing:border-box; overflow-wrap:anywhere; word-break:break-word; }
    .dashboard-alert-popover-item:hover { background: rgba(49,130,206,.08); }
    .dashboard-alert-popover-item strong, .dashboard-alert-popover-item span { display:block; white-space:normal; overflow-wrap:anywhere; word-break:break-word; }
    .dashboard-alert-popover-item strong { font-size:12px; margin-bottom:3px; }
    .dashboard-alert-popover-item span:not(.dashboard-alert-popover-dot) { font-size:11px; line-height:1.35; opacity:.78; }
    .dashboard-alert-popover-dot { width:8px; height:8px; margin-top:4px; flex:0 0 8px; border-radius:50%; background:#e53e3e; }
    .dashboard-alert-popover-empty { padding:20px 14px; text-align:center; font-size:12px; opacity:.7; }
    .dashboard-alert-popover-footer { width:100%; padding:10px 13px; border:0; background:transparent; color:#3182ce; font-weight:800; font-size:11px; text-align:center; cursor:pointer; }
    .dashboard-alert-popover-footer:hover { background:rgba(49,130,206,.08); }
    .dashboard-alert-popover-viewport { box-sizing: border-box; overflow-y: auto; -webkit-overflow-scrolling: touch; }

    .dashboard-alert-button { position: relative; }
    .dashboard-alert-badge {
      position: absolute; top: 1px; right: 1px; min-width: 15px; height: 15px;
      padding: 0 3px; border-radius: 999px; background: #e53e3e; color: #fff;
      font-size: 8px; line-height: 15px; font-weight: 800; text-align: center;
      border: 1px solid var(--dashboard-mobile-bg, #fff);
    }

    .dashboard-mobile-content {
      min-width: 0;
    }

    .dashboard-personal-desktop-shell {
      display: flex;
      align-items: flex-start;
      gap: 18px;
      width: 100%;
      min-width: 0;
    }

    .dashboard-personal-sidebar {
      width: 228px;
      flex: 0 0 228px;
      position: sticky;
      top: 12px;
      max-height: calc(100vh - 24px);
      overflow-y: auto;
      padding: 12px;
      border-radius: 12px;
      border: 1px solid #e2e8f0;
      background: #f8fafc;
      box-shadow: 0 3px 12px rgba(0,0,0,.06);
    }

    .dashboard-personal-content {
      flex: 1;
      min-width: 0;
    }

    .dashboard-personal-desktop-header {
      display: flex !important;
    }

    @media (max-width: 768px) {
      .dashboard-admin-desktop-alertbar,
      .dashboard-personal-desktop-header {
        display: none !important;
      }

      .dashboard-mobile-topbar {
        display: flex;
        position: sticky;
        top: 0;
        z-index: 1100;
        width: 100%;
        min-height: 54px;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        padding: 8px 10px;
        border-bottom: 1px solid var(--dashboard-mobile-border, #e2e8f0);
        background: var(--dashboard-mobile-bg, #ffffff);
        backdrop-filter: blur(12px);
      }

      .dashboard-mobile-topbar-title {
        min-width: 0;
        flex: 1;
        font-size: 14px;
        font-weight: 800;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .dashboard-mobile-topbar-subtitle {
        font-size: 10px;
        font-weight: 500;
        opacity: .72;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      /* En móvil el panel de la campana se posiciona respecto a la ventana,
         no respecto al botón. Así nunca queda fuera de pantalla. */
      .dashboard-alert-anchor .dashboard-alert-popover {
        position: fixed;
        top: 62px;
        left: 8px;
        right: 8px;
        width: auto;
        max-width: none;
        max-height: calc(100dvh - 132px);
        overflow-y: auto;
        -webkit-overflow-scrolling: touch;
        z-index: 1400;
      }

      .dashboard-alert-popover-viewport {
        box-sizing: border-box !important;
      }


      .dashboard-mobile-iconbutton {
        width: 38px;
        height: 38px;
        flex: 0 0 38px;
        border: 1px solid var(--dashboard-mobile-border, #e2e8f0);
        border-radius: 10px;
        background: transparent;
        color: inherit;
        font-size: 18px;
        cursor: pointer;
      }

      .dashboard-mobile-bottomnav {
        display: grid;
        grid-template-columns: repeat(4, minmax(0, 1fr));
        position: fixed;
        left: 0;
        right: 0;
        bottom: 0;
        z-index: 1200;
        min-height: 64px;
        padding: 6px 8px max(6px, env(safe-area-inset-bottom));
        border-top: 1px solid var(--dashboard-mobile-border, #e2e8f0);
        background: var(--dashboard-mobile-bg, #ffffff);
        box-shadow: 0 -4px 18px rgba(15, 23, 42, .08);
      }

      .dashboard-mobile-bottomnav button {
        border: none;
        background: transparent;
        color: inherit;
        border-radius: 10px;
        padding: 5px 3px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 2px;
        min-width: 0;
        font-size: 10px;
        font-weight: 700;
        cursor: pointer;
      }

      .dashboard-mobile-bottomnav button.active {
        background: rgba(49, 130, 206, .12);
        color: #3182ce;
      }

      .dashboard-mobile-bottomnav .nav-icon {
        font-size: 18px;
        line-height: 1;
      }

      .dashboard-mobile-bottomnav.dashboard-personal-bottomnav {
        grid-template-columns: repeat(5, minmax(0, 1fr));
      }

      .dashboard-mobile-bottomnav.dashboard-personal-bottomnav button {
        font-size: 9px;
      }

      .dashboard-mobile-bottomnav.dashboard-personal-bottomnav .nav-icon {
        font-size: 17px;
      }

      .dashboard-mobile-drawer {
        position: fixed !important;
        top: 0 !important;
        bottom: 0 !important;
        left: 0 !important;
        width: min(88vw, 330px) !important;
        max-height: none !important;
        overflow-y: auto !important;
        z-index: 1300 !important;
        border-radius: 0 16px 16px 0 !important;
        box-shadow: 8px 0 28px rgba(15, 23, 42, .20) !important;
        transform: translateX(0);
      }

      .dashboard-mobile-overlay {
        display: block;
        position: fixed;
        inset: 0;
        z-index: 1250;
        background: rgba(15, 23, 42, .42);
        backdrop-filter: blur(2px);
      }

      .dashboard-mobile-content {
        width: 100% !important;
        padding: 10px 10px calc(156px + env(safe-area-inset-bottom)) !important;
        scroll-padding-bottom: calc(156px + env(safe-area-inset-bottom));
      }

      .dashboard-mobile-content > div:first-child {
        margin-bottom: 10px !important;
      }

      .dashboard-mobile-safe .dashboard-mobile-bottomnav {
        padding-left: max(8px, env(safe-area-inset-left));
        padding-right: max(8px, env(safe-area-inset-right));
      }

      .dashboard-admin-shell {
        flex-direction: row !important;
        align-items: stretch !important;
        gap: 0 !important;
        margin-bottom: 0 !important;
      }

      .dashboard-admin-shell > aside {
        position: fixed !important;
        top: 0 !important;
        left: 0 !important;
        width: min(88vw, 330px) !important;
        max-height: none !important;
        height: 100dvh !important;
        padding: 14px !important;
        overflow-y: auto !important;
        border-radius: 0 16px 16px 0 !important;
        z-index: 1300 !important;
        box-shadow: 8px 0 28px rgba(15, 23, 42, .20) !important;
      }

      .dashboard-admin-shell > aside.dashboard-mobile-drawer-closed {
        transform: translateX(-105%) !important;
        pointer-events: none;
      }

      .dashboard-admin-shell > div {
        width: 100% !important;
      }

      .dashboard-mobile-safe {
        margin-top: 0 !important;
        padding: 0 0 calc(156px + env(safe-area-inset-bottom)) !important;
        scroll-padding-bottom: calc(156px + env(safe-area-inset-bottom));
        overflow-x: hidden;
      }

      /* La barra inferior es fija; el contenido necesita una zona de seguridad
         propia para que el ultimo boton, tarjeta o texto nunca quede oculto. */
      .dashboard-mobile-safe.dashboard-personal-history {
        padding-bottom: calc(156px + env(safe-area-inset-bottom)) !important;
        scroll-padding-bottom: calc(156px + env(safe-area-inset-bottom)) !important;
      }

      .dashboard-mobile-safe.dashboard-personal-history > main {
        padding-bottom: calc(48px + env(safe-area-inset-bottom)) !important;
      }

      .dashboard-mobile-safe > main,
      .dashboard-mobile-safe .dashboard-mobile-content,
      .dashboard-mobile-safe .dashboard-personal-content {
        padding-bottom: calc(48px + env(safe-area-inset-bottom)) !important;
        scroll-padding-bottom: calc(48px + env(safe-area-inset-bottom));
      }

      .dashboard-personal-desktop-shell {
        display: block !important;
      }

      .dashboard-personal-sidebar {
        display: none !important;
      }

      .dashboard-personal-content {
        width: 100% !important;
        padding: 0 !important;
      }

      .dashboard-mobile-safe .dashboard-personal-history {
        padding: 0 !important;
      }

      .dashboard-mobile-safe .dashboard-personal-history > main {
        gap: 12px !important;
      }
    }

    @media (max-width: 700px) {
      .dashboard-mobile-safe [style*="grid-template-columns"] {
        grid-template-columns: minmax(0, 1fr) !important;
      }

      .dashboard-mobile-safe {
        margin-top: 10px !important;
        padding: 10px !important;
        overflow-x: hidden;
      }

      .dashboard-mobile-safe > section {
        width: 100% !important;
        max-width: 100% !important;
        min-width: 0 !important;
        padding: 12px !important;
        margin-bottom: 16px !important;
      }

      .dashboard-mobile-safe h1 {
        font-size: 21px !important;
        line-height: 1.2 !important;
      }

      .dashboard-mobile-safe h2 {
        font-size: 17px !important;
        line-height: 1.25 !important;
      }

      .dashboard-mobile-safe h3 {
        font-size: 14px !important;
        line-height: 1.3 !important;
      }

      .dashboard-mobile-safe button {
        white-space: normal !important;
        overflow-wrap: anywhere;
      }

      .dashboard-mobile-safe input,
      .dashboard-mobile-safe select,
      .dashboard-mobile-safe textarea {
        min-width: 0 !important;
        width: 100%;
      }

      .dashboard-mobile-safe > div:first-of-type {
        width: 100%;
        min-width: 0;
      }

      .dashboard-mobile-safe .dashboard-tab-navigation {
        display: grid !important;
        grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
        width: 100% !important;
        gap: 6px !important;
        padding-bottom: 10px !important;
      }

      .dashboard-mobile-safe .dashboard-tab-navigation button {
        width: 100% !important;
        min-width: 0 !important;
        padding: 9px 6px !important;
        font-size: 11px !important;
      }

      .dashboard-mobile-safe .dashboard-calendar-grid {
        gap: 2px !important;
        margin-bottom: 12px !important;
      }

      .dashboard-mobile-safe .dashboard-calendar-grid > div {
        min-width: 0 !important;
        min-height: 58px !important;
        padding: 2px !important;
        border-radius: 4px !important;
        overflow: hidden;
      }

      .dashboard-mobile-safe .dashboard-calendar-grid > div > span {
        font-size: 9px !important;
      }

      .dashboard-mobile-safe .dashboard-calendar-grid > div > div {
        max-height: 45px !important;
        min-width: 0 !important;
        overflow-x: hidden;
      }

      .dashboard-mobile-safe .dashboard-calendar-grid > div > div > div {
        min-width: 0 !important;
        max-width: 100% !important;
        font-size: 8px !important;
        padding: 1px 2px !important;
        overflow: hidden !important;
        text-overflow: ellipsis !important;
        white-space: nowrap !important;
      }

      .dashboard-mobile-safe .dashboard-calendar-grid > div:nth-child(-n+7) {
        min-height: 25px !important;
        height: auto !important;
        border: 0 !important;
        background: transparent !important;
        padding: 2px 0 !important;
      }

      .dashboard-mobile-safe .dashboard-calendar-grid > div:nth-child(-n+7) {
        font-size: 9px !important;
      }

      .dashboard-mobile-safe .dashboard-report-filters {
        grid-template-columns: minmax(0, 1fr) !important;
        gap: 10px !important;
      }

      .dashboard-mobile-safe .dashboard-report-filters > div {
        width: 100% !important;
      }

      .dashboard-mobile-safe .dashboard-report-filters select,
      .dashboard-mobile-safe .dashboard-report-filters input {
        width: 100% !important;
      }

      .dashboard-mobile-safe .dashboard-report-actions {
        width: 100% !important;
        display: grid !important;
        grid-template-columns: minmax(0, 1fr) !important;
      }

      .dashboard-mobile-safe .dashboard-report-actions button {
        width: 100% !important;
      }

      .dashboard-personal-history > div:first-child {
        flex-direction: column !important;
        align-items: stretch !important;
      }

      .dashboard-personal-history > div:first-child > div {
        width: 100% !important;
      }

      .dashboard-personal-history > div:first-child select {
        width: auto !important;
        max-width: 100% !important;
      }

      .dashboard-mobile-safe .dashboard-personal-history-table,
      .dashboard-mobile-safe .dashboard-personal-history-table tbody {
        display: block;
        width: 100%;
      }

      .dashboard-mobile-safe .dashboard-personal-history-table thead {
        display: none;
      }

      .dashboard-mobile-safe .dashboard-personal-history-table tbody tr {
        display: grid;
        grid-template-columns: minmax(0, 1fr);
        gap: 0;
        width: 100%;
        margin-bottom: 10px;
        padding: 8px;
        border: 1px solid currentColor;
        border-color: inherit;
        border-radius: 8px;
      }

      .dashboard-mobile-safe .dashboard-personal-history-table tbody td {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 10px;
        width: 100% !important;
        max-width: 100% !important;
        min-width: 0 !important;
        padding: 7px 4px !important;
        text-align: right;
        overflow-wrap: anywhere;
        word-break: break-word;
      }

      .dashboard-mobile-safe .dashboard-personal-history-table tbody td::before {
        flex: 0 0 42%;
        text-align: left;
        font-weight: bold;
        color: #718096;
        font-size: 10px;
      }

      .dashboard-mobile-safe .dashboard-personal-history-table tbody td:nth-child(1)::before { content: 'ID Registro'; }
      .dashboard-mobile-safe .dashboard-personal-history-table tbody td:nth-child(2)::before { content: 'Auditoría'; }
      .dashboard-mobile-safe .dashboard-personal-history-table tbody td:nth-child(3)::before { content: 'Tipo'; }
      .dashboard-mobile-safe .dashboard-personal-history-table tbody td:nth-child(4)::before { content: 'Fecha y hora'; }
      .dashboard-mobile-safe .dashboard-personal-history-table tbody td:nth-child(5)::before { content: 'Ubicación GPS'; }

      .dashboard-mobile-safe .dashboard-personal-history-table tbody td > * {
        max-width: 58%;
        min-width: 0;
        overflow-wrap: anywhere;
      }

      .dashboard-mobile-safe .dashboard-personal-history-table tbody td[colspan] {
        display: block;
        text-align: center;
      }

      .dashboard-mobile-safe .dashboard-personal-history-table tbody td[colspan]::before {
        display: none;
      }

      .dashboard-mobile-safe .dashboard-personal-history-table tbody tr > td:first-child {
        border-top: 0 !important;
      }

      .dashboard-mobile-safe .dashboard-calendar-section > div:first-child {
        flex-direction: column !important;
        align-items: stretch !important;
      }

      .dashboard-mobile-safe .dashboard-calendar-section > div:first-child > div:last-child {
        width: 100% !important;
        display: grid !important;
        grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
      }

      .dashboard-mobile-safe .dashboard-calendar-section > div:first-child select {
        width: 100% !important;
      }

      .dashboard-mobile-safe .dashboard-calendar-section > div:nth-child(2) > div:first-child {
        flex-direction: column !important;
        align-items: stretch !important;
        gap: 7px !important;
      }

      .dashboard-mobile-safe .dashboard-calendar-section > div:nth-child(2) button {
        width: 100% !important;
      }

      .dashboard-mobile-safe .dashboard-calendar-section > div:nth-child(2) > div:last-child {
        max-height: 130px !important;
        overflow-y: auto !important;
      }

      .dashboard-mobile-safe .dashboard-calendar-section > div:nth-child(3) {
        overflow: visible !important;
      }

      .dashboard-mobile-safe .dashboard-calendar-section > div:last-child > div {
        padding: 12px !important;
      }

      .dashboard-mobile-safe .dashboard-calendar-section > div:last-child > div > div:first-child {
        flex-direction: column !important;
        align-items: flex-start !important;
      }

      .dashboard-mobile-safe .dashboard-calendar-section > div:last-child > div > div:first-child > * {
        max-width: 100% !important;
      }

      .dashboard-mobile-safe header {
        flex-wrap: wrap !important;
        gap: 12px !important;
      }

      .dashboard-mobile-safe header > div {
        max-width: 100% !important;
      }

      .dashboard-mobile-safe header > button {
        width: 100% !important;
      }
    }

    @media (max-width: 390px) {
      .dashboard-mobile-safe {
        padding: 8px !important;
      }

      .dashboard-mobile-safe > section {
        padding: 10px !important;
      }

      .dashboard-mobile-safe .dashboard-tab-navigation {
        grid-template-columns: minmax(0, 1fr) !important;
      }

      .dashboard-mobile-safe .dashboard-calendar-grid {
        gap: 1px !important;
      }

      .dashboard-mobile-safe .dashboard-calendar-grid > div {
        min-height: 52px !important;
      }

      .dashboard-mobile-safe .dashboard-calendar-grid > div > div {
        max-height: 38px !important;
      }
    }
  `;

// COMPONENTE: PANEL DE ADMINISTRACIÓN REDISEÑADO CON CATÁLOGO DE SITIOS Y MOTOR DE HORARIOS
  const AdminPanel = ({ token, isDarkMode, companySettings, onCompanySettingsChanged, companyId, user, jornadaConfirmationBusy, confirmNotificationJornadaClosure, onPersonalView, personalView, personalContent, monitorViewMode, setMonitorViewMode }) => {
      // 🎯 BYPASS DE SEGURIDAD MAESTRO: Evita la caída por funciones ausentes en AdminPanel (Línea 955)

  const [adminData, setAdminData] = useState([]);
  const [activeTab, setActiveTab] = useState('live');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openMenuGroup, setOpenMenuGroup] = useState('operacion');
  const adminLandingInitialized = useRef(false);
  const [globalCompanies, setGlobalCompanies] = useState([]);
  const [selectedGlobalCompanyId, setSelectedGlobalCompanyId] = useState('');
  const [liveStatus, setLiveStatus] = useState([]);
  const [liveStatusLoading, setLiveStatusLoading] = useState(false);
  const [liveStatusError, setLiveStatusError] = useState('');
  const [longShiftAlerts, setLongShiftAlerts] = useState([]);
  const [longShiftAlertsLoading, setLongShiftAlertsLoading] = useState(false);
  const [longShiftClarificationHistory, setLongShiftClarificationHistory] = useState([]);
  const [longShiftClarificationHistoryLoading, setLongShiftClarificationHistoryLoading] = useState(false);
  const [longShiftClarificationUserFilter, setLongShiftClarificationUserFilter] = useState('Todos');
  const [myPermissions, setMyPermissions] = useState({});
  const [myRole, setMyRole] = useState('');
  const [administrators, setAdministrators] = useState([]);
  const [permissionsLoading, setPermissionsLoading] = useState(false);
  const tieneAlertaCarga = false;

  const decodedRole = useMemo(() => {
    try { return String(jwtDecode(token)?.role || '').trim(); } catch (_) { return ''; }
  }, [token]);
  const effectiveRole = decodedRole || myRole;
  // Normalizamos el rol para que la interfaz no dependa de si el JWT/backend
  // devuelve el nombre con mayúsculas, minúsculas o espacios.
  const normalizedEffectiveRole = String(effectiveRole || '').trim().toLowerCase();
  const isGlobalSuperAdmin = normalizedEffectiveRole === 'superadministrador';
  const isCompanySuperAdmin = normalizedEffectiveRole === 'superadministradorempresarial';
  const isSuperAdmin = isGlobalSuperAdmin || isCompanySuperAdmin;
  // El SuperAdmin Empresarial conserva la vista operativa completa del Administrador.
  // Las funciones exclusivas del SuperAdmin Global siguen controladas por isGlobalSuperAdmin.
  const isAdministrativeRole = normalizedEffectiveRole === 'administrador' || isSuperAdmin;

  // Contexto empresarial: el Global trabaja siempre sobre una empresa
  // seleccionada en los módulos operativos. El Empresarial queda fijado a
  // su propia empresa. El backend vuelve a validar el alcance.
  const operationalCompanyId = isGlobalSuperAdmin
    ? (Number(selectedGlobalCompanyId) || Number(companyId) || '')
    : companyId;
  const operationalCompany = globalCompanies.find(c => Number(c.id) === Number(operationalCompanyId));

  useEffect(() => {
    if (!isGlobalSuperAdmin) {
      setGlobalCompanies([]);
      setSelectedGlobalCompanyId(String(companyId || ''));
      return undefined;
    }
    let active = true;
    axios.get('/api/companies/administration/management', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (!active) return;
        const rows = Array.isArray(r.data) ? r.data : [];
        setGlobalCompanies(rows);
        setSelectedGlobalCompanyId(prev => {
          if (prev && rows.some(c => String(c.id) === String(prev))) return prev;
          return rows.length ? String(rows[0].id) : '';
        });
      })
      .catch(error => console.error('No fue posible cargar el contexto empresarial global:', error));
    return () => { active = false; };
  }, [isGlobalSuperAdmin, token, companyId]);

  useEffect(() => {
    if (!isGlobalSuperAdmin || !operationalCompanyId) return undefined;
    const interceptor = axios.interceptors.request.use(config => {
      const url = String(config.url || '');
      const esModuloAdministrativo = url.startsWith('/api/attendance') || url.startsWith('/api/work-schedules') || url.startsWith('/api/employee-management') || url.startsWith('/api/leave-management') || url.startsWith('/api/overtime');
      if (!esModuloAdministrativo) return config;
      config.params = { ...(config.params || {}), companyId: Number(operationalCompanyId) };
      if (config.data && typeof config.data === 'object' && !Array.isArray(config.data)) {
        config.data = { ...config.data, companyId: Number(operationalCompanyId) };
      }
      return config;
    });
    return () => axios.interceptors.request.eject(interceptor);
  }, [isGlobalSuperAdmin, operationalCompanyId]);

  // La autorización real la determina el backend. El Dashboard solamente
  // refleja los permisos efectivos recibidos desde /users/me/permissions.
  // Para cuentas Administrador antiguas, si una clave todavía no existe en
  // la respuesta, respetamos los mismos valores predeterminados del backend.
  const can = useCallback((permission) => {
    if (isSuperAdmin) return true;

    if (Object.prototype.hasOwnProperty.call(myPermissions || {}, permission)) {
      return Boolean(myPermissions[permission]);
    }

    if (normalizedEffectiveRole === 'administrador') {
      const defaultAdminPermissions = {
        ver_monitor: true,
        ver_calendario: true,
        descargar_reportes: true
      };
      return Boolean(defaultAdminPermissions[permission]);
    }

    return false;
  }, [normalizedEffectiveRole, isSuperAdmin, myPermissions]);

  // El panel administrativo abre en Inicio/Mis registros para que el fichaje
  // sea la primera vista operativa. El Dashboard Ejecutivo queda disponible
  // dentro de Inicio y no se muestra automáticamente.
  useEffect(() => {
    if (adminLandingInitialized.current || permissionsLoading) return;

    // El SuperAdministrador Global no pertenece a ninguna empresa y no tiene
    // portal de asistencia personal ni fichaje. Su entrada natural es la
    // administración global de empresas.
    if (isGlobalSuperAdmin) {
      onPersonalView?.(null);
      setActiveTab('companies');
      setOpenMenuGroup('administracion');
    } else if (isAdministrativeRole && can('descargar_reportes')) {
      onPersonalView?.('personalRecords');
      setActiveTab('personalRecords');
      setOpenMenuGroup('inicio');
    }

    adminLandingInitialized.current = true;
  }, [isGlobalSuperAdmin, isAdministrativeRole, can, permissionsLoading, onPersonalView]);


  // Estados para crear un nuevo usuario
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState('Empleado');

  // Estados para modificar un usuario existente
  const [editingUserId, setEditingUserId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editRole, setEditRole] = useState('Empleado');
  const [editPassword, setEditPassword] = useState('');

  // 🏢 ESTADOS: CATÁLOGO DE SITIOS Y MAPAS LEAFLET
  const [sitesList, setSitesList] = useState([]);
  const [newSiteName, setNewSiteName] = useState('');
  const [newSiteLat, setNewSiteLat] = useState('20.9688');
  const [newSiteLon, setNewSiteLon] = useState('-89.6234');
  const [newSiteRadius, setNewSiteRadius] = useState(100);
  const [newSiteAddress, setNewSiteAddress] = useState('');
  const [newSiteActive, setNewSiteActive] = useState(true);
  const [newSiteDefault, setNewSiteDefault] = useState(false);
  const [editingSiteId, setEditingSiteId] = useState(null);
  const [siteSaving, setSiteSaving] = useState(false);
  const mapContainerRef = useRef(null);
  const leafletMapInstanceRef = useRef(null);
  const leafletMarkerRef = useRef(null);

  // 🕒 ESTADOS: CONFIGURACIÓN AVANZADA DE HORARIOS Y POLÍTICAS MULTI-ID
  const [advancedTarget, setAdvancedTarget] = useState([]); 
  const [advStartTime, setAdvStartTime] = useState('08:00');
  const [advEndTime, setAdvEndTime] = useState('17:00');
  const [advDays, setAdvDays] = useState(['1','2','3','4','5']); // Lunes a Viernes
  const [advPolicy, setAdvPolicy] = useState('Flexible');
  const [advTolerance, setAdvTolerance] = useState(15);
  // 🔐 Política biométrica individual del usuario seleccionado.
  // HEREDAR usa la política biométrica general de la empresa.
  const [advBiometricPolicy, setAdvBiometricPolicy] = useState('HEREDAR');
  const [advBiometricPolicySaving, setAdvBiometricPolicySaving] = useState(false);
  const [advSiteId, setAdvSiteId] = useState([]);
  const [advancedUserSearch, setAdvancedUserSearch] = useState('');

  // 📋 MONITOREO GLOBAL: vista de tarjetas o lista, con un periodo global.
  // Por defecto se muestra el día actual; también se puede consultar 4, 7, 15 o 30 días.
  const [monitorPeriodDays, setMonitorPeriodDays] = useState(1);
  // En Plantilla cada usuario puede consultar un periodo distinto sin alterar
  // el periodo de los demás. Si no existe una selección individual, usa el
  // periodo general como valor inicial.
  const [monitorUserPeriodDays, setMonitorUserPeriodDays] = useState({});
  const [monitorFilterType, setMonitorFilterType] = useState('Todos');
  // Selección múltiple del Monitor Global. Un arreglo vacío significa: todos.
  const [monitorSelectedUserIds, setMonitorSelectedUserIds] = useState([]);
  const [monitorUserFilterOpen, setMonitorUserFilterOpen] = useState(false);

  const cambiarPeriodoMonitorUsuario = useCallback((userId, days) => {
    const numericId = Number(userId);
    if (!Number.isFinite(numericId)) return;
    setMonitorUserPeriodDays(prev => ({ ...prev, [numericId]: Number(days) || 1 }));
  }, []);

  const restablecerPeriodosMonitorUsuarios = useCallback(() => {
    setMonitorUserPeriodDays({});
  }, []);

  // 📊 REPORTE EXCEL: filtros completamente independientes del monitor.
  const [excelSelectedUserIds, setExcelSelectedUserIds] = useState([]);
  const [excelFilterType, setExcelFilterType] = useState('Todos');
  const [excelDesde, setExcelDesde] = useState('');
  const [excelHasta, setExcelHasta] = useState('');
  const [excelLoading, setExcelLoading] = useState(false);
  const [recordsExcelLoading, setRecordsExcelLoading] = useState(false);
  // Selector exclusivo de reportes: respeta la Empresa en contexto sin
  // modificar adminData, que alimenta otros módulos ya estabilizados.
  const [reportUsers, setReportUsers] = useState([]);

  // ⚖️ CONFIGURACIÓN LEGAL POR AÑO (solo SuperAdministrador)
  const [legalRules, setLegalRules] = useState([]);
  const [legalRuleYear, setLegalRuleYear] = useState(new Date().getFullYear());
  const [legalRuleWeeklyHours, setLegalRuleWeeklyHours] = useState(48);
  const [legalRuleWeeklyOvertimeMax, setLegalRuleWeeklyOvertimeMax] = useState(9);
  const [legalRuleTripleMax, setLegalRuleTripleMax] = useState(4);
  const [legalRuleNotes, setLegalRuleNotes] = useState('');
  const [legalRuleSaving, setLegalRuleSaving] = useState(false);
  const [legalRulesLoading, setLegalRulesLoading] = useState(false);

  // 🏢 CONFIGURACIÓN DE EMPRESA / LOGO (solo SuperAdministrador)
  const [companyNameDraft, setCompanyNameDraft] = useState(companySettings?.companyName || 'Reloj Checador 3.0');
  const [companyLogoDraft, setCompanyLogoDraft] = useState(companySettings?.logoDataUrl || '');
  const [companyLogoFileName, setCompanyLogoFileName] = useState(companySettings?.logoFileName || '');
  const [companySaving, setCompanySaving] = useState(false);

  // 🔐 POLÍTICA BIOMÉTRICA
  const [biometricPolicyDraft, setBiometricPolicyDraft] = useState(companySettings?.biometricPolicy || 'OBLIGATORIA');
  const [biometricPolicySaving, setBiometricPolicySaving] = useState(false);

  useEffect(() => {
    setCompanyNameDraft(companySettings?.companyName || 'Reloj Checador 3.0');
    setCompanyLogoDraft(companySettings?.logoDataUrl || '');
    setCompanyLogoFileName(companySettings?.logoFileName || '');
  }, [companySettings?.companyName, companySettings?.logoDataUrl, companySettings?.logoFileName]);

  useEffect(() => {
    setBiometricPolicyDraft(companySettings?.biometricPolicy || 'OBLIGATORIA');
  }, [companySettings?.biometricPolicy]);

  // Estados para el calendario interactivo de horas
  const [calEventos, setCalEventos] = useState([]);
  const calendarRequestRef = useRef(0);
  const [calSelectedUsers, setCalSelectedUsers] = useState([]);

  // 📝 ACLARACIONES / AUDITORÍA DE ASISTENCIA
  const [adjustmentsAdmin, setAdjustmentsAdmin] = useState([]);
  const [adjustmentStatusFilter, setAdjustmentStatusFilter] = useState('Todos');
  const [adjustmentUserFilter, setAdjustmentUserFilter] = useState('Todos');
  const [adjustmentSubmitting, setAdjustmentSubmitting] = useState(false);
  const [adminAdjustmentUserId, setAdminAdjustmentUserId] = useState('');
  const [adminAdjustmentOriginalId, setAdminAdjustmentOriginalId] = useState('');
  const [adminAdjustmentType, setAdminAdjustmentType] = useState('Salida');
  const [adminAdjustmentDateTime, setAdminAdjustmentDateTime] = useState('');
  const [adminAdjustmentReason, setAdminAdjustmentReason] = useState('');
  const [adjustmentReviewComments, setAdjustmentReviewComments] = useState({});

  // 🚡️ FASE 6: Centro de Auditoría e Incidencias
  const [auditRows, setAuditRows] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditActionFilter, setAuditActionFilter] = useState('Todos');
  const [auditEntityFilter, setAuditEntityFilter] = useState('Todos');
  const [auditUserFilter, setAuditUserFilter] = useState('Todos');
  const [auditCatalogs, setAuditCatalogs] = useState({ actions: [], entityTypes: [], users: [] });
  const [incidentRows, setIncidentRows] = useState([]);
  const [incidentLoading, setIncidentLoading] = useState(false);
  const [incidentStatusFilter, setIncidentStatusFilter] = useState('Todos');
  const [incidentTypeFilter, setIncidentTypeFilter] = useState('Todos');
  const [laborFileUserId, setLaborFileUserId] = useState('');
  const [laborFile, setLaborFile] = useState(null);
  const [laborFileLoading, setLaborFileLoading] = useState(false);
  const [advancedSummary, setAdvancedSummary] = useState(null);
  const [advancedSummaryLoading, setAdvancedSummaryLoading] = useState(false);

  // ⚙️ FASE 7: automatización, notificaciones y tablero ejecutivo
  const [automationSettings, setAutomationSettings] = useState(null);
  const [automationLoading, setAutomationLoading] = useState(false);
  const [automationSaving, setAutomationSaving] = useState(false);
  const [notificationRows, setNotificationRows] = useState([]);
  const [notificationSummary, setNotificationSummary] = useState({ unread: 0, pending: 0, total: 0 });
  const [notificationLoading, setNotificationLoading] = useState(false);
  const [leaveReviewBusyId, setLeaveReviewBusyId] = useState(null);
  const [alertPopoverOpen, setAlertPopoverOpen] = useState(false);
  const alertAnchorRef = useRef(null);
  const [alertPopoverStyle, setAlertPopoverStyle] = useState({});
  const [executiveDashboard, setExecutiveDashboard] = useState(null);
  const [executiveDashboardError, setExecutiveDashboardError] = useState('');
  const [executiveLoading, setExecutiveLoading] = useState(false);
  const [automationRunResult, setAutomationRunResult] = useState(null);

  // 🚡️ FASE 8: centro de seguridad exclusivo del SuperAdministrador
  const [securityEvents, setSecurityEvents] = useState([]);
  const [securitySummary, setSecuritySummary] = useState(null);
  const [securityLoading, setSecurityLoading] = useState(false);


  // 🌐 EFECTO INYECTOR: Carga los archivos CSS y JS de Leaflet directamente desde el CDN si faltan
  useEffect(() => {
    if (!document.getElementById('leaflet-css')) {
      const link = document.createElement('link');
      link.id = 'leaflet-css';
      link.rel = 'stylesheet';
      link.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
      document.head.appendChild(link);
    }
    if (!document.getElementById('leaflet-js')) {
      const script = document.createElement('script');
      script.id = 'leaflet-js';
      script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
      document.body.appendChild(script);
    }
  }, []);

  // ⚙️ FUNCIÓN DE DETECCIÓN DINÁMICA DE ENTORNO SANEADA AL 100% (ADMIN)
  // 🌐 URL CANÓNICA DE API: el frontend local y producción usan el mismo prefijo /api.
  const formatMinutesFromMinutes = (minutes) => { const n=Math.max(0,Math.round(Number(minutes)||0)); return `${Math.floor(n/60)} h ${String(n%60).padStart(2,'0')} min`; };

  const obtenerUrlBase = (prefijo, subRuta = '') => {
    const p = String(prefijo || '').replace(/^\/+|\/+$/g, '');
    const s = String(subRuta || '').replace(/^\/+/, '');

    let rutaBase = p;
    if (p === 'attendance') rutaBase = 'api/attendance';
    else if (p === 'api') rutaBase = 'api';

    return `/${rutaBase}${s ? `/${s}` : ''}`.replace(/\/{2,}/g, '/');
  };


  const fetchMyPermissions = useCallback(async () => {
    try {
      const response = await axios.get(obtenerUrlBase('/api', '/users/me/permissions'), {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: false
      });
      const rolServidor = String(response.data?.role || '').trim();
      const permisosEfectivos = response.data?.effectivePermissions || response.data?.permissions || {};
      if (rolServidor) setMyRole(rolServidor);
      setMyPermissions(permisosEfectivos);
    } catch (error) {
      console.error('Error al cargar autorizaciones del usuario:', error);
      setMyPermissions({});
    }
  }, [token]);

  const fetchAdministrators = useCallback(async () => {
    if (!isSuperAdmin) return;
    setPermissionsLoading(true);
    try {
      const response = await axios.get(obtenerUrlBase('/api', '/users/administrators'), {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: false
      });
      setAdministrators(response.data || []);
    } catch (error) {
      console.error('Error al cargar administradores para autorización:', error);
    } finally {
      setPermissionsLoading(false);
    }
  }, [token, can]);


  // 📊 DESCARGA DE REPORTES EXCEL
  // Se mantienen dos exportaciones porque tienen objetivos distintos:
  // 1) Registros = evidencia/auditoría, sin cómputo.
  // 2) Reporte administrativo = cómputo de jornadas y resumen semanal.
  const downloadExcel = useCallback(async (modo = 'administrativo') => {
    if (!can('descargar_reportes')) {
      alert('No tienes autorización para descargar reportes.');
      return;
    }
    if (excelDesde && excelHasta && excelDesde > excelHasta) {
      alert('La fecha inicial no puede ser posterior a la fecha final.');
      return;
    }

    const esRegistros = modo === 'registros';
    const setLoading = esRegistros ? setRecordsExcelLoading : setExcelLoading;
    setLoading(true);
    try {
      const params = {
        userIds: excelSelectedUserIds.length > 0 ? excelSelectedUserIds.join(',') : 'Todos',
        tipo: excelFilterType || 'Todos'
      };
      if (excelDesde) params.desde = excelDesde;
      if (excelHasta) params.hasta = excelHasta;

      const endpoint = esRegistros ? '/reports/records-excel' : '/reports/excel';
      const response = await axios.get(obtenerUrlBase('/attendance', endpoint), {
        params,
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: false
      });
      const contentType = String(response.headers?.['content-type'] || '');
      if (contentType.includes('application/json')) {
        const contenido = await response.data.text();
        let mensaje = 'No fue posible generar el reporte.';
        try { mensaje = JSON.parse(contenido)?.message || mensaje; } catch (_) {}
        throw new Error(mensaje);
      }
      const blob = new Blob([response.data], { type: contentType || 'application/pdf' });
      const disposition = String(response.headers?.['content-disposition'] || '');
      const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/i);
      const nombreArchivo = match?.[1] ? decodeURIComponent(match[1]) : (esRegistros ? 'Registros_Asistencia_Auditoria.xlsx' : 'Reporte_Administrativo_Asistencia.xlsx');
      const url = window.URL.createObjectURL(blob);
      const enlace = document.createElement('a');
      enlace.href = url;
      enlace.download = nombreArchivo;
      document.body.appendChild(enlace);
      enlace.click();
      enlace.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al descargar Excel:', error);
      let mensaje = error?.message || 'No fue posible descargar el Excel.';
      try {
        if (error?.response?.data instanceof Blob) {
          const texto = await error.response.data.text();
          mensaje = JSON.parse(texto)?.message || mensaje;
        } else mensaje = error?.response?.data?.message || mensaje;
      } catch (_) {}
      alert(mensaje);
    } finally {
      setLoading(false);
    }
  }, [token, excelSelectedUserIds, excelFilterType, excelDesde, excelHasta, can]);

  const downloadExcelReport = useCallback(() => downloadExcel('administrativo'), [downloadExcel]);
  const downloadExcelRecords = useCallback(() => downloadExcel('registros'), [downloadExcel]);
  // Los usuarios del reporte se consultan por separado. Así el cambio de
  // Empresa en contexto del Global no altera la carga de empleados del resto
  // del Dashboard.
  useEffect(() => {
    if (activeTab !== 'reports' || !can('descargar_reportes') || !operationalCompanyId) return undefined;
    let activo = true;
    axios.get(obtenerUrlBase('/attendance', '/reports/users'), {
      headers: { Authorization: `Bearer ${token}` },
      withCredentials: false
    }).then(response => {
      if (!activo) return;
      setReportUsers(Array.isArray(response.data) ? response.data : []);
      setExcelSelectedUserIds(prev => {
        const validos = new Set((Array.isArray(response.data) ? response.data : []).map(u => Number(u.id)));
        return prev.filter(id => validos.has(Number(id)));
      });
    }).catch(error => {
      if (!activo) return;
      console.error('Error al cargar empleados para reportes:', error);
      setReportUsers([]);
      setExcelSelectedUserIds([]);
    });
    return () => { activo = false; };
  }, [activeTab, operationalCompanyId, token, can]);

  const toggleExcelUser = (userId) => {
    setExcelSelectedUserIds(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const toggleAllExcelUsers = () => {
    const ids = reportUsers.map(user => user.id);
    const todos = ids.length > 0 && ids.every(id => excelSelectedUserIds.includes(id));
    setExcelSelectedUserIds(todos ? [] : ids);
  };

  const handlePermissionToggle = async (adminId, permission, value) => {
    try {
      await axios.put(obtenerUrlBase('/api', `/users/administrators/${adminId}/permissions`), {
        permissions: { [permission]: value }
      }, {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: false
      });
      setAdministrators(prev => prev.map(admin => admin.id === adminId ? {
        ...admin,
        permissions: { ...(admin.permissions || {}), [permission]: value }
      } : admin));

      // Si el SuperAdministrador está probando la cuenta que está abierta en
      // otra sesión/pestaña, la sesión actual no se modifica aquí. En la
      // siguiente carga /users/me/permissions devuelve el valor efectivo.
      if (String(adminId) === String(jwtDecode(token)?.userId || '')) {
        await fetchMyPermissions();
      }
    } catch (error) {
      console.error('Error al actualizar autorización:', error);
      alert(error.response?.data?.message || 'No fue posible actualizar la autorización.');
    }
  };

  // Cargar registros de usuarios y asistencias globales
  const fetchAdminData = useCallback(async () => {
    try {
      const response = await axios.get(
        obtenerUrlBase('/api', '/users/all-with-attendance'),
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: false
        }
      );

      const usuarios = Array.isArray(response.data)
        ? response.data
        : [];

      const paramsEmpresa = operationalCompanyId ? { companyId: operationalCompanyId } : {};
      const [leaveResponse, scheduleResponse] = await Promise.all([
        axios.get('/api/leave-management', {
          headers: { Authorization: `Bearer ${token}` },
          params: paramsEmpresa
        }).catch(() => ({ data: [] })),
        axios.get('/api/work-schedules/assignments', {
          headers: { Authorization: `Bearer ${token}` },
          params: paramsEmpresa
        }).catch(() => ({ data: [] }))
      ]);
      const leaveRows = Array.isArray(leaveResponse.data) ? leaveResponse.data : [];
      const scheduleRows = Array.isArray(scheduleResponse.data) ? scheduleResponse.data : [];
      // Seleccionamos la asignación de jornada que realmente está vigente
      // hoy. La asignación más reciente no necesariamente es la efectiva:
      // puede ser futura, vencida o tener una prioridad distinta.
      const hoyMerida = new Intl.DateTimeFormat('en-CA', {
        timeZone: 'America/Merida',
        year: 'numeric', month: '2-digit', day: '2-digit'
      }).format(new Date());
      const scheduleMap = new Map();
      scheduleRows
        .filter(item => String(item.status || 'ACTIVO').toUpperCase() === 'ACTIVO')
        .filter(item => String(item.startDate || '').slice(0, 10) <= hoyMerida)
        .filter(item => !item.endDate || String(item.endDate).slice(0, 10) >= hoyMerida)
        .forEach(item => {
          const key = String(item.userId);
          const current = scheduleMap.get(key);
          const itemPriority = Number(item.priority || 1);
          const currentPriority = Number(current?.priority || 1);
          const itemStart = String(item.startDate || '').slice(0, 10);
          const currentStart = String(current?.startDate || '').slice(0, 10);
          if (!current || itemPriority < currentPriority ||
              (itemPriority === currentPriority && itemStart > currentStart) ||
              (itemPriority === currentPriority && itemStart === currentStart && Number(item.id || 0) > Number(current.id || 0))) {
            scheduleMap.set(key, item);
          }
        });

      // Compatibilidad reforzada:
      // si /users/all-with-attendance devuelve los usuarios pero la
      // asociación Sequelize no incluye "checkins", recuperamos el
      // historial de cada usuario mediante el endpoint protegido
      // /api/attendance/:userId. Esto evita que una asociación faltante
      // deje al administrador viendo las tarjetas sin registros.
      const usuariosConHistorial = await Promise.all(
        usuarios.map(async (usuario) => {
          if (Array.isArray(usuario.checkins)) {
            return {
              ...usuario,
              activeLeaves: leaveRows.filter(row => String(row.userId) === String(usuario.id) && String(row.status || '').toUpperCase() === 'APROBADA'),
              scheduleAssignment: scheduleMap.get(String(usuario.id)) || null
            };
          }

          try {
            const historial = await axios.get(
              obtenerUrlBase('/attendance', `/${usuario.id}`),
              {
                headers: { Authorization: `Bearer ${token}` },
                withCredentials: false
              }
            );

            return {
              ...usuario,
              checkins: Array.isArray(historial.data)
                ? historial.data
                : [],
              activeLeaves: leaveRows.filter(row => String(row.userId) === String(usuario.id) && String(row.status || '').toUpperCase() === 'APROBADA'),
              scheduleAssignment: scheduleMap.get(String(usuario.id)) || null
            };
          } catch (historialError) {
            console.error(
              `Error al obtener historial del usuario ${usuario.id}:`,
              historialError
            );

            return {
              ...usuario,
              checkins: [],
              activeLeaves: leaveRows.filter(row => String(row.userId) === String(usuario.id) && String(row.status || '').toUpperCase() === 'APROBADA'),
              scheduleAssignment: scheduleMap.get(String(usuario.id)) || null
            };
          }
        })
      );

      setAdminData(usuariosConHistorial);
    } catch (error) {
      console.error('Error al cargar panel de administración:', error);
      setAdminData([]);
    }
  }, [token, operationalCompanyId]);

  // 📝 AUDITORÍA: carga las aclaraciones pendientes/históricas para administración.
  const fetchAdminAdjustments = useCallback(async () => {
    try {
      const params = {};
      if (adjustmentStatusFilter !== 'Todos') params.status = adjustmentStatusFilter;
      if (adjustmentUserFilter !== 'Todos') params.userId = adjustmentUserFilter;
      const response = await axios.get(obtenerUrlBase('/attendance', '/adjustments/admin'), {
        params,
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: false
      });
      setAdjustmentsAdmin(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error al cargar auditoría de aclaraciones:', error);
    }
  }, [token, adjustmentStatusFilter, adjustmentUserFilter]);

  const handleCreateAdminAdjustment = async (e) => {
    e.preventDefault();
    if (!adminAdjustmentUserId) return alert('Selecciona un empleado.');
    if (!adminAdjustmentDateTime) return alert('Indica la fecha y hora del ajuste.');
    if (adminAdjustmentReason.trim().length < 5) return alert('Explica el motivo del ajuste.');

    setAdjustmentSubmitting(true);
    try {
      await axios.post(obtenerUrlBase('/attendance', '/adjustments'), {
        userId: Number(adminAdjustmentUserId),
        originalAttendanceId: adminAdjustmentOriginalId || null,
        proposedType: adminAdjustmentType,
        proposedTimestamp: adminAdjustmentDateTime,
        reason: adminAdjustmentReason.trim()
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: false
      });
      alert('📝 Ajuste enviado al empleado para su aceptación.');
      setAdminAdjustmentOriginalId('');
      setAdminAdjustmentDateTime('');
      setAdminAdjustmentReason('');
      fetchAdminAdjustments();
    } catch (error) {
      console.error('Error al crear ajuste administrativo:', error);
      alert(error.response?.data?.message || 'No fue posible crear el ajuste.');
    } finally {
      setAdjustmentSubmitting(false);
    }
  };

  const handleReviewAdjustment = async (adjustmentId, decision) => {
    const comentario = String(adjustmentReviewComments[adjustmentId] || '').trim();
    try {
      await axios.put(obtenerUrlBase('/attendance', `/adjustments/${adjustmentId}/review`), {
        decision,
        reviewComment: comentario
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: false
      });
      setAdjustmentReviewComments(prev => ({ ...prev, [adjustmentId]: '' }));

      // Una aclaración aceptada/rechazada puede cambiar inmediatamente la
      // jornada efectiva. Refrescamos todas las vistas que dependen de ella,
      // no solamente la bandeja de aclaraciones.
      await Promise.all([
        fetchAdminAdjustments(),
        fetchAdminData(),
        can('ver_monitor') ? fetchLiveStatus() : Promise.resolve(),
        activeTab === 'calendar' && can('ver_calendario') ? fetchCalendarEvents() : Promise.resolve()
      ]);
    } catch (error) {
      console.error('Error al revisar ajuste:', error);
      alert(error.response?.data?.message || 'No fue posible resolver la aclaración.');
    }
  };

  // 🎯 MONITOR EN VIVO: DETECCIÓN DE ENTORNO EN TIEMPO REAL
  const fetchLiveStatus = useCallback(async () => {
    setLiveStatusLoading(true);
    setLiveStatusError('');

    try {
      const response = await axios.get(obtenerUrlBase('/attendance', '/live/status'), {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: false
      });

      const datos = Array.isArray(response.data) ? response.data : [];
      setLiveStatus(datos);
    } catch (error) {
      console.error('Error al cargar el monitoreo en vivo por red:', error);
      setLiveStatusError(
        error?.response?.data?.message ||
        'No fue posible actualizar el monitor en vivo.'
      );
    } finally {
      setLiveStatusLoading(false);
    }
  }, [token]);

  // 🚨 ALERTAS PERSISTENTES DE JORNADAS PROLONGADAS
  const fetchLongShiftAlerts = useCallback(async () => {
    setLongShiftAlertsLoading(true);
    try {
      const endpoint = can('ver_monitor')
        ? '/long-shift-alerts'
        : '/long-shift-alerts/my';
      const response = await axios.get(obtenerUrlBase('/attendance', endpoint), {
        params: operationalCompanyId ? { companyId: Number(operationalCompanyId) } : {},
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: false
      });
      setLongShiftAlerts(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error al cargar alertas de jornadas prolongadas:', error);
    } finally {
      setLongShiftAlertsLoading(false);
    }
  }, [token, can, operationalCompanyId]);

  // 📚 HISTORIAL DE ACLARACIONES DE JORNADAS PROLONGADAS
  // Los registros atendidos no se eliminan: se consultan desde la tabla
  // LongShiftAlertClarifications para conservar la trazabilidad completa.
  const fetchLongShiftClarificationHistory = useCallback(async () => {
    if (!can('gestionar_aclaraciones')) {
      setLongShiftClarificationHistory([]);
      return;
    }

    setLongShiftClarificationHistoryLoading(true);
    try {
      const params = {};
      if (longShiftClarificationUserFilter !== 'Todos') {
        params.userId = longShiftClarificationUserFilter;
      }

      const response = await axios.get(
        obtenerUrlBase('/attendance', '/long-shift-alerts/clarifications/history'),
        {
          params,
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: false
        }
      );

      setLongShiftClarificationHistory(
        Array.isArray(response.data) ? response.data : []
      );
    } catch (error) {
      console.error('Error al cargar historial de aclaraciones de jornadas:', error);
      setLongShiftClarificationHistory([]);
    } finally {
      setLongShiftClarificationHistoryLoading(false);
    }
  }, [token, can, longShiftClarificationUserFilter]);

  const fetchAuditCenter = useCallback(async () => {
    if (!can('descargar_reportes')) return;
    setAuditLoading(true);
    try {
      const params = { action: auditActionFilter, entityType: auditEntityFilter, limit: 300 };
      if (auditUserFilter !== 'Todos') params.userId = auditUserFilter;
      const response = await axios.get(obtenerUrlBase('/attendance', '/audit/center'), { params, headers: { Authorization: `Bearer ${token}` }, withCredentials: false });
      setAuditRows(Array.isArray(response.data) ? response.data : []);
    } catch (error) { console.error('Error cargando Centro de Auditoría:', error); setAuditRows([]); }
    finally { setAuditLoading(false); }
  }, [token, can, auditActionFilter, auditEntityFilter, auditUserFilter]);

  const fetchAuditCatalogs = useCallback(async () => {
    if (!can('descargar_reportes')) return;
    try {
      const response = await axios.get(obtenerUrlBase('/attendance', '/audit/catalogs'), { headers: { Authorization: `Bearer ${token}` }, withCredentials: false });
      setAuditCatalogs(response.data || { actions: [], entityTypes: [], users: [] });
    } catch (error) { console.error('Error cargando filtros de auditoría:', error); }
  }, [token, can]);

  const fetchIncidents = useCallback(async () => {
    if (!can('descargar_reportes')) return;
    setIncidentLoading(true);
    try {
      await axios.post(obtenerUrlBase('/attendance', '/incidents/sync'), {}, { headers: { Authorization: `Bearer ${token}` }, withCredentials: false });
      const params = {};
      if (operationalCompanyId) params.companyId = Number(operationalCompanyId);
      if (incidentStatusFilter !== 'Todos') params.status = incidentStatusFilter;
      if (incidentTypeFilter !== 'Todos') params.type = incidentTypeFilter;
      const response = await axios.get(obtenerUrlBase('/attendance', '/incidents'), { params, headers: { Authorization: `Bearer ${token}` }, withCredentials: false });
      setIncidentRows(Array.isArray(response.data) ? response.data : []);
    } catch (error) { console.error('Error cargando Centro de Incidencias:', error); setIncidentRows([]); }
    finally { setIncidentLoading(false); }
  }, [token, can, incidentStatusFilter, incidentTypeFilter, operationalCompanyId]);

  const handleIncidentStatus = async (incidentId, status) => {
    const resolution = window.prompt(status === 'EN_REVISION' ? 'Observación de revisión:' : 'Indica la resolución/cierre de la incidencia:');
    if (resolution === null) return;
    try {
      await axios.put(obtenerUrlBase('/attendance', `/incidents/${incidentId}`), { status, resolution: String(resolution).trim() }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: false });
      await fetchIncidents();
      await fetchAuditCenter();
    } catch (error) { alert(error.response?.data?.message || 'No fue posible actualizar la incidencia.'); }
  };


  const fetchAutomationSettings = useCallback(async () => {
    if (!can('descargar_reportes')) return;
    setAutomationLoading(true);
    try {
      const response = await axios.get(obtenerUrlBase('/attendance', '/automation/settings'), { headers: { Authorization: `Bearer ${token}` }, withCredentials: false });
      setAutomationSettings(response.data || null);
    } catch (error) { console.error('Error cargando automatización:', error); }
    finally { setAutomationLoading(false); }
  }, [token, can]);

  const saveAutomationSettings = async () => {
    if (!automationSettings) return;
    setAutomationSaving(true);
    try {
      const response = await axios.put(obtenerUrlBase('/attendance', '/automation/settings'), automationSettings, { headers: { Authorization: `Bearer ${token}` }, withCredentials: false });
      setAutomationSettings(response.data?.settings || automationSettings);
      alert(response.data?.message || 'Configuración de automatización guardada.');
    } catch (error) { alert(error.response?.data?.message || 'No fue posible guardar la automatización.'); }
    finally { setAutomationSaving(false); }
  };

  const runAutomationNow = async () => {
    try {
      const response = await axios.post(obtenerUrlBase('/attendance', '/automation/run'), {}, { headers: { Authorization: `Bearer ${token}` }, withCredentials: false });
      setAutomationRunResult(response.data?.result || null);
      await fetchNotifications();
      await fetchExecutiveDashboard();
    } catch (error) {
      alert(error.response?.data?.message || 'No fue posible ejecutar la automatización.');
    }
  };

  const reviewPersonalExportRequestFromNotification = async (notification, decision) => {
    try {
      await axios.post(obtenerUrlBase('/attendance', `/reports/export-requests/${notification.id}/review`), { decision }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: false });
      await fetchNotifications();
      alert(decision === 'AUTORIZADA' ? 'Solicitud autorizada. El usuario ya puede descargar el PDF.' : 'Solicitud rechazada.');
    } catch (error) {
      alert(error.response?.data?.message || 'No fue posible revisar la solicitud de exportación.');
      await fetchNotifications();
    }
  };

  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    setNotificationLoading(true);
    try {
      const [rows, summary] = await Promise.all([
        axios.get(obtenerUrlBase('/attendance', '/notifications'), { params: operationalCompanyId ? { companyId: Number(operationalCompanyId) } : {}, headers: { Authorization: `Bearer ${token}` }, withCredentials: false }),
        axios.get(obtenerUrlBase('/attendance', '/notifications/summary'), { params: operationalCompanyId ? { companyId: Number(operationalCompanyId) } : {}, headers: { Authorization: `Bearer ${token}` }, withCredentials: false })
      ]);
      const filasNotificaciones = Array.isArray(rows.data) ? rows.data : [];
      // Una confirmación de cierre de jornada ya resuelta no debe seguir
      // apareciendo como aviso operativo para el empleado. Se conserva en
      // backend para auditoría, pero deja de mostrarse en el centro de alertas.
      const filasVisibles = filasNotificaciones.filter(n => {
        const tipoNotificacion = String(n?.type || '').toUpperCase();
        if (tipoNotificacion.startsWith('INCIDENCIA_') || tipoNotificacion === 'JORNADA_PROLONGADA_PENDIENTE') return false;
        const esCierreJornada = tipoNotificacion === 'CIERRE_JORNADA_CONFIRMACION';
        if (!esCierreJornada) return true;
        const estadoNotificacion = String(n?.status || '').toUpperCase();
        const estadoCierre = String(n?.metadata?.status || '').toUpperCase();
        return !['CERRADA', 'ACUERDO', 'DESACUERDO'].includes(estadoNotificacion)
          && !['ACUERDO', 'DESACUERDO'].includes(estadoCierre);
      });
      // El centro de alertas es operativo: sólo muestra solicitudes todavía
      // abiertas. El historial CERRADA/ATENDIDA permanece en sus módulos y
      // en auditoría, pero no debe seguir llenando la campana.
      const filasOperativas = filasVisibles.filter(n => ['NUEVA', 'LEIDA'].includes(String(n?.status || '').toUpperCase()));
      setNotificationRows(filasOperativas);
      setNotificationSummary(summary.data || { unread: 0, pending: 0, total: 0 });
    } catch (error) { console.error('Error cargando notificaciones:', error); }
    finally { setNotificationLoading(false); }
  }, [token, can, operationalCompanyId]);

  const handleConfirmNotificationJornadaClosure = async (notification, decision) => {
    try {
      const confirmado = await confirmNotificationJornadaClosure(notification, decision);
      if (confirmado !== false) {
        // La confirmación del cierre resuelve también la notificación. La
        // quitamos de la lista local inmediatamente para no dejar visible un
        // aviso NUEVA/ALTA que ya fue atendido. El refresco posterior valida
        // nuevamente el estado en servidor.
        setNotificationRows(prev => prev.filter(item => Number(item.id) !== Number(notification.id)));
      }
      await fetchNotifications();
    } catch (error) {
      console.error('Error confirmando cierre de jornada desde notificaciones:', error);
      alert(error?.response?.data?.message || error?.message || 'No fue posible confirmar el cierre de la jornada.');
    }
  };

  const reviewLeaveRequestFromNotification = async (notification, status) => {
    const requestId = Number(notification?.referenceId || notification?.metadata?.requestId);
    if (!Number.isInteger(requestId) || requestId <= 0) {
      alert('La solicitud no tiene un identificador válido.');
      return;
    }
    const comentario = window.prompt(
      status === 'APROBADA' ? 'Comentario de aprobación (opcional):' : 'Motivo de rechazo:',
      status === 'APROBADA' ? '' : 'Solicitud rechazada por revisión administrativa.'
    );
    if (status === 'RECHAZADA' && comentario === null) return;
    try {
      setLeaveReviewBusyId(requestId);
      await axios.put(obtenerUrlBase('/api', `/leave-management/${requestId}/review`), {
        status,
        reviewComment: comentario || '',
        companyId: operationalCompanyId
      }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: false });
      await fetchNotifications();
    } catch (error) {
      // Si otro administrador ya resolvió la solicitud, la copia visible
      // puede haber quedado abierta en esta sesión. Refrescamos de inmediato
      // para quitarla del centro y de la campana.
      if (Number(error?.response?.status) === 409) {
        await fetchNotifications();
      }
      alert(error?.response?.data?.message || 'No fue posible revisar la solicitud.');
    } finally {
      setLeaveReviewBusyId(null);
    }
  };

  const updateNotificationStatus = async (id, status) => {
    try {
      await axios.put(obtenerUrlBase('/attendance', `/notifications/${id}`), { status }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: false });
      await fetchNotifications();
    } catch (error) { alert(error.response?.data?.message || 'No fue posible actualizar la notificación.'); }
  };

  const fetchExecutiveDashboard = useCallback(async () => {
    if (!can('descargar_reportes')) return;
    setExecutiveLoading(true);
    setExecutiveDashboardError('');
    try {
      const response = await axios.get(obtenerUrlBase('/attendance', '/executive-dashboard'), { headers: { Authorization: `Bearer ${token}` }, withCredentials: false });
      setExecutiveDashboard(response.data || null);
    } catch (error) {
      console.error('Error cargando dashboard ejecutivo:', error);
      setExecutiveDashboard(null);
      setExecutiveDashboardError(error?.response?.data?.message || 'No fue posible cargar los indicadores ejecutivos.');
    } finally { setExecutiveLoading(false); }
  }, [token, can, operationalCompanyId]);

  const fetchSecurityCenter = useCallback(async () => {
    if (!isSuperAdmin) return;
    setSecurityLoading(true);

    try {
      const [events, summary] = await Promise.all([
        axios.get('/api/system/security/events?limit=100', {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: false
        }),
        axios.get('/api/system/security/summary', {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: false
        })
      ]);

      setSecurityEvents(
        Array.isArray(events.data) ? events.data : []
      );
      setSecuritySummary(summary.data || null);
    } catch (error) {
      console.error(
        'Error cargando centro de seguridad:',
        error
      );
      setSecurityEvents([]);
      setSecuritySummary(null);
    } finally {
      setSecurityLoading(false);
    }
  }, [token, isSuperAdmin]);

  const createSQLiteBackup = async () => {
    try {
      const response = await axios.post(
        '/api/system/backup/sqlite',
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`
          },
          withCredentials: false
        }
      );

      alert(
        `${response.data?.message || 'Copia creada.'}
Archivo: ${response.data?.filename || 'N/A'}`
      );

      await fetchSecurityCenter();
    } catch (error) {
      alert(
        error.response?.data?.message ||
        'No fue posible crear la copia de seguridad.'
      );
    }
  };

  const fetchLaborFile = useCallback(async (userId = laborFileUserId) => {
    if (!userId) { setLaborFile(null); return; }
    setLaborFileLoading(true);
    try {
      const response = await axios.get(`/api/employee-management/${Number(userId)}/profile`, { headers: { Authorization: `Bearer ${token}` }, params: operationalCompanyId ? { companyId: operationalCompanyId } : {}, withCredentials: false });
      setLaborFile(response.data || null);
    } catch (error) { console.error('Error cargando expediente laboral:', error); setLaborFile(null); alert(error.response?.data?.message || 'No fue posible cargar el expediente.'); }
    finally { setLaborFileLoading(false); }
  }, [token, laborFileUserId, operationalCompanyId]);

  const fetchAdvancedSummary = useCallback(async () => {
    if (!can('descargar_reportes')) return;
    setAdvancedSummaryLoading(true);
    try {
      const response = await axios.get(obtenerUrlBase('/attendance', '/reports/advanced-summary'), { headers: { Authorization: `Bearer ${token}` }, withCredentials: false });
      setAdvancedSummary(response.data || null);
    } catch (error) { console.error('Error cargando resumen avanzado:', error); setAdvancedSummary(null); }
    finally { setAdvancedSummaryLoading(false); }
  }, [token, can]);

  const handleAttendLongShiftAlert = async (alertId) => {
    const observation = window.prompt('Escribe la observación de la atención administrativa:');
    if (observation === null) return;
    const texto = String(observation).trim();
    if (!texto) {
      alert('Debes registrar una observación para atender la alerta.');
      return;
    }
    try {
      await axios.put(obtenerUrlBase('/attendance', `/long-shift-alerts/${alertId}/attend`), {
        observation: texto
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: false
      });
      await fetchLongShiftAlerts();
      await fetchLongShiftClarificationHistory();
    } catch (error) {
      console.error('Error al atender alerta:', error);
      alert(error.response?.data?.message || 'No fue posible atender la alerta.');
    }
  };

  // ⚖️ LEGISLACIÓN: carga y administra reglas por año.
  const fetchLegalRules = useCallback(async () => {
    if (!can('gestionar_legislacion') && !can('gestionar_empresa') && !can('gestionar_biometria')) return;
    setLegalRulesLoading(true);
    try {
      const response = await axios.get('/api/settings', { headers: { Authorization: `Bearer ${token}` }, withCredentials: false });
      const rules = Array.isArray(response.data?.legalRules) ? response.data.legalRules : [];
      if (response.data?.company?.biometricPolicy) setBiometricPolicyDraft(response.data.company.biometricPolicy);
      setLegalRules(rules);
      const actual = rules.find(r => Number(r.year) === new Date().getFullYear()) || rules[rules.length - 1];
      if (actual) {
        setLegalRuleYear(Number(actual.year));
        setLegalRuleWeeklyHours(Number(actual.weeklyMaxHours));
        setLegalRuleWeeklyOvertimeMax(Number(actual.weeklyOvertimeMaxHours ?? actual.overtimeDoubleWeeklyHours ?? 9));
        setLegalRuleTripleMax(Number(actual.tripleMaxHours ?? 4));
        setLegalRuleNotes(actual.notes || '');
      }
    } catch (error) {
      console.error('Error cargando reglas legales:', error);
    } finally { setLegalRulesLoading(false); }
  }, [token, can]);

  const seleccionarReglaLegal = (year) => {
    const regla = legalRules.find(r => Number(r.year) === Number(year));
    setLegalRuleYear(Number(year));
    if (regla) {
      setLegalRuleWeeklyHours(Number(regla.weeklyMaxHours));
      setLegalRuleWeeklyOvertimeMax(Number(regla.weeklyOvertimeMaxHours ?? regla.overtimeDoubleWeeklyHours ?? 9));
      setLegalRuleTripleMax(Number(regla.tripleMaxHours ?? 4));
      setLegalRuleNotes(regla.notes || '');
    } else {
      setLegalRuleWeeklyHours(48);
      setLegalRuleWeeklyOvertimeMax(9);
      setLegalRuleTripleMax(4);
      setLegalRuleNotes('');
    }
  };

  const handleSaveLegalRule = async (e) => {
    e.preventDefault();
    setLegalRuleSaving(true);
    try {
      await axios.put('/api/settings/legal-rules', {
        year: Number(legalRuleYear),
        weeklyMaxHours: Number(legalRuleWeeklyHours),
        overtimeDoubleWeeklyHours: Number(legalRuleWeeklyOvertimeMax),
        weeklyOvertimeMaxHours: Number(legalRuleWeeklyOvertimeMax),
        tripleMaxHours: Number(legalRuleTripleMax),
        notes: legalRuleNotes.trim()
      }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: false });
      alert(`⚖️ Regla legal de ${legalRuleYear} guardada correctamente.`);
      await fetchLegalRules();
    } catch (error) {
      alert(error.response?.data?.message || 'No fue posible guardar la regla legal.');
    } finally { setLegalRuleSaving(false); }
  };

  const handleDeleteLegalRule = async (year) => {
    if (!window.confirm(`¿Eliminar la configuración futura del año ${year}?`)) return;
    try {
      await axios.delete(`/api/settings/legal-rules/${year}`, { headers: { Authorization: `Bearer ${token}` }, withCredentials: false });
      await fetchLegalRules();
    } catch (error) {
      alert(error.response?.data?.message || 'No fue posible eliminar la regla legal.');
    }
  };

  const handleCompanyLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/png', 'image/jpeg'].includes(file.type)) {
      alert('Selecciona un logo PNG o JPG/JPEG.');
      e.target.value = '';
      return;
    }
    if (file.size > 1800000) {
      alert('El logo debe pesar menos de 1.8 MB.');
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setCompanyLogoDraft(String(reader.result || ''));
      setCompanyLogoFileName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const handleSaveCompany = async (e) => {
    e.preventDefault();
    setCompanySaving(true);
    try {
      const response = await axios.put('/api/settings/company', {
        companyName: companyNameDraft.trim(),
        logoDataUrl: companyLogoDraft || null,
        logoFileName: companyLogoFileName || null
      }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: false });
      alert('🏢 Configuración de empresa guardada correctamente.');
      if (onCompanySettingsChanged) onCompanySettingsChanged(response.data?.company || { companyName: companyNameDraft, logoDataUrl: companyLogoDraft, logoFileName: companyLogoFileName });
    } catch (error) {
      alert(error.response?.data?.message || 'No fue posible guardar la configuración de empresa.');
    } finally { setCompanySaving(false); }
  };


  const handleSaveBiometricPolicy = async (e) => {
    e.preventDefault();
    setBiometricPolicySaving(true);
    try {
      const response = await axios.put('/api/settings/biometric-policy', {
        biometricPolicy: biometricPolicyDraft
      }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: false });
      alert('🔐 Política biométrica guardada correctamente.');
      if (onCompanySettingsChanged) onCompanySettingsChanged(response.data?.company || { ...companySettings, biometricPolicy: biometricPolicyDraft });
    } catch (error) {
      alert(error.response?.data?.message || 'No fue posible guardar la política biométrica.');
    } finally { setBiometricPolicySaving(false); }
  };

  // 🏢 SUCURSALES: Trae la lista de sitios de registro corporativos
  const fetchSitesList = useCallback(async () => {
    try {
      const response = await axios.get(obtenerUrlBase('/attendance', '/sites/all'), {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: false
      });
      setSitesList(response.data);
    } catch (error) {
      console.error('Error al cargar el catálogo de sitios corporativos:', error);
    }
  }, [token]);

  // 📅 CALENDARIO: invalida respuestas antiguas para evitar parpadeos
  // cuando el usuario cambia rápidamente la selección.
  const fetchCalendarEvents = useCallback(async () => {
    const requestId = ++calendarRequestRef.current;
    const usuariosParam =
      calSelectedUsers.length > 0 ? calSelectedUsers.join(',') : 'Todos';

    // El calendario operativo siempre representa cuatro semanas completas:
    // semana en curso primero y las tres semanas siguientes, de lunes a domingo.
    const hoy = new Date();
    hoy.setHours(12, 0, 0, 0);
    const indiceLunes = (hoy.getDay() + 6) % 7;
    const inicioSemanaActual = new Date(hoy);
    inicioSemanaActual.setDate(hoy.getDate() - indiceLunes);

    const desdeDate = new Date(inicioSemanaActual);
    desdeDate.setDate(desdeDate.getDate() - 21);
    const hastaDate = new Date(inicioSemanaActual);
    hastaDate.setDate(hastaDate.getDate() + 6);

    const fechaPlanaLocal = fecha => {
      const y = fecha.getFullYear();
      const m = String(fecha.getMonth() + 1).padStart(2, '0');
      const d = String(fecha.getDate()).padStart(2, '0');
      return `${y}-${m}-${d}`;
    };

    try {
      const response = await axios.get(
        obtenerUrlBase('/attendance', '/calendar/events'),
        {
          params: {
            userIds: usuariosParam,
            desde: fechaPlanaLocal(desdeDate),
            hasta: fechaPlanaLocal(hastaDate),
            ...(operationalCompanyId ? { companyId: Number(operationalCompanyId) } : {})
          },
          headers: {
            'Authorization': `Bearer ${token}`,
            'Cache-Control': 'no-cache, no-store, max-age=0',
            'Pragma': 'no-cache'
          },
          withCredentials: false
        }
      );

      if (requestId !== calendarRequestRef.current) return;

      setCalEventos(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      if (axios.isCancel?.(error) || error?.code === 'ERR_CANCELED') return;
      if (requestId !== calendarRequestRef.current) return;
      console.error('Error cargando eventos de calendario:', error);
    }
  }, [calSelectedUsers, token, operationalCompanyId]);

  // Al cambiar de empresa en el contexto Global, los IDs de empleados
  // seleccionados de la empresa anterior dejan de ser válidos.
  // Reiniciamos la selección para que el calendario consulte a todos
  // los empleados de la nueva empresa.
  useEffect(() => {
    setCalSelectedUsers([]);
    setCalEventos([]);
  }, [operationalCompanyId]);


  // 🔐 Cargar las autorizaciones del usuario UNA sola vez por token.
  // IMPORTANTE:
  // No debe compartir efecto con fetchAdminData/fetchLiveStatus/etc.,
  // porque fetchMyPermissions modifica myPermissions y eso modifica can().
  useEffect(() => {
    fetchMyPermissions();
  }, [fetchMyPermissions]);

  // 🔥 CARGAS BASE:
  // Se ejecutan cuando cambia la pestaña o cuando cambian las autorizaciones
  // disponibles del usuario, pero NO vuelven a llamar fetchMyPermissions().
  useEffect(() => {
    fetchAdminData();
    fetchNotifications();

    if (can('ver_monitor')) {
      fetchLiveStatus();
    }
    fetchLongShiftAlerts();

    if (can('gestionar_sitios')) {
      fetchSitesList();
    }

    if (activeTab === 'authorizations' && isSuperAdmin) {
      fetchAdministrators();
    }
    if (activeTab === 'config' && (can('gestionar_legislacion') || can('gestionar_empresa') || can('gestionar_biometria'))) {
      fetchLegalRules();
    }
    if (activeTab === 'adjustments') {
      fetchAdminAdjustments();
      fetchLongShiftClarificationHistory();
    }
    if (activeTab === 'audit') {
      fetchAuditCatalogs();
      fetchAuditCenter();
    }
    if (activeTab === 'incidents') fetchIncidents();
    if (activeTab === 'laborFile' && laborFileUserId) fetchLaborFile(laborFileUserId);
    if (activeTab === 'advancedSummary') fetchAdvancedSummary();
    if (activeTab === 'executive') { fetchExecutiveDashboard(); fetchNotifications(); fetchAutomationSettings(); }
    if (activeTab === 'notifications') fetchNotifications();
  }, [
    activeTab,
    fetchAdminData,
    fetchLiveStatus,
    fetchSitesList,
    fetchAdministrators,
    fetchAdminAdjustments,
    fetchLongShiftClarificationHistory,
    fetchAuditCatalogs,
    fetchAuditCenter,
    fetchIncidents,
    fetchLaborFile,
    fetchAdvancedSummary,
    fetchExecutiveDashboard,
    fetchNotifications,
    fetchAutomationSettings,
    fetchLegalRules,
    can,
    isSuperAdmin
  ]);
  useEffect(() => {
    if (personalView === 'personalNotifications') fetchNotifications();
  }, [personalView, fetchNotifications]);

  // 📅 CALENDARIO: sólo se vuelve a consultar cuando cambia la selección,
  // el mes, el año o la pestaña.
  useEffect(() => {
    if (activeTab === 'calendar' && can('ver_calendario')) {
      fetchCalendarEvents();
    }
  }, [activeTab, fetchCalendarEvents, can]);

  // Polling únicamente para el monitor en vivo.
  useEffect(() => {
    const intervaloLive = setInterval(() => {
      if (can('ver_monitor')) {
        fetchLiveStatus();
      }
      fetchLongShiftAlerts();
      if (can('gestionar_aclaraciones')) {
        fetchLongShiftClarificationHistory();
      }
      fetchNotifications();
    }, 30000);

    return () => clearInterval(intervaloLive);
  }, [fetchLiveStatus, fetchLongShiftAlerts, fetchLongShiftClarificationHistory, fetchNotifications, can]);
  // 🗺️ FUNCIÓN INTEGRAL CORREGIDA: Inicializa el mapa de Leaflet y fuerza su redimensión visual
  const inicializarMapaLeaflet = () => {
    if (!window.L || !mapContainerRef.current) return;

    if (leafletMapInstanceRef.current) {
      leafletMapInstanceRef.current.remove();
      leafletMapInstanceRef.current = null;
    }

    const latInicial = parseFloat(newSiteLat);
    const lonInicial = parseFloat(newSiteLon);

    const mapa = window.L.map(mapContainerRef.current).setView([latInicial, lonInicial], 13);
    leafletMapInstanceRef.current = mapa;

    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OpenStreetMap'
    }).addTo(mapa);

    const marcador = window.L.marker([latInicial, lonInicial], { draggable: true }).addTo(mapa);
    leafletMarkerRef.current = marcador;

    marcador.on('dragend', () => {
      const pos = marcador.getLatLng();
      setNewSiteLat(pos.lat.toFixed(7));
      setNewSiteLon(pos.lng.toFixed(7));
    });

    mapa.on('click', (e) => {
      const pos = e.latlng;
      marcador.setLatLng(pos);
      setNewSiteLat(pos.lat.toFixed(7));
      setNewSiteLon(pos.lng.toFixed(7));
    });

    // 🔥 SOLUCIÓN AL MAPA INVISIBLE: Fuerza a Leaflet a recalcular sus dimensiones en el HTML real
    setTimeout(() => {
      if (leafletMapInstanceRef.current) {
        leafletMapInstanceRef.current.invalidateSize();
      }
    }, 300);
  };

  // Disparador controlado del mapa al entrar a Configuraciones
  useEffect(() => {
    if (activeTab === 'config') {
      const timerMapa = setTimeout(() => {
        inicializarMapaLeaflet();
      }, 250); // Tiempo óptimo de renderizado
      return () => clearTimeout(timerMapa);
    }
  }, [activeTab]);

  // 👥 SELECCIÓN INTELIGENTE DE PERSONAL PARA POLÍTICAS AVANZADAS
  const empleadosAvanzadosFiltrados = useMemo(() => {
    const termino = String(advancedUserSearch || '').trim().toLowerCase();
    if (!termino) return adminData;

    return adminData.filter(emp => {
      const texto = [emp.name, emp.email, emp.id, emp.role]
        .map(valor => String(valor ?? '').toLowerCase())
        .join(' ');
      return texto.includes(termino);
    });
  }, [adminData, advancedUserSearch]);

  const handleToggleAdvancedUser = (userId) => {
    setAdvancedTarget(prev => {
      const next = prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId];
      if (next.length === 1) {
        const empleado = adminData.find(u => Number(u.id) === Number(next[0]));
        if (empleado) cargarConfiguracionUsuarioAvanzado(empleado);
      }
      return next;
    });
  };

  const handleSelectAllAdvancedEmployees = () => {
    const idsVisibles = empleadosAvanzadosFiltrados.map(u => u.id);
    const todosVisiblesSeleccionados = idsVisibles.length > 0 && idsVisibles.every(id => advancedTarget.includes(id));

    setAdvancedTarget(prev => todosVisiblesSeleccionados
      ? prev.filter(id => !idsVisibles.includes(id))
      : Array.from(new Set([...prev, ...idsVisibles]))
    );
  };

  // 📥 CARGA LA CONFIGURACIÓN REAL DEL USUARIO SELECCIONADO EN EL FORMULARIO.
  // Esto evita que el administrador tenga que recordar horario, días o geocercas.
  const cargarConfiguracionUsuarioAvanzado = (empleado) => {
    if (!empleado) return;

    const dias = String(empleado.work_days ?? '1,2,3,4,5')
      .split(',')
      .map(d => d.trim())
      .filter(Boolean);

    const sitios = String(empleado.site_id ?? '')
      .split(',')
      .map(id => id.trim())
      .filter(id => id && id.toLowerCase() !== 'ninguno');

    setAdvStartTime(String(empleado.work_start_time || '08:00'));
    setAdvEndTime(String(empleado.work_end_time || '17:00'));
    setAdvDays(dias.length > 0 ? dias : ['1','2','3','4','5']);
    setAdvPolicy(String(empleado.policy_type || 'Flexible'));
    setAdvTolerance(Number.isFinite(Number(empleado.tolerance_minutes)) ? Number(empleado.tolerance_minutes) : 15);
    setAdvSiteId(sitios);
    setAdvBiometricPolicy(String(empleado.biometricPolicyOverride || 'HEREDAR').toUpperCase());
  };

  const handleSaveIndividualBiometricPolicy = async () => {
    if (!can('gestionar_biometria')) return alert('No tienes autorización para gestionar la biometría.');
    if (advancedTarget.length === 0) return alert('⚠️ Selecciona al menos un usuario.');
    setAdvBiometricPolicySaving(true);
    try {
      for (const userId of advancedTarget) {
        await axios.put(obtenerUrlBase('/api', `/biometric/users/${userId}/policy`), {
          biometricPolicyOverride: advBiometricPolicy
        }, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: false
        });
      }
      alert('🔐 Política biométrica individual aplicada correctamente.');
      await fetchAdminData();
      if (advancedTarget.length === 1) {
        const empleado = adminData.find(u => Number(u.id) === Number(advancedTarget[0]));
        if (empleado) setAdvBiometricPolicy(advBiometricPolicy);
      }
    } catch (error) {
      console.error('Error guardando política biométrica individual:', error);
      alert(error.response?.data?.message || 'No fue posible guardar la política biométrica individual.');
    } finally {
      setAdvBiometricPolicySaving(false);
    }
  };

  // 📅 DÍAS LABORALES Y SELECCIÓN DE PERSONAL DEL CALENDARIO MATRICIAL
  const handleToggleCalUser = (userId) => {
    setCalEventos([]);
    setCalSelectedUsers(prev =>
      prev.includes(userId)
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAllCalUsers = () => {
    const todosLosIds = adminData.map(u => u.id);
    const todosSeleccionados =
      todosLosIds.length > 0 &&
      todosLosIds.every(id => calSelectedUsers.includes(id));

    setCalEventos([]);
    setCalSelectedUsers(todosSeleccionados ? [] : todosLosIds);
  };


  // 🏢 CREAR / EDITAR: guarda una nueva sucursal o actualiza una existente.
  const handleCreateSiteSubmit = async (e) => {
    e.preventDefault();
    if (!can('gestionar_sitios')) return alert('No tienes autorización para gestionar sitios.');
    if (!newSiteName.trim()) return alert('⚠️ Por favor ingresa un nombre descriptivo para el sitio de registro.');

    const latitude = parseFloat(newSiteLat);
    const longitude = parseFloat(newSiteLon);
    const radius = parseInt(newSiteRadius, 10);

    if (!Number.isFinite(latitude) || latitude < -90 || latitude > 90) return alert('⚠️ La latitud no es válida.');
    if (!Number.isFinite(longitude) || longitude < -180 || longitude > 180) return alert('⚠️ La longitud no es válida.');
    if (!Number.isFinite(radius) || radius < 1) return alert('⚠️ El radio debe ser mayor que cero.');

    setSiteSaving(true);
    try {
      const payload = { name: newSiteName.trim(), latitude, longitude, radius, address: newSiteAddress.trim(), active: newSiteActive, isDefault: newSiteDefault };
      const url = editingSiteId
        ? obtenerUrlBase('/attendance', `/sites/update/${editingSiteId}`)
        : obtenerUrlBase('/attendance', '/sites/create');

      if (editingSiteId) {
        await axios.put(url, payload, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: false
        });
        alert('✏️ Geocerca actualizada correctamente.');
      } else {
        await axios.post(url, payload, {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: false
        });
        alert('🏢 ¡Sitio de registro guardado correctamente!');
      }

      setEditingSiteId(null);
      setNewSiteName('');
      setNewSiteLat('20.9688');
      setNewSiteLon('-89.6234');
      setNewSiteRadius(100);
      setNewSiteAddress('');
      setNewSiteActive(true);
      setNewSiteDefault(false);
      await fetchSitesList();
      await fetchAdminData();
    } catch (error) {
      console.error('Error al guardar geocerca:', error);
      alert(error.response?.data?.message || 'Error al guardar la geocerca.');
    } finally {
      setSiteSaving(false);
    }
  };

  const handleEditSiteClick = (site) => {
    if (!can('gestionar_sitios')) return alert('No tienes autorización para gestionar sitios.');

    setEditingSiteId(site.id);
    setNewSiteName(site.name || '');
    setNewSiteLat(String(site.latitude ?? ''));
    setNewSiteLon(String(site.longitude ?? ''));
    setNewSiteRadius(Number(site.radius) || 100);
    setNewSiteAddress(site.address || '');
    setNewSiteActive(site.active !== false);
    setNewSiteDefault(site.isDefault === true);

    setTimeout(() => {
      if (leafletMapInstanceRef.current && Number.isFinite(parseFloat(site.latitude)) && Number.isFinite(parseFloat(site.longitude))) {
        const lat = parseFloat(site.latitude);
        const lon = parseFloat(site.longitude);
        leafletMapInstanceRef.current.setView([lat, lon], 15);
        if (leafletMarkerRef.current) leafletMarkerRef.current.setLatLng([lat, lon]);
      }
    }, 50);
  };

  const handleCancelEditSite = () => {
    setEditingSiteId(null);
    setNewSiteName('');
    setNewSiteLat('20.9688');
    setNewSiteLon('-89.6234');
    setNewSiteRadius(100);
    setNewSiteAddress('');
    setNewSiteActive(true);
    setNewSiteDefault(false);
  };

  // 🗑️ ACCIÓN SANEADA: Eliminar una sucursal del catálogo vinculada al entorno dinámico
  const handleDeleteSiteClick = async (siteId) => {
    if (!can('gestionar_sitios')) return alert('No tienes autorización para gestionar sitios.');
    if (!window.confirm('⚠️ ¿Estás seguro de eliminar este sitio de registro?')) return;
    try {
      await axios.delete(obtenerUrlBase('/attendance', `/sites/delete/${siteId}`), {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: false
      });

      alert('🗑️ Sitio removido con éxito.');
      fetchSitesList();
      fetchAdminData();
    } catch (error) {
      console.error(error);
      alert('Error al intentar dar de baja el sitio seleccionado.');
    }
  };

  // 🕒 ACCIÓN CORREGIDA: Guardar políticas de horarios con Geocercas Múltiples (Multi-ID)
  const handleUpdateAdvancedSettingsSubmit = async (e) => {
    e.preventDefault();
    if (!can('gestionar_horarios')) return alert('No tienes autorización para modificar horarios.');
    if (advancedTarget.length === 0) {
      alert('⚠️ Por favor selecciona al menos un empleado.');
      return;
    }
    try {
      const todosLosIds = adminData.map(u => u.id);
      const targetToSend = advancedTarget.length === todosLosIds.length ? 'Todos' : advancedTarget;

      // 🎯 El Secreto: Si el arreglo está vacío mandamos 'Ninguno'; si tiene datos, los une con comas (Ej: "1,2")
      const sitiosToSend = advSiteId.length === 0 ? 'Ninguno' : advSiteId.join(',');

      await axios.put(obtenerUrlBase('/attendance', '/settings/advanced'), {
        targetUserId: targetToSend,
        work_start_time: advStartTime,
        work_end_time: advEndTime,
        work_days: advDays.join(','),
        policy_type: advPolicy,
        tolerance_minutes: parseInt(advTolerance),
        site_id: sitiosToSend // Enviamos la cadena de texto plano multi-sitio homologada
      }, {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: false
      });

      alert('🕒 ¡Políticas de horarios y geocercas múltiples aplicadas con éxito!');
      setAdvancedTarget([]);
      setAdvSiteId([]); // 🎯 Limpiamos el arreglo para la siguiente asignación corporativa
      fetchAdminData();
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || 'Error al guardar la configuración horaria avanzada.');
    }
  };

  // Registro tradicional de usuarios
  const handleCreateUser = async (e) => {
    e.preventDefault();
    if (!can('gestionar_empleados')) return alert('No tienes autorización para gestionar empleados.');
    try {
      await axios.post(obtenerUrlBase('/api', '/users/register'), {
        name: newName, email: newEmail, password: newPassword, role: newRole
      }, {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: false
      });
      alert('🎉 ¡Empleado registrado exitosamente!');
      setNewName(''); setNewEmail(''); setNewPassword(''); setNewRole('Empleado');
      fetchAdminData();
      fetchLiveStatus();
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || 'Hubo un error al registrar al usuario');
    }
  };

  // Modificación tradicional de usuarios
  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!can('gestionar_empleados')) return alert('No tienes autorización para gestionar empleados.');
    try {
      await axios.put(obtenerUrlBase('/api', `/users/${editingUserId}`), {
        name: editName,
        email: editEmail,
        role: editRole,
        password: editPassword
      }, {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: false
      });
      
      alert('💾 ¡Cambios guardados con éxito!');
      setEditingUserId(null);
      setEditPassword('');
      fetchAdminData();
      fetchLiveStatus();
    } catch (error) {
      console.error(error);
      alert(error.response?.data?.message || 'Error al actualizar usuario');
    }
  };

  const startEditing = (user) => {
    setEditingUserId(user.id);
    setEditName(user.name);
    setEditEmail(user.email);
    setEditRole(user.role);
  };

  // 📋 MONITOREO GLOBAL: prepara los registros por empleado según el periodo global seleccionado.
  // No recorta el historial por cantidad de registros; el filtro se realiza por fecha.
  const filteredAdminData = useMemo(() => {
    const ahora = obtenerPartesTimestamp(new Date());

    const selectedIdsValidos = monitorSelectedUserIds
      .map(Number)
      .filter(id => adminData.some(user => Number(user.id) === id));
    const usuariosBase = selectedIdsValidos.length === 0
      ? adminData
      : adminData.filter(user => selectedIdsValidos.includes(Number(user.id)));

    return usuariosBase.map(user => {
      // Plantilla: cada usuario conserva su propio periodo.
      // Lista: continúa usando el periodo general del Monitor.
      const diasIndividuales = Number(monitorUserPeriodDays?.[Number(user.id)] || 0);
      const dias = diasIndividuales > 0 ? diasIndividuales : (monitorViewMode === 'cards' ? 1 : Number(monitorPeriodDays || 1));
      let fechaLimite = null;

      if (ahora?.fechaPlana) {
        const [anio, mes, dia] = ahora.fechaPlana.split('-').map(Number);
        const fecha = new Date(anio, mes - 1, dia);
        fecha.setDate(fecha.getDate() - (dias - 1));
        fechaLimite = `${fecha.getFullYear()}-${String(fecha.getMonth() + 1).padStart(2, '0')}-${String(fecha.getDate()).padStart(2, '0')}`;
      }

      const todasLasMarcas = Array.isArray(user.checkins) ? [...user.checkins] : [];
      todasLasMarcas.sort((a, b) => {
        const pa = obtenerClaveCronologicaTimestamp(a.timestamp);
        const pb = obtenerClaveCronologicaTimestamp(b.timestamp);
        return pb - pa || Number(b.id || 0) - Number(a.id || 0);
      });

      const personnelStatus = resolvePersonnelStatus(user);
      const monitorStatus = personnelStatus.key;
      const monitorStatusLabel = personnelStatus.label;
      const monitorStatusDetail = personnelStatus.detail;

      let records = [...todasLasMarcas];
      if (monitorFilterType !== 'Todos') {
        records = records.filter(checkin => checkin.type === monitorFilterType);
      }

      if (fechaLimite) {
        records = records.filter(checkin => {
          const partes = obtenerPartesTimestamp(checkin.timestamp);
          return partes ? partes.fechaPlana >= fechaLimite : false;
        });
      }

      // El Monitor debe conservar SIEMPRE a todos los usuarios que pertenecen
      // al conjunto seleccionado, aunque no tengan marcaciones en el periodo.
      // Las marcaciones sí se filtran por periodo y tipo, pero el usuario se
      // mantiene visible para poder consultar su estado laboral.
      return { ...user, checkins: records, allCheckins: todasLasMarcas, monitorDays: dias, monitorStartDate: fechaLimite, monitorStatus, monitorStatusLabel, monitorStatusDetail, personnelStatusLabel: personnelStatus.label, personnelStatus };
    });
  }, [adminData, monitorPeriodDays, monitorUserPeriodDays, monitorFilterType, monitorSelectedUserIds, monitorViewMode]);

  const abrirUbicacionEnMapa = useCallback((checkin) => {
    if (checkin?.latitude === undefined || checkin?.latitude === null ||
        checkin?.longitude === undefined || checkin?.longitude === null) {
      return;
    }

    const latLimpia = String(checkin.latitude).replace(/https?:\/\/(www\.)?google\.com\/?/gi, '').replace(/[^0-9.-]/g, '');
    const lonLimpia = String(checkin.longitude).replace(/https?:\/\/(www\.)?google\.com\/?/gi, '').replace(/[^0-9.-]/g, '');

    if (!latLimpia || !lonLimpia) {
      alert('Las coordenadas de esta marcación no son válidas.');
      return;
    }

    const esMovil = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    const urlMaps = esMovil
      ? `geo:${latLimpia},${lonLimpia}?q=${latLimpia},${lonLimpia}`
      : `https://google.com/maps?q=${latLimpia},${lonLimpia}`;

    window.open(urlMaps, '_blank', 'noopener,noreferrer');
  }, []);

  const cardBg = isDarkMode ? '#1e293b' : '#ffffff';
  const borderCol = isDarkMode ? '#334155' : '#e2e8f0';
  const inputBg = isDarkMode ? '#334155' : '#ffffff';
  const textCol = isDarkMode ? '#f8fafc' : '#2d3748';


  // ================================================================
  // 📱 RESPONSIVE REAL — EVITA QUE EL CONTENIDO IMPONGA UN ANCHO MÍNIMO
  // ================================================================


  // Plantilla: todos los usuarios se distribuyen en una sola cuadrícula.
  // El orden prioriza la marcación más reciente de cada usuario y, cuando
  // coincide, conserva un orden estable por estado y nombre. El estado se
  // muestra dentro de cada tarjeta; no se crean columnas o bloques separados
  // por estado.
  const monitorTemplateUsers = useMemo(() => {
    const statusOrder = new Map((Array.isArray(PERSONNEL_STATUS_LEGEND) ? PERSONNEL_STATUS_LEGEND : []).map((item, index) => [item.key, index]));
    return [...filteredAdminData].sort((a, b) => {
      const getLatest = (emp) => {
        const marcas = Array.isArray(emp.checkins) ? emp.checkins : [];
        if (!marcas.length) return Number.NEGATIVE_INFINITY;
        return Math.max(...marcas.map(m => {
          const value = obtenerClaveCronologicaTimestamp(m.timestamp);
          return Number.isFinite(value) ? value : Number.NEGATIVE_INFINITY;
        }));
      };
      const latestDiff = getLatest(b) - getLatest(a);
      if (latestDiff !== 0) return latestDiff;
      const statusDiff = (statusOrder.get(a.monitorStatus || 'fuera_jornada') ?? 999) - (statusOrder.get(b.monitorStatus || 'fuera_jornada') ?? 999);
      if (statusDiff !== 0) return statusDiff;
      return String(a.name || '').localeCompare(String(b.name || ''), 'es', { sensitivity: 'base' });
    });
  }, [filteredAdminData]);

  return (
    <div className="dashboard-mobile-safe" style={{ marginTop: '40px' }}>
      <style>{responsiveStyles}</style>
      {!can('ver_monitor') && longShiftAlerts.filter(a => a.status === 'PENDIENTE').length > 0 && (
        <div style={{ marginBottom: '18px', padding: '15px', borderRadius: '10px', border: '1px solid #f56565', backgroundColor: isDarkMode ? '#2a1719' : '#fff5f5' }}>
          <strong style={{ color: '#c53030', fontSize: '15px' }}>🚨 Jornada prolongada — atención requerida</strong>
          {longShiftAlerts.filter(a => a.status === 'PENDIENTE').map(alerta => (
            <div key={alerta.id} style={{ marginTop: '8px', fontSize: '13px', color: isDarkMode ? '#fecaca' : '#742a2a' }}>
              Tu jornada alcanzó el umbral de <strong>{Number(alerta.hoursThreshold).toFixed(0)} horas</strong>. Un administrador debe confirmar y registrar la atención.
            </div>
          ))}
        </div>
      )}

      {/* NAVEGACIÓN LATERAL · AGRUPADA POR ÁREA Y FILTRADA POR PERMISOS */}
      {isAdministrativeRole && (() => {
        const menuGroups = [
          {
            id: 'inicio', label: 'Inicio', icon: '🏠', alwaysOpen: true,
            items: [
              ...(can('descargar_reportes') ? [{ id: 'executive', icon: '📊', label: 'Dashboard' }] : []),
              ...(!isGlobalSuperAdmin ? [
                { id: 'personalRecords', icon: '📋', label: 'Mis registros' },
                { id: 'personalLeave', icon: '🏖️', label: 'Mis permisos' },
                { id: 'personalAdjustments', icon: '📝', label: 'Mis aclaraciones' },
              ] : []),
            ]
          },
          {
            id: 'operacion', label: 'Operación', icon: '📋',
            items: [
              ...(can('ver_monitor') ? [{ id: 'monitor', icon: '🖥️', label: 'Monitoreo global' }] : []),
              ...(can('ver_calendario') ? [{ id: 'calendar', icon: '📅', label: 'Calendario' }] : []),
              ...(can('descargar_reportes') ? [{ id: 'incidents', icon: '🚨', label: 'Incidencias', badge: incidentRows.some(i => i.status === 'PENDIENTE') } ] : []),
              ...(can('descargar_reportes') ? [{ id: 'notifications', icon: '🔔', label: 'Alertas', count: notificationSummary.unread } ] : []),
              { id: 'adjustments', icon: '📝', label: 'Aclaraciones', badge: adjustmentsAdmin.some(a => a.status === 'PENDIENTE' && String(a.requestedByRole || '').toLowerCase() === 'empleado') }
            ]
          },
          {
            id: 'personal', label: 'Personal', icon: '👥',
            items: [
              ...((can('gestionar_empleados') || can('ver_expedientes') || can('editar_expedientes')) ? [{ id: 'employees', icon: '👥', label: 'Capital Humano' }] : []),
              ...(isAdministrativeRole ? [{ id: 'laborFile', icon: '📁', label: 'Expedientes' }] : []),
              // La administración de administradores queda consolidada en Administración avanzada.
              ...(isAdministrativeRole ? [] : [])
            ]
          },
          {
            id: 'jornadas', label: 'Jornadas', icon: '🕒',
            items: [
              ...(can('gestionar_horarios') ? [{ id: 'schedules', icon: '🕒', label: 'Horarios y jornadas' }] : [])
            ]
          },
          {
            id: 'tiempo', label: 'Tiempo y ausencias', icon: '⏱️',
            items: [
              ...(can('descargar_reportes') ? [{ id: 'overtime', icon: '⏱️', label: 'Horas extra' }] : []),
              { id: 'leave', icon: '🏖️', label: 'Permisos y ausencias' }
            ]
          },
          {
            id: 'reportes', label: 'Reportes', icon: '📊',
            items: [
              ...(can('descargar_reportes') ? [{ id: 'reports', icon: '📊', label: 'Reportes' }, { id: 'advancedSummary', icon: '📈', label: 'Resumen avanzado' }] : [])
            ]
          },
          {
            id: 'administracion', label: 'Administración', icon: '⚙️',
            items: [
              ...((can('gestionar_empleados') || can('gestionar_horarios') || can('gestionar_sitios') || can('gestionar_legislacion') || can('gestionar_empresa') || can('gestionar_biometria')) ? [{ id: 'config', icon: '⚙️', label: 'Configuración' }] : []),
              ...(isAdministrativeRole ? [{ id: 'adminManagement', icon: '🛡️', label: 'Administración avanzada' }] : []),
              ...(can('descargar_reportes') ? [{ id: 'audit', icon: '🧾', label: 'Auditoría' }] : []),
              ...(isGlobalSuperAdmin ? [{ id: 'companies', icon: '🏢', label: 'Empresas' }, { id: 'security', icon: '🔒', label: 'Seguridad global' }] : [])
            ]
          }
          ,
          {
            id: 'cuenta', label: 'Mi cuenta', icon: '👤', alwaysOpen: true,
            items: [{ id: 'account', icon: '👤', label: 'Mi perfil' }]
          }
        ].filter(group => group.alwaysOpen || group.items.length > 0);

        const irA = (id) => {
          if (id === 'personalRecords' || id === 'personalLeave' || id === 'personalAdjustments' || id === 'account') {
            onPersonalView?.(id);
            setActiveTab(id);
            setMobileMenuOpen(false);
            return;
          }
          onPersonalView?.(null);
          setActiveTab(id);
          setMobileMenuOpen(false);
          const group = menuGroups.find(g => g.items.some(item => item.id === id));
          if (group && !group.alwaysOpen) setOpenMenuGroup(group.id);
        };

        const menuItemStyle = (item) => ({
          width: '100%', boxSizing: 'border-box', display: 'flex', alignItems: 'center', gap: sidebarCollapsed ? 0 : '9px',
          justifyContent: sidebarCollapsed ? 'center' : 'flex-start', padding: sidebarCollapsed ? '10px 8px' : '9px 11px',
          border: 'none', borderRadius: '7px', cursor: 'pointer', textAlign: 'left',
          background: activeTab === item.id ? (isDarkMode ? '#2563eb' : '#3182ce') : 'transparent',
          color: activeTab === item.id ? '#fff' : textCol, fontWeight: activeTab === item.id ? 700 : 500,
          fontSize: '13px', transition: 'background .15s ease'
        });

        const currentMenuItem = menuGroups.flatMap(g => g.items).find(i => i.id === activeTab);
        const mobileGo = (id) => irA(id);
        const openAlerts = () => {
          onPersonalView?.(null);
          setActiveTab('notifications');
          setMobileMenuOpen(false);
          setAlertPopoverOpen(false);
          const alertsGroup = menuGroups.find(g => g.items.some(item => item.id === 'notifications'));
          if (alertsGroup && !alertsGroup.alwaysOpen) setOpenMenuGroup(alertsGroup.id);
        };

        const updateAlertPopoverPosition = useCallback(() => {
          if (!alertAnchorRef.current) return;
          const rect = alertAnchorRef.current.getBoundingClientRect();
          const mobile = window.innerWidth <= 768;
          if (mobile) {
            const top = Math.max(8, Math.round(rect.bottom + 12));
            const available = Math.max(220, Math.round(window.innerHeight - top - 12));
            setAlertPopoverStyle({
              position: 'fixed',
              top: `${top}px`,
              left: '4%',
              right: 'auto',
              width: '92%',
              maxWidth: '520px',
              bottom: 'auto',
              height: `${Math.min(Math.round(window.innerHeight * 0.46), available)}px`,
              minHeight: `${Math.min(220, available)}px`,
              maxHeight: `${available}px`,
              zIndex: 2147483647
            });
          } else {
            const width = Math.min(390, Math.max(280, window.innerWidth - 24));
            const left = Math.max(12, Math.min(rect.right - width, window.innerWidth - width - 12));
            setAlertPopoverStyle({
              position: 'fixed',
              top: `${Math.min(rect.bottom + 8, window.innerHeight - 120)}px`,
              left: `${left}px`,
              width: `${width}px`,
              maxHeight: 'min(480px, calc(100vh - 90px))',
              zIndex: 20000
            });
          }
        }, []);

        const toggleAlerts = () => {
          setAlertPopoverOpen(value => {
            const next = !value;
            if (next) requestAnimationFrame(updateAlertPopoverPosition);
            return next;
          });
        };

        const openAlertFromPopover = (notification) => {
          setAlertPopoverOpen(false);
          onPersonalView?.(null);
          setActiveTab('notifications');
          setMobileMenuOpen(false);
          const alertsGroup = menuGroups.find(g => g.items.some(item => item.id === 'notifications'));
          if (alertsGroup && !alertsGroup.alwaysOpen) setOpenMenuGroup(alertsGroup.id);
        };

        useEffect(() => {
          if (!alertPopoverOpen) return undefined;
          const reposition = () => updateAlertPopoverPosition();
          window.addEventListener('resize', reposition);
          window.addEventListener('scroll', reposition, true);
          return () => { window.removeEventListener('resize', reposition); window.removeEventListener('scroll', reposition, true); };
        }, [alertPopoverOpen, updateAlertPopoverPosition]);

        const renderAdminAlertPopover = () => {
          const filas = Array.isArray(notificationRows) ? notificationRows.slice(0, 6) : [];
          return (
            <div className="dashboard-alert-popover dashboard-alert-popover-viewport" style={{ ...alertPopoverStyle, position: 'fixed', top: alertPopoverStyle?.top || '76px', left: alertPopoverStyle?.left || '4%', right: alertPopoverStyle?.right || 'auto', width: alertPopoverStyle?.width || '92%', maxWidth: alertPopoverStyle?.maxWidth || '520px', bottom: alertPopoverStyle?.bottom || undefined, height: alertPopoverStyle?.height || '46vh', minHeight: alertPopoverStyle?.minHeight || '240px', maxHeight: alertPopoverStyle?.maxHeight || '420px', overflowY: 'auto', zIndex: 2147483647 }} onClick={e => e.stopPropagation()}>
              <div className="dashboard-alert-popover-header">
                <strong>🔔 Alertas y solicitudes</strong>
                <span style={{display:'inline-flex',alignItems:'center',gap:8}}>{Number(notificationSummary.unread) || 0} nuevas <button type="button" aria-label="Cerrar alertas" title="Cerrar" onClick={() => setAlertPopoverOpen(false)} style={{border:0,background:'transparent',fontSize:18,lineHeight:1,cursor:'pointer',padding:2}}>×</button></span>
              </div>
              {notificationLoading ? (
                <div className="dashboard-alert-popover-empty">Cargando alertas…</div>
              ) : !filas.length ? (
                <div className="dashboard-alert-popover-empty">No hay alertas pendientes.</div>
              ) : (
                <div>
                  {filas.map(item => (
                    <button key={item.id} type="button" className="dashboard-alert-popover-item" onClick={() => openAlertFromPopover(item)}>
                      <span className="dashboard-alert-popover-dot" />
                      <span style={{ minWidth: 0, flex: 1 }}>
                        <strong>{item.title || item.type || 'Alerta'}</strong>
                        {String(item.type || '').toUpperCase().startsWith('SOLICITUD_') && item.sourceUser?.name && (
                          <span style={{fontWeight:800}}>Solicita: {item.sourceUser.name}</span>
                        )}
                        <span>{item.message || 'Solicitud pendiente de atención.'}</span>
                      </span>
                    </button>
                  ))}
                </div>
              )}
              <button type="button" className="dashboard-alert-popover-footer" onClick={openAlerts}>Ver todas las alertas y solicitudes →</button>
            </div>
          );
        };

        return (
          <>
            <div
              className="dashboard-mobile-topbar"
              style={{ '--dashboard-mobile-bg': isDarkMode ? '#0f172a' : '#ffffff', '--dashboard-mobile-border': borderCol, color: textCol }}
            >
              <button type="button" className="dashboard-mobile-iconbutton" onClick={() => setMobileMenuOpen(true)} aria-label="Abrir menú">☰</button>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div className="dashboard-mobile-topbar-title">{currentMenuItem?.label || 'ANA'}</div>
                <div className="dashboard-mobile-topbar-subtitle">{operationalCompany?.name || companySettings?.companyName || 'Empresa no disponible'}</div>
              </div>
              <div className="dashboard-alert-anchor" ref={alertAnchorRef}>
                <button type="button" className="dashboard-mobile-iconbutton dashboard-alert-button" onClick={toggleAlerts} aria-expanded={alertPopoverOpen} aria-label="Alertas y solicitudes" title="Alertas y solicitudes">🔔{Number(notificationSummary.unread) > 0 && <span className="dashboard-alert-badge">{Number(notificationSummary.unread) > 99 ? '99+' : notificationSummary.unread}</span>}</button>
                {alertPopoverOpen && createPortal(renderAdminAlertPopover(), document.body)}
              </div>
              <button type="button" className="dashboard-mobile-iconbutton" onClick={() => mobileGo('account')} aria-label="Mi perfil">👤</button>
            </div>

            {mobileMenuOpen && <div className="dashboard-mobile-overlay" onClick={() => setMobileMenuOpen(false)} />}

            <div className="dashboard-admin-desktop-alertbar" style={{ '--dashboard-mobile-bg': isDarkMode ? '#0f172a' : '#ffffff', '--dashboard-mobile-border': borderCol, color: textCol }}>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: '15px', fontWeight: 800 }}>{currentMenuItem?.label || 'ANA'}</div>
                <div style={{ fontSize: '10px', opacity: .72, marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{operationalCompany?.name || companySettings?.companyName || 'Empresa no disponible'}</div>
              </div>
              <div className="dashboard-alert-anchor" ref={alertAnchorRef}>
                <button type="button" className="dashboard-desktop-alert-button dashboard-alert-button" onClick={toggleAlerts} aria-expanded={alertPopoverOpen} aria-label="Abrir Alertas y Solicitudes" title="Alertas y Solicitudes">
                  <span aria-hidden="true">🔔</span>
                  <span>Alertas y solicitudes</span>
                  {Number(notificationSummary.unread) > 0 && <span className="dashboard-desktop-alert-badge">{Number(notificationSummary.unread) > 99 ? '99+' : notificationSummary.unread}</span>}
                </button>
                
              </div>
              <button type="button" className="dashboard-desktop-profile-button" onClick={() => mobileGo('account')} aria-label="Mi perfil" title="Mi perfil">👤 Mi perfil</button>
            </div>

          <div className="dashboard-admin-shell" style={{ display: 'flex', alignItems: 'flex-start', gap: '18px', marginBottom: '24px' }}>
            <aside className={mobileMenuOpen ? 'dashboard-mobile-drawer' : 'dashboard-mobile-drawer-closed'} style={{ width: sidebarCollapsed ? '66px' : '248px', flexShrink: 0, position: 'sticky', top: '12px', maxHeight: 'calc(100vh - 24px)', overflowY: 'auto', padding: sidebarCollapsed ? '10px 8px' : '12px', borderRadius: '12px', border: `1px solid ${borderCol}`, background: isDarkMode ? '#111827' : '#f8fafc', boxShadow: '0 3px 12px rgba(0,0,0,.06)', transition: 'width .2s ease, transform .2s ease' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: sidebarCollapsed ? 'center' : 'space-between', gap: '8px', marginBottom: '12px' }}>
                {!sidebarCollapsed && <div><div style={{ color: textCol, fontWeight: 800, fontSize: '14px' }}>Navegación</div><div style={{ color: isDarkMode ? '#94a3b8' : '#718096', fontSize: '10px', marginTop: '2px' }}>Panel administrativo</div></div>}
                <button type="button" onClick={() => setSidebarCollapsed(v => !v)} title={sidebarCollapsed ? 'Expandir menú' : 'Contraer menú'} style={{ width: '34px', height: '34px', border: `1px solid ${borderCol}`, borderRadius: '7px', background: isDarkMode ? '#1e293b' : '#fff', color: textCol, cursor: 'pointer', fontSize: '17px' }}>{sidebarCollapsed ? '☰' : '⬹'}</button>
              </div>

              {isGlobalSuperAdmin && !sidebarCollapsed && (
                <div style={{ marginBottom: '12px', padding: '10px', borderRadius: '9px', border: `1px solid ${borderCol}`, background: isDarkMode ? '#172033' : '#fff' }}>
                  <div style={{ color: isDarkMode ? '#93c5fd' : '#2b6cb0', fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', marginBottom: '5px' }}>Empresa en contexto</div>
                  <select value={selectedGlobalCompanyId} onChange={e => setSelectedGlobalCompanyId(e.target.value)} style={{ width: '100%', padding: '7px 8px', borderRadius: '6px', border: `1px solid ${borderCol}`, background: inputBg, color: textCol, fontSize: '12px' }}>
                    {!globalCompanies.length && <option value="">Cargando empresas…</option>}
                    {globalCompanies.map(c => <option key={c.id} value={c.id}>{c.name} · #{c.id}</option>)}
                  </select>
                  <div style={{ color: isDarkMode ? '#94a3b8' : '#718096', fontSize: '9px', marginTop: '5px' }}>Los módulos operativos respetan esta empresa.</div>
                </div>
              )}
              {!isGlobalSuperAdmin && !sidebarCollapsed && <div style={{ marginBottom: '12px', padding: '8px 10px', borderRadius: '8px', background: isDarkMode ? '#172033' : '#fff', border: `1px solid ${borderCol}`, color: textCol, fontSize: '11px', fontWeight: 700 }}>🏢 {operationalCompany?.name || companySettings?.companyName || 'Empresa no disponible'}</div>}

              <nav style={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                {menuGroups.map(group => {
                  const activeInGroup = group.items.some(item => item.id === activeTab);
                  const isOpen = group.alwaysOpen || openMenuGroup === group.id || activeInGroup;
                  return (
                    <div key={group.id}>
                      <button type="button" onClick={() => { if (group.id === 'inicio') irA('personalRecords'); else if (group.items.length === 1 || group.alwaysOpen) irA(group.items[0].id); else setOpenMenuGroup(g => g === group.id ? '' : group.id); }} title={sidebarCollapsed ? group.label : undefined} style={{ width: '100%', border: 'none', background: activeInGroup && !sidebarCollapsed ? (isDarkMode ? '#1e3a5f' : '#ebf4ff') : 'transparent', color: activeInGroup && !sidebarCollapsed ? (isDarkMode ? '#bfdbfe' : '#2b6cb0') : textCol, borderRadius: '7px', padding: sidebarCollapsed ? '10px 8px' : '9px 10px', display: 'flex', alignItems: 'center', justifyContent: sidebarCollapsed ? 'center' : 'space-between', gap: '8px', cursor: 'pointer', fontWeight: 800, fontSize: '12px' }}>
                        <span style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><span>{group.icon}</span>{!sidebarCollapsed && <span>{group.label}</span>}</span>
                        {!sidebarCollapsed && !group.alwaysOpen && <span>{isOpen ? '⌒' : '⌄'}</span>}
                      </button>
                      {isOpen && !sidebarCollapsed && group.items.length > 0 && (
                        <div style={{ marginTop: '3px', marginBottom: '4px', paddingLeft: '6px', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                          {group.items.map(item => (
                            <button key={item.id} type="button" onClick={() => irA(item.id)} style={menuItemStyle(item)} title={item.label}>
                              <span>{item.icon}</span><span style={{ flex: 1 }}>{item.label}</span>{item.count ? <span style={{ minWidth: '18px', padding: '2px 5px', borderRadius: '10px', background: '#e53e3e', color: '#fff', fontSize: '9px', textAlign: 'center' }}>{item.count}</span> : null}{item.badge ? <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#e53e3e', flexShrink: 0 }} /> : null}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </nav>
            </aside>

            <div className="dashboard-mobile-content" style={{ flex: 1, minWidth: 0 }}>
              <div style={{ marginBottom: '14px', padding: '11px 14px', borderRadius: '10px', border: `1px solid ${borderCol}`, background: isDarkMode ? '#172033' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                <div><div style={{ color: textCol, fontWeight: 800, fontSize: '14px' }}>{menuGroups.flatMap(g => g.items).find(i => i.id === activeTab)?.label || 'Panel administrativo'}</div><div style={{ color: isDarkMode ? '#94a3b8' : '#718096', fontSize: '10px', marginTop: '2px' }}>{isGlobalSuperAdmin ? `Empresa: ${operationalCompany?.name || 'selecciona una empresa'}` : `Empresa: ${operationalCompany?.name || companySettings?.companyName || 'Empresa no disponible'}`}</div></div>
                <div style={{ color: isDarkMode ? '#94a3b8' : '#718096', fontSize: '10px' }}>Selecciona una sección desde el menú lateral</div>
              </div>

              {personalView && personalContent ? personalContent(true) : (
                <>
      {/* FASE 10 · BLOQUE 4: ADMINISTRACIÓN AVANZADA DE EMPLEADOS */}
      {activeTab === 'employees' && (can('gestionar_empleados') || can('ver_expedientes') || can('editar_expedientes')) && (
        <PersonalManagement token={token} isDarkMode={isDarkMode} companyId={operationalCompanyId} canEditExpedientes={can('editar_expedientes')} canManageEmployees={can('gestionar_empleados')} />
      )}

      {/* ADMINISTRACIÓN AVANZADA · ADMINISTRADORES + ALCANCE + DELEGACIÓN + PERMISOS */}
      {activeTab === 'adminManagement' && isAdministrativeRole && (
        <AdministrationAdvanced
          token={token}
          isDarkMode={isDarkMode}
          companyId={operationalCompanyId}
          effectiveRole={effectiveRole}
        />
      )}

      {/* FASE 10 · BLOQUE 1: POLÍTICAS DE JORNADA */}
      {activeTab === 'schedules' && (
        <WorkScheduleManagement
          token={token}
          isDarkMode={isDarkMode}
          companyId={operationalCompanyId}
        />
      )}

      {activeTab === 'schedules' && (
        <UserWorkSettings
          token={token}
          isDarkMode={isDarkMode}
          companyId={operationalCompanyId}
          canManageBiometric={can('gestionar_biometria')}
          canManageSites={can('gestionar_sitios')}
        />
      )}

      {/* FASE 11 · BLOQUE 2: VACACIONES, PERMISOS E INCAPACIDADES */}
      {activeTab === 'leave' && (
        <LeaveManagement token={token} isDarkMode={isDarkMode} companyId={operationalCompanyId} canManage={can('gestionar_permisos')} />
      )}

      {/* FASE 11 · BLOQUE 3: HORAS EXTRA Y CÁLCULO LABORAL */}
      {activeTab === 'overtime' && can('descargar_reportes') && (
        <OvertimeManagement token={token} isDarkMode={isDarkMode} companyId={operationalCompanyId} canManage={can('gestionar_permisos')} />
      )}

      {/* 🎯 PESTAÑA 1: MONITOR EN VIVO */}
      {activeTab === 'live' && (
        <section style={{ background: cardBg, padding: '25px', borderRadius: '12px', border: "1px solid " + borderCol, boxShadow: '0 4px 6px rgba(0,0,0,0.02)', marginBottom: '30px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '10px' }}>
            <div>
              <h2 style={{ color: textCol, margin: 0, fontSize: '20px' }}>🎯 Asistencia en Tiempo Real</h2>
              <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: isDarkMode ? '#94a3b8' : '#718096' }}>Estado operativo e instantáneo de las jornadas activas en las oficinas.</p>
            </div>
            <button 
              type="button"
              onClick={(e) => {
                e.preventDefault();
                fetchLiveStatus();
              }} 
              style={{ padding: '6px 14px', backgroundColor: '#319795', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '13px' }}
            >
              🔄 Actualizar Ahora
            </button>
          </div>

          {longShiftAlerts.filter(a => a.status === 'PENDIENTE').length > 0 && (
            <div style={{ marginBottom: '18px', padding: '15px', borderRadius: '10px', border: '1px solid #f56565', backgroundColor: isDarkMode ? '#2a1719' : '#fff5f5' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px', gap: '10px', flexWrap: 'wrap' }}>
                <div>
                  <strong style={{ color: '#c53030', fontSize: '15px' }}>🚨 Alertas de jornadas prolongadas</strong>
                  <div style={{ fontSize: '12px', color: isDarkMode ? '#fecaca' : '#742a2a', marginTop: '3px' }}>
                    Requieren confirmación y registro de atención por un administrador.
                  </div>
                </div>
                <span style={{ padding: '5px 10px', borderRadius: '16px', background: '#fed7d7', color: '#9b2c2c', fontWeight: 'bold', fontSize: '12px' }}>
                  {longShiftAlerts.filter(a => a.status === 'PENDIENTE').length} pendiente(s)
                </span>
              </div>
              <div style={{ display: 'grid', gap: '10px' }}>
                {longShiftAlerts.filter(a => a.status === 'PENDIENTE').map(alerta => (
                  <div key={alerta.id} style={{ padding: '12px', borderRadius: '8px', backgroundColor: isDarkMode ? '#321b1d' : '#ffffff', border: `1px solid ${borderCol}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <div>
                      <strong style={{ color: textCol }}>🔴 {alerta.userName}</strong>
                      <div style={{ fontSize: '12px', color: isDarkMode ? '#cbd5e1' : '#4a5568', marginTop: '4px' }}>
                        Jornada supera <strong>{Number(alerta.hoursThreshold).toFixed(0)} horas</strong> · Generada: {String(alerta.generatedAt || '').replace('T', ' ').slice(0, 16)}
                      </div>
                    </div>
                    <button type="button" onClick={() => handleAttendLongShiftAlert(alerta.id)} style={{ padding: '8px 12px', border: 'none', borderRadius: '6px', background: '#c53030', color: '#fff', cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>
                      Atender alerta
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '15px' }}>
            {liveStatus.map(emp => {
              // El monitor en vivo ya no mantiene un segundo cálculo de estado.
              // Usa exactamente el mismo estado consolidado de Plantilla/Monitor Global.
              const baseUser = adminData.find(item => String(item.id) === String(emp.id)) || emp;
              const personnelStatus = resolvePersonnelStatus(baseUser);
              const liveStatusVisual = {
                laborando: { background: isDarkMode ? '#12351f' : '#f0fdf4', color: '#16a34a' },
                jornada_concluida: { background: isDarkMode ? '#12351f' : '#f0fdf4', color: '#16a34a' },
                salida_anticipada: { background: isDarkMode ? '#3a2f08' : '#fefce8', color: '#a16207' },
                retardo: { background: isDarkMode ? '#3a2f08' : '#fefce8', color: '#a16207' },
                jornada_extendida: { background: isDarkMode ? '#431a08' : '#fff7ed', color: '#c2410c' },
                cierre_administrativo: { background: isDarkMode ? '#2e1065' : '#faf5ff', color: '#7e22ce' },
                vacaciones: { background: isDarkMode ? '#172554' : '#eff6ff', color: '#2563eb' },
                incapacidad: { background: isDarkMode ? '#500724' : '#fdf2f8', color: '#db2777' },
                permiso: { background: isDarkMode ? '#500724' : '#fdf2f8', color: '#db2777' },
                suspendido: { background: isDarkMode ? '#111827' : '#f3f4f6', color: isDarkMode ? '#f8fafc' : '#111827' },
                fuera_jornada: { background: isDarkMode ? '#1f2937' : '#f9fafb', color: isDarkMode ? '#d1d5db' : '#6b7280' }
              }[personnelStatus.key] || { background: isDarkMode ? '#1f2937' : '#f9fafb', color: isDarkMode ? '#d1d5db' : '#6b7280' };

              return (
                <div key={emp.id} style={{ padding: '15px', borderRadius: '8px', border: `1px solid ${borderCol}`, backgroundColor: isDarkMode ? '#131924' : '#f8f9fa', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <h4 style={{ margin: '0 0 4px 0', color: textCol }}>{emp.name}</h4>
                    <p style={{ margin: '0 0 8px 0', fontSize: '12px', color: isDarkMode ? '#94a3b8' : '#718096' }}>ID: {emp.id} — {emp.email}</p>
                    {emp.ultimaHora !== '--:--' && (
                      <span style={{ fontSize: '11px', color: textCol }}>
                        Última marca: <strong>{emp.ultimaHora} hrs</strong>
                        {emp.geocercaEstatus === 'Fuera de Rango' && <strong style={{ color: '#e53e3e', marginLeft: '6px' }}>(🚨 Fuera de Rango)</strong>}
                      </span>
                    )}
                  </div>
                  <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: '6px', padding: '6px 12px', borderRadius: '20px', fontSize: '12px', fontWeight: 'bold', backgroundColor: liveStatusVisual.background, color: liveStatusVisual.color, textAlign: 'center', minWidth: '110px' }}>
                    <span aria-hidden="true" style={{ width: 9, height: 9, borderRadius: '50%', background: personnelStatus.color, border: personnelStatus.key === 'suspendido' ? '1px solid #475569' : '1px solid rgba(0,0,0,.08)', flexShrink: 0 }} />
                    {personnelStatus.icon} {personnelStatus.label}
                  </span>
                </div>
              );
            })}
            {liveStatusLoading && liveStatus.length === 0 && (
              <p style={{ color: '#718096', fontSize: '14px', gridColumn: '1/-1', textAlign: 'center' }}>
                🔄 Cargando monitor en vivo...
              </p>
            )}

            {!liveStatusLoading && liveStatus.length === 0 && liveStatusError && (
              <div style={{ gridColumn: '1/-1', textAlign: 'center', padding: '12px' }}>
                <p style={{ color: '#e53e3e', fontSize: '14px', marginBottom: '8px' }}>
                  ⚠️ {liveStatusError}
                </p>
                <button
                  type="button"
                  onClick={fetchLiveStatus}
                  style={{ padding: '6px 12px', border: 'none', borderRadius: '6px', background: '#3182ce', color: '#fff', cursor: 'pointer', fontWeight: 'bold' }}
                >
                  🔄 Reintentar
                </button>
              </div>
            )}

            {!liveStatusLoading && liveStatus.length === 0 && !liveStatusError && (
              <p style={{ color: '#a0aec0', fontSize: '14px', gridColumn: '1/-1', textAlign: 'center' }}>
                No hay empleados registrados para monitorear.
              </p>
            )}
          </div>
        </section>
      )}
      {/* 📋 PESTAÑA 2: MONITOREO GLOBAL */}
      {activeTab === 'monitor' && (
        <div style={{ padding: '25px', border: `1px solid ${borderCol}`, borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.05)', backgroundColor: cardBg, transition: 'all 0.3s', marginBottom: '30px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '18px', flexWrap: 'wrap', gap: '15px' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '20px', color: textCol }}>📋 Monitoreo Global de Asistencias y Horas</h2>
              <p style={{ margin: '5px 0 0', fontSize: '13px', color: isDarkMode ? '#94a3b8' : '#718096' }}>
                Alterna entre tarjetas y lista. En Plantilla cada usuario tiene su propio periodo; la vista Lista utiliza un periodo general.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '4px', border: `1px solid ${borderCol}`, borderRadius: '8px', background: isDarkMode ? '#111827' : '#f8fafc' }}>
                <span style={{ padding: '4px 6px', fontSize: '11px', fontWeight: 'bold', color: isDarkMode ? '#cbd5e1' : '#4a5568' }}>Vista:</span>
                <button type="button" aria-pressed={monitorViewMode === 'cards'} onClick={() => setMonitorViewMode('cards')} style={{ padding: '7px 12px', borderRadius: '6px', border: monitorViewMode === 'cards' ? '1px solid #3182ce' : '1px solid transparent', background: monitorViewMode === 'cards' ? '#3182ce' : 'transparent', color: monitorViewMode === 'cards' ? '#fff' : textCol, cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>▦ Tarjetas</button>
                <button type="button" aria-pressed={monitorViewMode === 'list'} onClick={() => setMonitorViewMode('list')} style={{ padding: '7px 12px', borderRadius: '6px', border: monitorViewMode === 'list' ? '1px solid #3182ce' : '1px solid transparent', background: monitorViewMode === 'list' ? '#3182ce' : 'transparent', color: monitorViewMode === 'list' ? '#fff' : textCol, cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>☷ Lista</button>
              </div>
              {monitorViewMode === 'list' && (
                <>
                  <label style={{ fontSize: '12px', fontWeight: 'bold', color: isDarkMode ? '#94a3b8' : '#4a5568', marginLeft: '4px' }}>Periodo de lista:</label>
                  <select value={monitorPeriodDays} onChange={(e) => setMonitorPeriodDays(Number(e.target.value))} style={{ padding: '7px 10px', borderRadius: '6px', border: `1px solid ${borderCol}`, backgroundColor: inputBg, color: textCol, cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>
                    <option value={1}>Del día</option>
                    <option value={4}>Últimos 4 días</option>
                    <option value={7}>Últimos 7 días</option>
                    <option value={15}>Últimos 15 días</option>
                    <option value={30}>Últimos 30 días</option>
                  </select>
                </>
              )}
              <label style={{ fontSize: '12px', fontWeight: 'bold', color: isDarkMode ? '#94a3b8' : '#4a5568', marginLeft: '4px' }}>Tipo:</label>
              <select value={monitorFilterType} onChange={(e) => setMonitorFilterType(e.target.value)} style={{ padding: '7px 10px', borderRadius: '6px', border: `1px solid ${borderCol}`, backgroundColor: inputBg, color: textCol, cursor: 'pointer', fontWeight: 'bold', fontSize: '12px' }}>
                <option value="Todos">🔄 Entradas y Salidas</option>
                <option value="Entrada">📥 Solo Entradas</option>
                <option value="Salida">📤 Solo Salidas</option>
              </select>
            </div>
          </div>


          {(() => {
            const obtenerApellidoOrden = (nombre) => {
              const partes = String(nombre || '').trim().split(/\s+/).filter(Boolean);
              return partes.length ? partes[partes.length - 1] : '';
            };
            const usuariosMonitorOrdenados = [...adminData].sort((a, b) =>
              obtenerApellidoOrden(a.name).localeCompare(obtenerApellidoOrden(b.name), 'es', { sensitivity: 'base' }) ||
              String(a.name || '').localeCompare(String(b.name || ''), 'es', { sensitivity: 'base' })
            );
            const seleccionadosValidos = monitorSelectedUserIds.filter(id => adminData.some(u => Number(u.id) === Number(id)));
            const etiquetaUsuariosMonitor = seleccionadosValidos.length === 0
              ? 'Todos los empleados'
              : seleccionadosValidos.length === 1
                ? '1 empleado seleccionado'
                : `${seleccionadosValidos.length} empleados seleccionados`;

            const toggleMonitorUser = (id) => {
              const numericId = Number(id);
              setMonitorSelectedUserIds(prev => {
                const actual = prev.map(Number);
                return actual.includes(numericId) ? actual.filter(x => x !== numericId) : [...actual, numericId];
              });
            };

            return (
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', alignItems: 'center', margin: '4px 0 12px', padding: '10px 11px', borderRadius: 8, border: `1px solid ${borderCol}`, background: isDarkMode ? '#111827' : '#f8fafc' }}>
                <span style={{ fontSize: '12px', fontWeight: 800, color: textCol }}>👥 Empleados:</span>
                <div style={{ position: 'relative' }}>
                  <button type="button" onClick={() => setMonitorUserFilterOpen(v => !v)} aria-expanded={monitorUserFilterOpen} style={{ minWidth: '220px', padding: '8px 11px', borderRadius: '6px', border: `1px solid ${borderCol}`, background: inputBg, color: textCol, cursor: 'pointer', fontWeight: 700, fontSize: '12px', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' }}>
                    <span>{etiquetaUsuariosMonitor}</span><span>{monitorUserFilterOpen ? '⌃' : '⌄'}</span>
                  </button>
                  {monitorUserFilterOpen && (
                    <div style={{ position: 'absolute', zIndex: 30, top: 'calc(100% + 4px)', left: 0, width: '310px', maxHeight: '330px', overflowY: 'auto', padding: '8px', border: `1px solid ${borderCol}`, borderRadius: '8px', background: cardBg, boxShadow: '0 8px 24px rgba(0,0,0,.15)' }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 6px', borderBottom: `1px solid ${borderCol}`, marginBottom: '5px', color: textCol, fontSize: '12px', fontWeight: 800, cursor: 'pointer' }}>
                        <input type="checkbox" checked={monitorSelectedUserIds.length === 0} onChange={() => setMonitorSelectedUserIds([])} />
                        Todos los empleados
                      </label>
                      {usuariosMonitorOrdenados.map(emp => {
                        const seleccionado = monitorSelectedUserIds.includes(Number(emp.id));
                        const apellido = obtenerApellidoOrden(emp.name);
                        return (
                          <label key={`monitor-user-filter-${emp.id}`} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px', color: textCol, fontSize: '12px', cursor: 'pointer', borderRadius: '5px', background: seleccionado ? (isDarkMode ? '#1e3a5f' : '#ebf4ff') : 'transparent' }}>
                            <input type="checkbox" checked={seleccionado} onChange={() => toggleMonitorUser(emp.id)} />
                            <span style={{ flex: 1 }}>{apellido}, {String(emp.name || '').trim().split(/\s+/).slice(0, -1).join(' ') || emp.name}</span>
                          </label>
                        );
                      })}
                      <div style={{ borderTop: `1px solid ${borderCol}`, marginTop: '5px', padding: '7px 6px 2px', fontSize: '10px', color: isDarkMode ? '#94a3b8' : '#718096' }}>
                        Ordenado alfabéticamente por apellido.
                      </div>
                    </div>
                  )}
                </div>
                {monitorSelectedUserIds.length > 0 && (
                  <button type="button" onClick={() => setMonitorSelectedUserIds([])} style={{ padding: '7px 10px', border: 'none', borderRadius: '6px', background: '#718096', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '11px' }}>↺ Mostrar todos</button>
                )}
              </div>
            );
          })()}

          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', margin: '4px 0 12px', padding: '9px 11px', borderRadius: 8, border: `1px solid ${borderCol}`, background: isDarkMode ? '#111827' : '#f8fafc' }}>
            {PERSONNEL_STATUS_LEGEND.map(item => (
              <span key={item.key} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 10, color: isDarkMode ? '#cbd5e1' : '#4a5568' }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: item.color, border: item.key === 'suspendido' ? '1px solid #475569' : '1px solid rgba(0,0,0,.08)' }} />
                {item.label}
              </span>
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap', margin: '4px 0 8px' }}>
            <div style={{ fontSize: '11px', color: isDarkMode ? '#94a3b8' : '#718096' }}>Plantilla proyectada: <strong style={{ color: textCol }}>{filteredAdminData.length}</strong> usuario{filteredAdminData.length === 1 ? '' : 's'}</div>
            {monitorViewMode === 'cards' && Object.keys(monitorUserPeriodDays).length > 0 && (
              <button type="button" onClick={restablecerPeriodosMonitorUsuarios} style={{ padding: '6px 9px', border: `1px solid ${borderCol}`, borderRadius: '6px', background: inputBg, color: textCol, cursor: 'pointer', fontWeight: 700, fontSize: '10px' }}>↺ Restablecer periodos individuales</button>
            )}
          </div>

          {monitorViewMode === 'cards' ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 360px))', justifyContent: 'start', alignItems: 'start', gap: '12px', marginTop: '10px' }}>
            {monitorTemplateUsers.map((emp) => {
              // El Monitor siempre ordena por la fecha/hora EFECTIVA de la marca.
              // Esto es especialmente importante para aclaraciones aceptadas: la marca
              // puede haber sido creada hoy, pero debe ocupar en el historial el lugar
              // correspondiente a la fecha/hora que la aclaración corrigió.
              const marcasEsteUsuario = (Array.isArray(emp.checkins) ? [...emp.checkins] : []).sort((a, b) => {
                const ta = obtenerClaveCronologicaTimestamp(a.timestamp);
                const tb = obtenerClaveCronologicaTimestamp(b.timestamp);
                return tb - ta || Number(b.id || 0) - Number(a.id || 0);
              });
              // Para el cómputo nunca contamos una marca original que fue sustituida
              // por una aclaración aceptada. Las marcas se siguen mostrando arriba
              // para conservar la auditoría completa.
              const marcasEfectivas = marcasEsteUsuario.filter(marca => marca.isEffectiveForCalculation !== false);

              const calculo = emp.calculationSummary || {};
              const resumenSemanal = Array.isArray(calculo.resumenSemanal) ? calculo.resumenSemanal : [];

              // El resumen que entrega el backend contiene todo el historial y
              // debe permanecer intacto para Reportes y Calendario. El Monitor
              // Global muestra un periodo seleccionado por usuario: su contador
              // Total se obtiene sumando los minutos de jornadas ya calculadas
              // por el motor existente, limitadas únicamente para esta vista.
              const fechaMonitorHoy = obtenerPartesTimestamp(new Date())?.fechaPlana || '';
              const jornadasMonitorPeriodo = (Array.isArray(calculo.jornadas) ? calculo.jornadas : [])
                .filter(j => {
                  const fechaJornada = String(j?.fecha || '').slice(0, 10);
                  if (!fechaJornada) return false;
                  if (emp.monitorStartDate && fechaJornada < emp.monitorStartDate) return false;
                  if (fechaMonitorHoy && fechaJornada > fechaMonitorHoy) return false;
                  return true;
                });
              const totalAcumuladoMinutos = Math.max(0, Math.round(
                jornadasMonitorPeriodo.reduce((sum, j) => sum + (Number(j.minutosTrabajados) || 0), 0)
              ));
              // El resumen semanal del motor contiene todo el historial del usuario.
              // Para el Monitor Global únicamente se muestran las categorías de las
              // semanas que tienen jornadas dentro del periodo seleccionado en esta
              // tarjeta. Así no se arrastra el acumulado de meses anteriores. La
              // clasificación legal sigue siendo la misma y no se modifica el motor.
              const semanasIncluidasMonitor = new Set(
                jornadasMonitorPeriodo.map(j => String(j?.semana || '')).filter(Boolean)
              );
              const resumenSemanalMonitor = resumenSemanal.filter(s =>
                semanasIncluidasMonitor.has(String(s?.semana || ''))
              );
              const ordinariasMinutos = resumenSemanalMonitor.reduce((sum, s) => sum + (Number(s.horasOrdinariasMinutos) || 0), 0);
              const doblesMinutos = resumenSemanalMonitor.reduce((sum, s) => sum + (Number(s.horasDoblesMinutos) || 0), 0);
              const triplesMinutos = resumenSemanalMonitor.reduce((sum, s) => sum + (Number(s.horasTriplesMinutos) || 0), 0);
              const superoTope = resumenSemanalMonitor.some(s => Number(s.horasTrabajadasMinutos || 0) > Number(s.limiteMinutos || 0));
              const statusVisual = {
                laborando: { icon: '🟢', background: isDarkMode ? '#12351f' : '#f0fdf4', color: '#16a34a', dot: '#16a34a' },
                jornada_concluida: { icon: '🟢', background: isDarkMode ? '#12351f' : '#f0fdf4', color: '#16a34a', dot: '#16a34a' },
                salida_anticipada: { icon: '🟡', background: isDarkMode ? '#3a2f08' : '#fefce8', color: '#a16207', dot: '#eab308' },
                retardo: { icon: '🟡', background: isDarkMode ? '#3a2f08' : '#fefce8', color: '#a16207', dot: '#eab308' },
                jornada_extendida: { icon: '🟠', background: isDarkMode ? '#431a08' : '#fff7ed', color: '#c2410c', dot: '#f97316' },
                cierre_administrativo: { icon: '🟣', background: isDarkMode ? '#2e1065' : '#faf5ff', color: '#7e22ce', dot: '#9333ea' },
                vacaciones: { icon: '🔵', background: isDarkMode ? '#172554' : '#eff6ff', color: '#2563eb', dot: '#2563eb' },
                incapacidad: { icon: '🌸', background: isDarkMode ? '#500724' : '#fdf2f8', color: '#db2777', dot: '#ec4899' },
                permiso: { icon: '🌸', background: isDarkMode ? '#500724' : '#fdf2f8', color: '#db2777', dot: '#ec4899' },
                suspendido: { icon: '⚫', background: isDarkMode ? '#111827' : '#f3f4f6', color: isDarkMode ? '#f8fafc' : '#111827', dot: '#000000' },
                fuera_jornada: { icon: '⚪', background: isDarkMode ? '#1f2937' : '#f9fafb', color: isDarkMode ? '#d1d5db' : '#6b7280', dot: '#d1d5db' }
              }[emp.monitorStatus] || { icon: '⚪', background: isDarkMode ? '#1f2937' : '#f9fafb', color: isDarkMode ? '#d1d5db' : '#6b7280', dot: '#d1d5db' };

              return (
                <div key={emp.id} style={{ padding: '20px', borderRadius: '12px', backgroundColor: cardBg, border: `1px solid ${superoTope ? '#fed7d7' : borderCol}`, boxShadow: '0 2px 8px rgba(0,0,0,0.02)', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${borderCol}`, paddingBottom: '9px', gap: '10px' }}>
                    <span style={{ fontSize: '14px', fontWeight: 'bold', color: textCol }}>👤 {emp.name} <small style={{ color: '#718096', fontWeight: 'normal' }}>(ID: {emp.id})</small></span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', padding: '4px 9px', borderRadius: '999px', backgroundColor: statusVisual.background, color: statusVisual.color, fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                      <span aria-hidden="true" style={{ width: 9, height: 9, borderRadius: '50%', background: statusVisual.dot, border: emp.monitorStatus === 'suspendido' ? '1px solid #475569' : '1px solid rgba(0,0,0,.08)', flexShrink: 0 }} />
                      {statusVisual.icon} {emp.monitorStatusLabel || emp.personnelStatusLabel || 'Fuera de jornada'}
                    </span>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '11px', color: isDarkMode ? '#cbd5e1' : '#4a5568' }}>{emp.monitorStatusDetail}</span>
                    {superoTope && <span style={{ fontSize: '10px', padding: '3px 7px', borderRadius: '4px', backgroundColor: '#fff5f5', color: '#c53030', fontWeight: 'bold' }}>⚠️ Exceso semanal</span>}
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '1px', padding: '7px 8px', borderRadius: '7px', border: `1px solid ${borderCol}`, background: isDarkMode ? '#111827' : '#f8fafc' }}>
                    <span style={{ fontSize: '10px', fontWeight: 800, color: isDarkMode ? '#cbd5e1' : '#4a5568' }}>📅 Periodo de este usuario</span>
                    <select value={Number(monitorUserPeriodDays?.[Number(emp.id)] || 1)} onChange={(e) => cambiarPeriodoMonitorUsuario(emp.id, e.target.value)} style={{ padding: '5px 7px', borderRadius: '5px', border: `1px solid ${borderCol}`, background: inputBg, color: textCol, fontSize: '10px', fontWeight: 700 }}>
                      <option value={1}>Del día</option>
                      <option value={4}>Últimos 4 días</option>
                      <option value={7}>Últimos 7 días</option>
                      <option value={15}>Últimos 15 días</option>
                      <option value={30}>Últimos 30 días</option>
                    </select>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px', textAlign: 'center', backgroundColor: isDarkMode ? '#131924' : '#f8fafc', padding: '8px', borderRadius: '8px', border: `1px solid ${borderCol}` }}>
                    <div><span style={{ fontSize: '9px', color: '#718096', display: 'block', fontWeight: 'bold' }}>Total periodo</span><strong style={{ fontSize: '12px', color: textCol }}>{formatMinutesFromMinutes(totalAcumuladoMinutos)}</strong></div>
                    <div><span style={{ fontSize: '9px', color: '#3182ce', display: 'block', fontWeight: 'bold' }}>Ord</span><strong style={{ fontSize: '12px', color: '#3182ce' }}>{formatMinutesFromMinutes(ordinariasMinutos)}</strong></div>
                    <div><span style={{ fontSize: '9px', color: '#dd6b20', display: 'block', fontWeight: 'bold' }}>Dob</span><strong style={{ fontSize: '12px', color: '#dd6b20' }}>{formatMinutesFromMinutes(doblesMinutos)}</strong></div>
                    <div><span style={{ fontSize: '9px', color: triplesMinutos > 0 ? '#e53e3e' : '#48bb78', display: 'block', fontWeight: 'bold' }}>Trip</span><strong style={{ fontSize: '12px', color: triplesMinutos > 0 ? '#e53e3e' : '#48bb78' }}>{formatMinutesFromMinutes(triplesMinutos)}</strong></div>
                  </div>

                  <div>
                    <span style={{ fontSize: '11px', fontWeight: 'bold', color: '#718096', display: 'block', marginBottom: '7px' }}>
                      📋 Registros del periodo ({marcasEsteUsuario.length})
                    </span>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                      {(() => {
                        const cierresPeriodo = (Array.isArray(emp.jornadaClosures) ? emp.jornadaClosures : [])
                          .filter(cierre => String(cierre.closureType || '').toUpperCase() === 'ADMINISTRATIVA')
                          .filter(cierre => {
                            const entry = (Array.isArray(emp.checkins) ? emp.checkins : []).find(m => Number(m.id) === Number(cierre.entryAttendanceId));
                            const entryParts = entry?.timestamp ? obtenerPartesTimestamp(entry.timestamp) : null;
                            const closureParts = cierre.closedAt ? obtenerPartesTimestamp(cierre.closedAt) : null;
                            return !emp.monitorStartDate || Boolean(
                              (entryParts?.fechaPlana && entryParts.fechaPlana >= emp.monitorStartDate) ||
                              (closureParts?.fechaPlana && closureParts.fechaPlana >= emp.monitorStartDate)
                            );
                          })
                          .sort((a, b) => new Date(b.closedAt || 0).getTime() - new Date(a.closedAt || 0).getTime());

                        return cierresPeriodo.map(cierre => {
                          const entry = (Array.isArray(emp.checkins) ? emp.checkins : []).find(m => Number(m.id) === Number(cierre.entryAttendanceId));
                          const entradaTexto = entry?.timestamp ? formatearTimestampMerida(entry.timestamp) : 'Entrada no disponible en el historial cargado';
                          const salidaAdministrativaTexto = cierre.closedAt ? formatearTimestampMerida(cierre.closedAt) : '—';
                          return (
                            <div key={`cierre-admin-${cierre.id}`} style={{ padding: '10px', borderRadius: '8px', backgroundColor: isDarkMode ? '#21152f' : '#faf5ff', border: '1px solid #d8b4fe' }}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                                <strong style={{ color: '#7e22ce', fontSize: '12px' }}>🟣 Salida administrativa</strong>
                                <span style={{ padding: '3px 7px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold', backgroundColor: '#f3e8ff', color: '#7e22ce', whiteSpace: 'nowrap' }}>
                                  CIERRE ADMINISTRATIVO
                                </span>
                              </div>
                              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '7px', marginTop: '8px' }}>
                                <div style={{ padding: '7px 8px', borderRadius: '6px', background: isDarkMode ? '#172033' : '#ffffff', border: `1px solid ${borderCol}` }}>
                                  <div style={{ fontSize: '9px', color: '#718096', fontWeight: 'bold', textTransform: 'uppercase' }}>Entrada original</div>
                                  <strong style={{ display: 'block', marginTop: '2px', fontSize: '11px', color: textCol }}>📥 {entradaTexto}</strong>
                                  <span style={{ fontSize: '9px', color: '#718096' }}>Registro #{cierre.entryAttendanceId}</span>
                                </div>
                                <div style={{ padding: '7px 8px', borderRadius: '6px', background: isDarkMode ? '#2e1065' : '#ffffff', border: '1px solid #d8b4fe' }}>
                                  <div style={{ fontSize: '9px', color: '#7e22ce', fontWeight: 'bold', textTransform: 'uppercase' }}>Salida administrativa</div>
                                  <strong style={{ display: 'block', marginTop: '2px', fontSize: '11px', color: '#7e22ce' }}>📤 {salidaAdministrativaTexto}</strong>
                                  <span style={{ fontSize: '9px', color: '#718096' }}>Cierre #{cierre.id}</span>
                                </div>
                              </div>
                              <div style={{ fontSize: '10px', color: '#718096', marginTop: '7px' }}>
                                Estado del cierre: <strong style={{ color: '#7e22ce' }}>{cierre.status || '—'}</strong>
                              </div>
                              {cierre.reason ? <div style={{ fontSize: '10px', color: '#6b46c1', marginTop: '4px' }}>Motivo: {cierre.reason}</div> : null}
                            </div>
                          );
                        });
                      })()}

                      {marcasEsteUsuario.map((marca) => {
                        const esEntrada = marca.type === 'Entrada';
                        const partes = obtenerPartesTimestamp(marca.timestamp);
                        const tieneGps = marca.latitude !== undefined && marca.latitude !== null && marca.longitude !== undefined && marca.longitude !== null;
                        return (
                          <div key={marca.id} style={{ padding: '9px', borderRadius: '7px', backgroundColor: isDarkMode ? '#1a202c' : '#fff', border: `1px solid ${borderCol}` }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' }}>
                              <div>
                                <strong style={{ color: textCol, fontSize: '12px' }}>{formatearTimestampMerida(marca.timestamp)}</strong>
                                <div style={{ fontSize: '10px', color: '#718096', marginTop: '2px' }}>Registro #{marca.id}</div>
                                {(() => { const origen = obtenerEtiquetaOrigenMarca(marca); return <div style={{ fontSize: '10px', color: origen.color, fontWeight: 'bold', marginTop: '3px' }}>{origen.texto}</div>; })()}
                              </div>
                              <span style={{ padding: '3px 6px', borderRadius: '4px', fontSize: '10px', fontWeight: 'bold', backgroundColor: esEntrada ? '#e6fffa' : '#fff5f5', color: esEntrada ? '#319795' : '#e53e3e', whiteSpace: 'nowrap' }}>
                                {esEntrada ? '📥 Entrada' : '📤 Salida'}
                              </span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px', marginTop: '7px', flexWrap: 'wrap' }}>
                              <span style={{ fontSize: '10px', fontWeight: 'bold', color: (String(marca.status || '').includes('Válido') || (tieneGps && String(marca.status || '').trim().toUpperCase() === 'N/A')) ? '#48bb78' : '#e53e3e' }}>
                                📍 {tieneGps && String(marca.status || '').trim().toUpperCase() === 'N/A' ? 'GPS capturado · Geocerca desactivada' : (marca.status || 'Válido')}
                              </span>
                              {tieneGps ? (
                                <button type="button" onClick={() => abrirUbicacionEnMapa(marca)} style={{ background: 'none', border: 'none', color: '#3182ce', fontWeight: 'bold', textDecoration: 'underline', fontSize: '11px', cursor: 'pointer', padding: 0 }}>
                                  🗺️ Ver ubicación
                                </button>
                              ) : (
                                <span style={{ fontSize: '10px', color: '#a0aec0' }}>Sin GPS</span>
                              )}
                            </div>
                          </div>
                        );
                      })}

                      {marcasEsteUsuario.length === 0 && (
                        <span style={{ fontSize: '11px', color: '#a0aec0', fontStyle: 'italic', textAlign: 'center', padding: '10px' }}>
                          Sin marcas registradas en este periodo.
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          ) : (
            <div style={{ marginTop: '10px', border: `1px solid ${borderCol}`, borderRadius: '10px', overflow: 'hidden', background: cardBg }}>
              <div style={{ padding: '10px 12px', borderBottom: `1px solid ${borderCol}`, background: isDarkMode ? '#111827' : '#f8fafc', color: isDarkMode ? '#cbd5e1' : '#4a5568', fontSize: '11px', display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                <strong>Registros del periodo</strong>
                <span>Se muestran todas las marcaciones de los usuarios y filtros seleccionados.</span>
              </div>
              <div style={{ overflowX: 'auto' }}>
                <div style={{ minWidth: '780px' }}>
                  <div style={{ display: 'grid', gridTemplateColumns: 'minmax(250px, 1.6fr) 170px 110px 130px minmax(180px, 1.2fr)', gap: '10px', padding: '9px 12px', borderBottom: `1px solid ${borderCol}`, color: '#718096', fontSize: '10px', fontWeight: 'bold', textTransform: 'uppercase' }}>
                    <span>Usuario</span><span>Fecha y hora</span><span>Tipo</span><span>Estado</span><span>Origen / ubicación</span>
                  </div>
                  {filteredAdminData
                    .flatMap((emp) => {
                      const marcas = Array.isArray(emp.checkins) ? emp.checkins : [];
                      if (marcas.length > 0) return marcas.map((marca) => ({ emp, marca, sinRegistro: false }));
                      return [{ emp, marca: null, sinRegistro: true }];
                    })
                    .sort((a, b) => {
                      // Los usuarios con registros se ordenan por la marca más reciente.
                      // Los que no tienen registros quedan al final, pero permanecen visibles.
                      if (a.sinRegistro && !b.sinRegistro) return 1;
                      if (!a.sinRegistro && b.sinRegistro) return -1;
                      if (a.sinRegistro && b.sinRegistro) return String(a.emp.name || '').localeCompare(String(b.emp.name || ''));
                      const ta = obtenerClaveCronologicaTimestamp(a.marca.timestamp);
                      const tb = obtenerClaveCronologicaTimestamp(b.marca.timestamp);
                      return tb - ta || Number(b.marca.id || 0) - Number(a.marca.id || 0);
                    })
                    .map(({ emp, marca, sinRegistro }) => {
                      const esEntrada = !sinRegistro && marca.type === 'Entrada';
                      const tieneGps = !sinRegistro && marca.latitude !== undefined && marca.latitude !== null && marca.longitude !== undefined && marca.longitude !== null;
                      const statusVisual = {
                        laborando: { icon: '🟢', background: isDarkMode ? '#12351f' : '#f0fdf4', color: '#16a34a', dot: '#16a34a' },
                        jornada_concluida: { icon: '🟢', background: isDarkMode ? '#12351f' : '#f0fdf4', color: '#16a34a', dot: '#16a34a' },
                        salida_anticipada: { icon: '🟡', background: isDarkMode ? '#3a2f08' : '#fefce8', color: '#a16207', dot: '#eab308' },
                        retardo: { icon: '🟡', background: isDarkMode ? '#3a2f08' : '#fefce8', color: '#a16207', dot: '#eab308' },
                        jornada_extendida: { icon: '🟠', background: isDarkMode ? '#431a08' : '#fff7ed', color: '#c2410c', dot: '#f97316' },
                        cierre_administrativo: { icon: '🟣', background: isDarkMode ? '#2e1065' : '#faf5ff', color: '#7e22ce', dot: '#9333ea' },
                        vacaciones: { icon: '🔵', background: isDarkMode ? '#172554' : '#eff6ff', color: '#2563eb', dot: '#2563eb' },
                        incapacidad: { icon: '🌸', background: isDarkMode ? '#500724' : '#fdf2f8', color: '#db2777', dot: '#ec4899' },
                        permiso: { icon: '🌸', background: isDarkMode ? '#500724' : '#fdf2f8', color: '#db2777', dot: '#ec4899' },
                        suspendido: { icon: '⚫', background: isDarkMode ? '#111827' : '#f3f4f6', color: isDarkMode ? '#f8fafc' : '#111827', dot: '#000000' },
                        fuera_jornada: { icon: '⚪', background: isDarkMode ? '#1f2937' : '#f9fafb', color: isDarkMode ? '#d1d5db' : '#6b7280', dot: '#d1d5db' }
                      }[emp.monitorStatus] || { icon: '⚪', background: isDarkMode ? '#1f2937' : '#f9fafb', color: isDarkMode ? '#d1d5db' : '#6b7280', dot: '#d1d5db' };
                      const origen = !sinRegistro ? obtenerEtiquetaOrigenMarca(marca) : null;
                      return (
                        <div key={sinRegistro ? `lista-monitor-${emp.id}-sin-registro` : `lista-monitor-${emp.id}-${marca.id}`} style={{ display: 'grid', gridTemplateColumns: 'minmax(250px, 1.6fr) 170px 110px 130px minmax(180px, 1.2fr)', gap: '10px', alignItems: 'center', padding: '10px 12px', borderBottom: `1px solid ${borderCol}`, background: cardBg }}>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ color: textCol, fontSize: '12px', fontWeight: 'bold', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>👤 {emp.name} <span style={{ color: '#718096', fontWeight: 'normal' }}>#{emp.id}</span></div>
                            <div style={{ color: '#718096', fontSize: '9px', marginTop: '3px' }}>{sinRegistro ? 'Sin registros en el periodo' : `Registro #${marca.id}`}</div>
                          </div>
                          <div style={{ color: textCol, fontSize: '11px', fontWeight: 'bold' }}>{sinRegistro ? '— Sin registro' : formatearTimestampMerida(marca.timestamp)}</div>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', justifySelf: 'start', padding: '4px 7px', borderRadius: '999px', background: sinRegistro ? (isDarkMode ? '#1f2937' : '#f3f4f6') : (esEntrada ? (isDarkMode ? '#123b36' : '#e6fffa') : (isDarkMode ? '#3a1720' : '#fff5f5')), color: sinRegistro ? (isDarkMode ? '#d1d5db' : '#6b7280') : (esEntrada ? '#319795' : '#e53e3e'), fontSize: '10px', fontWeight: 'bold' }}>{sinRegistro ? '—' : (esEntrada ? '📥 Entrada' : '📤 Salida')}</div>
                          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', justifySelf: 'start', padding: '4px 7px', borderRadius: '999px', background: statusVisual.background, color: statusVisual.color, fontSize: '10px', fontWeight: 'bold' }}><span style={{ width: 7, height: 7, borderRadius: '50%', background: statusVisual.dot }} />{statusVisual.icon} {emp.monitorStatusLabel || 'Fuera de jornada'}</div>
                          <div style={{ minWidth: 0 }}>
                            <div style={{ color: sinRegistro ? '#718096' : origen.color, fontSize: '10px', fontWeight: 'bold' }}>{sinRegistro ? 'Sin marcaciones en el periodo' : origen.texto}</div>
                            <div style={{ color: '#718096', fontSize: '9px', marginTop: '3px' }}>{sinRegistro ? 'El usuario permanece visible para monitoreo laboral' : (tieneGps ? '📍 GPS capturado' : 'Sin GPS')}</div>
                          </div>
                        </div>
                      );
                    })}
                </div>
              </div>
            </div>
          )}

          {adminData.length === 0 && (
            <div style={{ padding: '40px', textAlign: 'center', color: '#a0aec0', fontStyle: 'italic' }}>
              No se encontraron empleados registrados en el sistema.
            </div>
          )}
        </div>
      )}

      {/* 📅 PESTAÑA 3: CALENDARIO CORPORATIVO DE JORNADAS HORARIAS (REFORMA LFT 2027) */}
      {activeTab === 'reports' && can('descargar_reportes') && (
        <section className="dashboard-report-section" style={{ background: cardBg, padding: '25px', borderRadius: '12px', border: `1px solid ${borderCol}`, boxShadow: '0 4px 6px rgba(0,0,0,0.03)', marginBottom: '30px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '15px' }}>
            <div>
              <h2 style={{ margin: 0, color: textCol, fontSize: '20px' }}>📊 Reportes de Asistencia</h2>
              <p style={{ margin: '5px 0 0', color: isDarkMode ? '#94a3b8' : '#718096', fontSize: '13px' }}>Los registros y el reporte administrativo son independientes: uno conserva la evidencia auditada y el otro calcula jornadas y resumen semanal. Por defecto, el reporte se genera para toda la plantilla de la empresa; puedes seleccionar usuarios específicos.</p>
            </div>
            <div className="dashboard-report-actions" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button type="button" onClick={downloadExcelRecords} disabled={recordsExcelLoading} style={{ backgroundColor: recordsExcelLoading ? '#718096' : '#3182ce', color: '#fff', border: 'none', padding: '9px 15px', borderRadius: '6px', cursor: recordsExcelLoading ? 'wait' : 'pointer', fontWeight: 'bold' }}>
                {recordsExcelLoading ? '⏳ Generando...' : '📋 Registros sin cómputo'}
              </button>
              <button type="button" onClick={downloadExcelReport} disabled={excelLoading} style={{ backgroundColor: excelLoading ? '#718096' : '#2f855a', color: '#fff', border: 'none', padding: '9px 15px', borderRadius: '6px', cursor: excelLoading ? 'wait' : 'pointer', fontWeight: 'bold' }}>
                {excelLoading ? '⏳ Generando...' : '📊 Reporte administrativo'}
              </button>
            </div>
          </div>

          <div className="dashboard-report-filters" style={{ display: 'grid', gridTemplateColumns: 'minmax(220px, 1.4fr) repeat(3, minmax(140px, 1fr))', gap: '12px', alignItems: 'start' }}>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: textCol, marginBottom: '5px' }}>Usuarios</label>
              <div style={{ maxHeight: '180px', overflowY: 'auto', padding: '9px', border: `1px solid ${borderCol}`, borderRadius: '6px', backgroundColor: cardBg }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '12px', color: textCol, marginBottom: '7px', fontWeight: 'bold' }}>
                  <input type="checkbox" checked={excelSelectedUserIds.length === 0 || (reportUsers.length > 0 && reportUsers.every(u => excelSelectedUserIds.includes(u.id)))} onChange={toggleAllExcelUsers} />
                  Todos los empleados
                </label>
                {reportUsers.map(u => (
                  <label key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '7px', fontSize: '12px', color: textCol, marginBottom: '5px' }}>
                    <input type="checkbox" checked={excelSelectedUserIds.includes(u.id)} onChange={() => toggleExcelUser(u.id)} />
                    {u.name} <span style={{ color: '#718096' }}>#{u.id}</span>
                  </label>
                ))}
              </div>
              <div style={{ marginTop: '4px', fontSize: '11px', color: '#718096' }}>
                {excelSelectedUserIds.length === 0 ? 'Toda la plantilla seleccionada por defecto' : `${excelSelectedUserIds.length} usuario(s) seleccionado(s)}`}
              </div>
            </div>

            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: textCol, marginBottom: '5px' }}>Registros</label>
              <select value={excelFilterType} onChange={(e) => setExcelFilterType(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: `1px solid ${borderCol}`, backgroundColor: cardBg, color: textCol }}>
                <option value="Todos">Entrada + Salida</option><option value="Entrada">Solo Entrada</option><option value="Salida">Solo Salida</option>
              </select>
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: textCol, marginBottom: '5px' }}>Fecha inicial</label>
              <input type="date" value={excelDesde} onChange={(e) => setExcelDesde(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', padding: '8px', borderRadius: '6px', border: `1px solid ${borderCol}`, backgroundColor: cardBg, color: textCol }} />
            </div>
            <div>
              <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: textCol, marginBottom: '5px' }}>Fecha final</label>
              <input type="date" value={excelHasta} onChange={(e) => setExcelHasta(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', padding: '8px', borderRadius: '6px', border: `1px solid ${borderCol}`, backgroundColor: cardBg, color: textCol }} />
            </div>
          </div>
        </section>
      )}

      {activeTab === 'calendar' && (
        <section className="dashboard-calendar-section" style={{ background: cardBg, padding: '25px', borderRadius: '12px', border: `1px solid ${borderCol}`, boxShadow: '0 4px 6px rgba(0,0,0,0.02)', marginBottom: '30px' }}>
          {(() => {
            const hoy = new Date();
            hoy.setHours(12, 0, 0, 0);
            const indiceLunes = (hoy.getDay() + 6) % 7;
            const inicioSemanaActual = new Date(hoy);
            inicioSemanaActual.setDate(hoy.getDate() - indiceLunes);

            const fechaPlana = fecha => {
              const y = fecha.getFullYear();
              const m = String(fecha.getMonth() + 1).padStart(2, '0');
              const d = String(fecha.getDate()).padStart(2, '0');
              return `${y}-${m}-${d}`;
            };
            const fechaBonita = fecha => fecha.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
            const iniciales = nombre => String(nombre || 'Usuario')
              .trim()
              .split(/\s+/)
              .filter(Boolean)
              .slice(0, 4)
              .map(x => x[0].toUpperCase())
              .join('');

            // Conservamos las mismas cuatro semanas y el mismo rango de datos;
            // únicamente cambiamos el orden visual para mostrar primero la actual.
            const semanas = [0, 1, 2, 3].map(offset => {
              const inicio = new Date(inicioSemanaActual);
              inicio.setDate(inicio.getDate() - offset * 7);
              const fin = new Date(inicio);
              fin.setDate(fin.getDate() + 6);
              return {
                key: fechaPlana(inicio),
                inicio,
                fin,
                label: offset === 0 ? 'Semana en curso' : `${offset} semana${offset === 1 ? '' : 's'} anterior${offset === 1 ? '' : 'es'}`
              };
            });

            const usuariosSeleccionados = (adminData || []).filter(user =>
              calSelectedUsers.includes(user.id)
            );
            const usuariosResumen = usuariosSeleccionados.length
              ? usuariosSeleccionados
              : (calEventos || []).reduce((lista, evento) => {
                  if (!lista.some(user => String(user.id) === String(evento.userId))) {
                    lista.push({ id: evento.userId, name: evento.employeeName, role: evento.role });
                  }
                  return lista;
                }, []);

            const minutosPorUsuarioSemana = (userId, semana) => (calEventos || [])
              .filter(evento =>
                String(evento.userId) === String(userId) &&
                String(evento.fecha || '') >= fechaPlana(semana.inicio) &&
                String(evento.fecha || '') <= fechaPlana(semana.fin) &&
                evento.tipoEvento === 'JORNADA'
              )
              .reduce((total, evento) => total + (Number(evento.minutosTrabajados) || 0), 0);

            return (
              <>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `2px solid ${borderCol}`, paddingBottom: '12px', marginBottom: '15px', flexWrap: 'wrap', gap: '10px' }}>
                  <div>
                    <h2 style={{ color: textCol, margin: 0, fontSize: '20px' }}>📅 Calendario de 4 semanas</h2>
                    <p style={{ margin: '4px 0 0', fontSize: '13px', color: isDarkMode ? '#94a3b8' : '#718096' }}>Semana actual + las 3 anteriores. Las jornadas se colocan en el día real de su Entrada.</p>
                  </div>
                  <div style={{ fontSize: '12px', color: isDarkMode ? '#cbd5e1' : '#4a5568', fontWeight: 700 }}>
                    {fechaBonita(semanas[0].inicio)} – {fechaBonita(semanas[3].fin)}
                  </div>
                </div>

                <div style={{ marginBottom: '18px', padding: '12px', borderRadius: '8px', backgroundColor: isDarkMode ? '#131924' : '#f8f9fa', border: `1px solid ${borderCol}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', borderBottom: `1px solid ${borderCol}`, paddingBottom: '7px', marginBottom: '9px', gap: '10px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '12px', fontWeight: 'bold', color: textCol }}>👥 Seleccionar empleados y administradores</span>
                    <button type="button" onClick={handleSelectAllCalUsers} style={{ padding: '4px 9px', fontSize: '11px', backgroundColor: '#718096', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
                      {adminData.length > 0 && adminData.every(emp => calSelectedUsers.includes(emp.id)) ? '🔲 Desseleccionar todos' : '☑️ Seleccionar todos'}
                    </button>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', maxHeight: '100px', overflowY: 'auto' }}>
                    {adminData.map(emp => {
                      const seleccionado = calSelectedUsers.includes(emp.id);
                      const color = obtenerColorPorId(emp.id, isDarkMode);
                      return (
                        <label key={emp.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', fontSize: '12px', padding: '5px 8px', borderRadius: '5px', border: `1px solid ${seleccionado ? color : borderCol}`, backgroundColor: seleccionado ? `${color}1A` : cardBg }}>
                          <input type="checkbox" checked={seleccionado} onChange={() => handleToggleCalUser(emp.id)} style={{ width: '13px', height: '13px' }} />
                          <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: color }} />
                          <span style={{ color: textCol }}>{emp.name} <small style={{ color: '#718096' }}>({emp.role})</small></span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {semanas.map(semana => {
                  const dias = Array.from({ length: 7 }, (_, i) => {
                    const fecha = new Date(semana.inicio);
                    fecha.setDate(fecha.getDate() + i);
                    return fecha;
                  });

                  return (
                    <div key={semana.key} style={{ marginBottom: '18px', border: `1px solid ${borderCol}`, borderRadius: '9px', padding: '11px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '9px' }}>
                        <div>
                          <strong style={{ color: textCol }}>{semana.label}</strong>
                          <span style={{ marginLeft: '8px', color: '#718096', fontSize: '12px' }}>{fechaBonita(semana.inicio)} – {fechaBonita(semana.fin)}</span>
                        </div>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,minmax(90px,1fr))', gap: '6px', overflowX: 'auto' }}>
                        {dias.map(dia => {
                          const claveDia = fechaPlana(dia);
                          const eventosDelDia = (calEventos || []).filter(ev => String(ev.fecha || '').trim() === claveDia);
                          return (
                            <div key={claveDia} style={{ minWidth: '90px', minHeight: '105px', border: `1px solid ${borderCol}`, borderRadius: '7px', padding: '7px', backgroundColor: isDarkMode ? '#131924' : '#fff' }}>
                              <div style={{ fontSize: '11px', fontWeight: 800, color: isDarkMode ? '#cbd5e1' : '#4a5568', textTransform: 'capitalize' }}>
                                {dia.toLocaleDateString('es-MX', { weekday: 'short', day: '2-digit' })}
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', marginTop: '5px' }}>
                                {eventosDelDia.map((ev, index) => {
                                  const color = obtenerColorPorId(ev.userId, isDarkMode);
                                  const ini = iniciales(ev.employeeName);
                                  const entrada = ev.entrada || '--:--';
                                  const salida = ev.salida || 'EN CURSO';
                                  const minutosEvento = Math.max(0, Math.round(Number(ev.minutosTrabajados) || 0));
                                  const duracionEvento = ev.duracion || formatMinutesFromMinutes(minutosEvento);
                                  const esCierre = ev.tipoEvento === 'CIERRE_ADMINISTRATIVO';
                                  return (
                                    <div key={`${ev.id}_${index}`} title={esCierre ? `${ev.employeeName}: Cierre Administrativo${ev.horaCierre ? ` · ${ev.horaCierre}` : ''}` : `${ev.employeeName}: ${entrada} – ${salida}${minutosEvento ? ` · ${duracionEvento}` : ''}`} style={{ backgroundColor: esCierre ? '#9333ea' : color, color: '#fff', fontSize: '10px', fontWeight: 700, padding: '4px 5px', borderRadius: '4px', lineHeight: 1.25 }}>
                                      {esCierre ? (
                                        <>
                                          <div>🟣 {ini} · CIERRE ADMINISTRATIVO</div>
                                          <div style={{ opacity: 0.9 }}>{ev.horaCierre ? `Cierre ${ev.horaCierre}` : 'Jornada cerrada'}</div>
                                        </>
                                      ) : (
                                        <>
                                          <div>{ini} · {entrada}–{salida}</div>
                                          <div style={{ opacity: 0.9 }}>{minutosEvento ? duracionEvento : 'En curso'}</div>
                                        </>
                                      )}
                                    </div>
                                  );
                                })}
                                {!eventosDelDia.length && <span style={{ color: '#a0aec0', fontSize: '10px' }}>Sin registros</span>}
                              </div>
                            </div>
                          );
                        })}
                      </div>

                      <div style={{ marginTop: '9px', paddingTop: '8px', borderTop: `1px solid ${borderCol}` }}>
                        <div style={{ fontSize: '11px', fontWeight: 800, color: textCol, marginBottom: '6px' }}>⏱️ Horas acumuladas de la semana</div>
                        {usuariosResumen.length ? (
                          <div style={{ border: `1px solid ${borderCol}`, borderRadius: '7px', overflow: 'hidden' }}>
                            <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 110px', gap: '8px', padding: '6px 8px', background: isDarkMode ? '#172033' : '#f1f5f9', fontSize: '9px', fontWeight: 800, color: '#64748b', textTransform: 'uppercase' }}>
                              <span>Persona</span>
                              <span style={{ textAlign: 'right' }}>Horas acumuladas</span>
                            </div>
                            {usuariosResumen.map(user => {
                              const totalMinutos = minutosPorUsuarioSemana(user.id, semana);
                              const color = obtenerColorPorId(user.id, isDarkMode);
                              return <div key={user.id} style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 110px', gap: '8px', alignItems: 'center', padding: '7px 8px', borderTop: `1px solid ${borderCol}`, background: isDarkMode ? '#0f172a' : '#ffffff', fontSize: '11px' }}>
                                <span style={{ color: textCol, minWidth: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                                  <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', background: color, flexShrink: 0 }} />
                                  <strong style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{iniciales(user.name)}</strong>
                                  <span style={{ color: '#718096', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user.name}</span>
                                </span>
                                <strong style={{ color: textCol, textAlign: 'right', fontSize: '12px' }}>{formatMinutesFromMinutes(totalMinutos)}</strong>
                              </div>;
                            })}
                          </div>
                        ) : <span style={{ fontSize: '11px', color: '#a0aec0' }}>Selecciona uno o varios usuarios para mostrar sus totales.</span>}
                      </div>
                    </div>
                  );
                })}
              </>
            );
          })()}
        </section>
      )}

      {/* 📊 FASE 11 · DASHBOARD EJECUTIVO INTEGRADO */}
      {activeTab === 'executive' && can('descargar_reportes') && (
        <section style={{ background: cardBg, padding: '25px', borderRadius: '12px', border: `1px solid ${borderCol}`, marginBottom: '30px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '18px' }}>
            <div>
              <h2 style={{ margin: 0, color: textCol }}>📊 Dashboard Ejecutivo</h2>
              <p style={{ margin: '5px 0 0', color: isDarkMode ? '#94a3b8' : '#718096', fontSize: '13px' }}>
                Vista ejecutiva de la empresa en contexto. Las horas y jornadas provienen del mismo motor utilizado por calendario y reportes.
              </p>
              {executiveDashboard?.fecha && (
                <div style={{ marginTop: '6px', fontSize: '12px', color: isDarkMode ? '#cbd5e1' : '#4a5568' }}>
                  Fecha operativa: <strong>{executiveDashboard.fecha}</strong>
                  {executiveDashboard.timezone ? ` · ${executiveDashboard.timezone}` : ''}
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button type="button" onClick={fetchExecutiveDashboard} style={{ padding: '8px 12px', border: 0, borderRadius: '6px', background: '#3182ce', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>
                🔄 Actualizar
              </button>
              {can('gestionar_empresa') && (
                <button type="button" onClick={runAutomationNow} style={{ padding: '8px 12px', border: 0, borderRadius: '6px', background: '#805ad5', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>
                  ⚙️ Ejecutar automatización
                </button>
              )}
            </div>
          </div>

          {executiveLoading && <p style={{ color: textCol }}>Cargando indicadores...</p>}

          {executiveDashboardError && !executiveLoading && (
            <div style={{ padding: '14px', borderRadius: '8px', border: '1px solid #e53e3e', background: isDarkMode ? '#2a1719' : '#fff5f5', color: isDarkMode ? '#fecaca' : '#742a2a', marginBottom: '12px' }}>
              ⚠️ {executiveDashboardError}
            </div>
          )}

          {executiveDashboard && !executiveLoading && (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(170px,1fr))', gap: '12px' }}>
                {[
                  ['Empleados activos', executiveDashboard.empleadosActivos, '👥', 'employees'],
                  ['Entradas hoy', executiveDashboard.entradasHoy, '🟢', 'monitor'],
                  ['Salidas hoy', executiveDashboard.salidasHoy, '🔴', 'monitor'],
                  ['Trabajando ahora', executiveDashboard.trabajandoAhora, '🟡', 'monitor'],
                  ['Asistencia hoy', `${executiveDashboard.asistenciaHoyPorcentaje ?? 0}%`, '📈', 'calendar'],
                  ['Horas trabajadas hoy', executiveDashboard.horasTrabajadasHoy, '⏱️', 'advancedSummary'],
                  ['Horas trabajadas mes', executiveDashboard.horasTrabajadasMes, '📅', 'reports'],
                  ['Incidencias pendientes', executiveDashboard.incidenciasPendientes, '🚨', 'incidents'],
                  ['Alertas prolongadas', executiveDashboard.alertasJornadaPendientes, '⚠️', 'notifications'],
                  ['Permisos pendientes', executiveDashboard.permisosPendientes, '🏖️', 'leave'],
                  ['Incapacidades activas', executiveDashboard.incapacidadesActivas, '🩺', 'leave'],
                  ['No clasificadas semana', executiveDashboard.horasNoClasificadasSemana, '📌', 'advancedSummary']
                ].map(([label, value, icon, target]) => (
                  <button key={label} type="button" onClick={() => { setActiveTab(target); onPersonalView?.(null); }} style={{ padding: '16px', border: `1px solid ${borderCol}`, borderRadius: '10px', background: isDarkMode ? '#111827' : '#f8fafc', textAlign: 'left', cursor: 'pointer', color: textCol, minHeight: '92px' }} title={`Ver detalle: ${label}`}>
                    <div style={{ fontSize: '12px', color: isDarkMode ? '#94a3b8' : '#718096' }}>{icon} {label}</div>
                    <div style={{ fontSize: '25px', fontWeight: '800', color: textCol, marginTop: '5px' }}>{value ?? 0}</div>
                    <div style={{ fontSize: '10px', marginTop: '4px', color: isDarkMode ? '#60a5fa' : '#3182ce' }}>Ver detalle →</div>
                  </button>
                ))}
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(280px,1fr))', gap: '16px', marginTop: '22px' }}>
                <div style={{ padding: '16px', border: `1px solid ${borderCol}`, borderRadius: '10px' }}>
                  <h3 style={{ color: textCol, margin: '0 0 12px' }}>⏱️ Horas extra</h3>
                  <div style={{ display: 'grid', gap: '8px', color: textCol }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                      <span>Detectadas hoy</span>
                      <strong>{executiveDashboard.horasExtraDetectadasHoy || '0 h 00 min'}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                      <span>Autorizadas hoy</span>
                      <strong>{executiveDashboard.horasExtraAutorizadasHoy || '0 h 00 min'}</strong>
                    </div>
                  </div>
                  <p style={{ margin: '10px 0 0', fontSize: '12px', color: isDarkMode ? '#94a3b8' : '#718096' }}>
                    Detectado y autorizado se muestran por separado; la detección no implica autorización.
                  </p>
                </div>

                <div style={{ padding: '16px', border: `1px solid ${borderCol}`, borderRadius: '10px' }}>
                  <h3 style={{ color: textCol, margin: '0 0 12px' }}>📊 Cobertura del día</h3>
                  <div style={{ display: 'grid', gap: '8px', color: textCol }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                      <span>Empleados con entrada</span>
                      <strong>{executiveDashboard.empleadosConEntradaHoy ?? 0} / {executiveDashboard.empleadosActivos ?? 0}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
                      <span>Notificaciones pendientes</span>
                      <strong>{executiveDashboard.notificacionesPendientes ?? 0}</strong>
                    </div>
                  </div>
                </div>
              </div>

              <h3 style={{ color: textCol, marginTop: '24px', marginBottom: '10px' }}>🏆 Horas acumuladas por empleado</h3>
              <div style={{ display: 'grid', gap: '8px' }}>
                {(executiveDashboard.topEmpleados || []).length === 0 && (
                  <div style={{ padding: '14px', border: `1px solid ${borderCol}`, borderRadius: '8px', color: isDarkMode ? '#94a3b8' : '#718096' }}>
                    No hay jornadas completas calculadas para el período mostrado.
                  </div>
                )}
                {(executiveDashboard.topEmpleados || []).map((e, idx) => (
                  <div key={e.userId} style={{ display: 'grid', gridTemplateColumns: '35px 1fr auto', gap: '10px', alignItems: 'center', padding: '10px 12px', border: `1px solid ${borderCol}`, borderRadius: '8px' }}>
                    <strong style={{ color: textCol }}>{idx + 1}</strong>
                    <div>
                      <strong style={{ color: textCol }}>{e.nombre}</strong>
                      <div style={{ fontSize: '11px', color: isDarkMode ? '#94a3b8' : '#718096' }}>
                        {e.jornadas} jornada{e.jornadas === 1 ? '' : 's'}
                      </div>
                    </div>
                    <strong style={{ color: textCol }}>{e.duracion}</strong>
                  </div>
                ))}
              </div>

              <div style={{ marginTop: '18px', padding: '12px 14px', borderRadius: '8px', background: isDarkMode ? '#172033' : '#edf2f7', color: isDarkMode ? '#cbd5e1' : '#4a5568', fontSize: '12px' }}>
                ℹ️ Este tablero es informativo: no sustituye el calendario ni los reportes. Ambos continúan utilizando sus propios flujos y el mismo motor de cálculo laboral.
              </div>
            </>
          )}
        </section>
      )}
      {/* 🔔 CENTRO DE ALERTAS · SOLICITUDES */}
      {activeTab === 'notifications' && can('descargar_reportes') && (
        <section style={{ background: cardBg, padding: '25px', borderRadius: '12px', border: `1px solid ${borderCol}`, marginBottom: '30px' }}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:'12px',flexWrap:'wrap',marginBottom:'18px'}}>
            <div><h2 style={{margin:0,color:textCol}}>🔔 Centro de Alertas y Solicitudes</h2><p style={{margin:'5px 0 0',color:isDarkMode?'#94a3b8':'#718096',fontSize:'13px'}}>Aquí aparecen solicitudes que requieren atención: aclaraciones, permisos, vacaciones y otras gestiones pendientes.</p></div>
            <button onClick={fetchNotifications} style={{padding:'8px 12px',border:0,borderRadius:'6px',background:'#3182ce',color:'#fff',fontWeight:'bold',cursor:'pointer'}}>🔄 Actualizar</button>
          </div>
          <div style={{display:'flex',gap:'15px',flexWrap:'wrap',marginBottom:'15px',color:textCol,fontSize:'13px'}}><strong>Nuevas: {notificationSummary.unread}</strong><strong>Pendientes: {notificationSummary.pending}</strong><strong>Total: {notificationSummary.total}</strong></div>
          {notificationLoading && <p style={{color:textCol}}>Cargando alertas...</p>}
          {!notificationLoading && !notificationRows.length && <p style={{color:isDarkMode?'#94a3b8':'#718096'}}>No hay alertas para mostrar.</p>}
          <div style={{display:'grid',gap:'9px'}}>{notificationRows.map(n=>{
            const tipoNotificacion = String(n.type || '').toUpperCase();
            const esCierreJornada = tipoNotificacion === 'CIERRE_JORNADA_CONFIRMACION';
            const esSolicitudAusencia = ['SOLICITUD_PERMISO', 'SOLICITUD_VACACIONES', 'SOLICITUD_INCAPACIDAD'].includes(tipoNotificacion);
            const esSolicitudExportacion = tipoNotificacion === 'SOLICITUD_EXPORTACION_PERSONAL';
            const esParaEsteUsuario = Number(n.targetUserId) === Number(user.id);
            const closureId = Number(n.referenceId || n.metadata?.id);
            return (
              <div key={n.id} style={{padding:'13px',border:`1px solid ${borderCol}`,borderRadius:'9px',borderLeft:`5px solid ${n.severity==='CRITICA'?'#c53030':n.severity==='ALTA'?'#dd6b20':'#3182ce'}`}}>
                <div style={{display:'flex',justifyContent:'space-between',gap:'12px',flexWrap:'wrap'}}>
                  <strong style={{color:textCol}}>{n.title}</strong>
                  <span style={{fontSize:'11px',color:isDarkMode?'#94a3b8':'#718096'}}>{n.status} · {n.severity}</span>
                </div>
                <p style={{margin:'6px 0',fontSize:'13px',color:isDarkMode?'#cbd5e1':'#4a5568'}}>{n.message}</p>
                {String(n.type || '').toUpperCase().startsWith('SOLICITUD_') && n.sourceUser?.name && (
                  <div style={{marginTop:'7px',fontSize:'12px',fontWeight:800,color:isDarkMode?'#93c5fd':'#2b6cb0'}}>👤 Solicitante: {n.sourceUser.name}</div>
                )}

                {esCierreJornada && esParaEsteUsuario && closureId > 0 && (
                  <div style={{marginTop:'10px',padding:'10px',borderRadius:'7px',border:`1px solid ${isDarkMode?'#7c2d12':'#fed7aa'}`,background:isDarkMode?'#3b2415':'#fffaf0'}}>
                    <div style={{fontSize:'12px',fontWeight:800,color:isDarkMode?'#fed7aa':'#9c4221',marginBottom:'8px'}}>
                      Esta decisión es obligatoria para liberar la siguiente Entrada.
                    </div>
                    <div style={{display:'flex',gap:'8px',flexWrap:'wrap'}}>
                      <button type="button" onClick={()=>handleConfirmNotificationJornadaClosure(n,'ACUERDO')} disabled={jornadaConfirmationBusy} style={{padding:'8px 12px',border:0,borderRadius:'6px',background:'#38a169',color:'#fff',fontWeight:800,cursor:jornadaConfirmationBusy?'not-allowed':'pointer',opacity:jornadaConfirmationBusy?0.65:1}}>✓ Acuerdo</button>
                      <button type="button" onClick={()=>handleConfirmNotificationJornadaClosure(n,'DESACUERDO')} disabled={jornadaConfirmationBusy} style={{padding:'8px 12px',border:0,borderRadius:'6px',background:'#c53030',color:'#fff',fontWeight:800,cursor:jornadaConfirmationBusy?'not-allowed':'pointer',opacity:jornadaConfirmationBusy?0.65:1}}>✗ Desacuerdo</button>
                    </div>
                  </div>
                )}

                {esSolicitudExportacion && isAdministrativeRole && can('descargar_reportes') && Number(n.sourceUser?.id || n.sourceUserId) !== Number(user.id) && (
                  <div style={{marginTop:'10px',display:'flex',gap:'8px',flexWrap:'wrap'}}>
                    <button type="button" onClick={()=>reviewPersonalExportRequestFromNotification(n,'AUTORIZADA')} style={{padding:'7px 11px',border:0,borderRadius:'6px',background:'#38a169',color:'#fff',fontWeight:800,cursor:'pointer'}}>✅ Autorizar descarga</button>
                    <button type="button" onClick={()=>reviewPersonalExportRequestFromNotification(n,'RECHAZADA')} style={{padding:'7px 11px',border:0,borderRadius:'6px',background:'#c53030',color:'#fff',fontWeight:800,cursor:'pointer'}}>❌ Rechazar</button>
                  </div>
                )}

                {esSolicitudAusencia && isAdministrativeRole && can('gestionar_permisos') && (
                  <div style={{marginTop:'10px',display:'flex',gap:'8px',flexWrap:'wrap'}}>
                    <button type="button" disabled={leaveReviewBusyId===Number(n.referenceId||n.metadata?.requestId)} onClick={()=>reviewLeaveRequestFromNotification(n,'APROBADA')} style={{padding:'7px 11px',border:0,borderRadius:'6px',background:'#38a169',color:'#fff',fontWeight:800,cursor:leaveReviewBusyId===Number(n.referenceId||n.metadata?.requestId)?'not-allowed':'pointer',opacity:leaveReviewBusyId===Number(n.referenceId||n.metadata?.requestId)?0.65:1}}>✅ {leaveReviewBusyId===Number(n.referenceId||n.metadata?.requestId)?'Procesando…':'Aprobar'}</button>
                    <button type="button" disabled={leaveReviewBusyId===Number(n.referenceId||n.metadata?.requestId)} onClick={()=>reviewLeaveRequestFromNotification(n,'RECHAZADA')} style={{padding:'7px 11px',border:0,borderRadius:'6px',background:'#c53030',color:'#fff',fontWeight:800,cursor:leaveReviewBusyId===Number(n.referenceId||n.metadata?.requestId)?'not-allowed':'pointer',opacity:leaveReviewBusyId===Number(n.referenceId||n.metadata?.requestId)?0.65:1}}>❌ Rechazar</button>
                  </div>
                )}

                {!esSolicitudAusencia && !esSolicitudExportacion && (
                  <div style={{display:'flex',gap:'7px',flexWrap:'wrap',marginTop:'10px'}}>
                    {n.status==='NUEVA'&&<button type="button" onClick={()=>updateNotificationStatus(n.id,'LEIDA')} style={{padding:'5px 8px',border:0,borderRadius:'5px',cursor:'pointer'}}>Marcar leída</button>}
                    {['NUEVA','LEIDA'].includes(n.status)&&<button type="button" onClick={()=>updateNotificationStatus(n.id,'ATENDIDA')} style={{padding:'5px 8px',border:0,borderRadius:'5px',background:'#38a169',color:'#fff',cursor:'pointer'}}>Atendida</button>}
                    {['NUEVA','LEIDA','ATENDIDA'].includes(n.status)&&<button type="button" onClick={()=>updateNotificationStatus(n.id,'CERRADA')} style={{padding:'5px 8px',border:0,borderRadius:'5px',background:'#718096',color:'#fff',cursor:'pointer'}}>Cerrar</button>}
                  </div>
                )}
              </div>
            );
          })}</div>
        </section>
      )}

      {/* 🚡️ FASE 8 · CENTRO DE SEGURIDAD */}
      {activeTab === 'companies' && isGlobalSuperAdmin && (
        <CompanyAdministration token={token} isDarkMode={isDarkMode} />
      )}

      {activeTab === 'security' && isGlobalSuperAdmin && (
        <section style={{
          background: cardBg,
          padding: '25px',
          borderRadius: '12px',
          border: `1px solid ${borderCol}`,
          marginBottom: '30px'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '12px',
            flexWrap: 'wrap',
            marginBottom: '18px'
          }}>
            <div>
              <h2 style={{ margin: 0, color: textCol }}>
                🚡️ Centro de Seguridad
              </h2>
              <p style={{
                margin: '5px 0 0',
                color: isDarkMode ? '#94a3b8' : '#718096',
                fontSize: '13px'
              }}>
                Eventos de autenticación, trazabilidad técnica y estado de protección del servidor.
              </p>
            </div>

            <div style={{
              display: 'flex',
              gap: '8px',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={fetchSecurityCenter}
                style={{
                  padding: '8px 12px',
                  border: 0,
                  borderRadius: '6px',
                  background: '#805ad5',
                  color: '#fff',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                🔄 Actualizar
              </button>

              <button
                onClick={createSQLiteBackup}
                style={{
                  padding: '8px 12px',
                  border: 0,
                  borderRadius: '6px',
                  background: '#3182ce',
                  color: '#fff',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                💾 Backup SQLite
              </button>
            </div>
          </div>

          {securitySummary && (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))',
              gap: '10px',
              marginBottom: '18px'
            }}>
              {[
                ['Eventos 24h', securitySummary.total24h],
                ['Advertencias', securitySummary.warnings24h],
                ['Críticos', securitySummary.critical24h],
                ['Empresas', securitySummary.companies],
                ['Usuarios', securitySummary.users]
              ].map(([label, value]) => (
                <div
                  key={label}
                  style={{
                    padding: '13px',
                    border: `1px solid ${borderCol}`,
                    borderRadius: '8px'
                  }}
                >
                  <div style={{
                    fontSize: '11px',
                    color: '#718096'
                  }}>
                    {label}
                  </div>
                  <strong style={{
                    fontSize: '20px',
                    color: textCol
                  }}>
                    {value ?? 0}
                  </strong>
                </div>
              ))}
            </div>
          )}

          {securityLoading ? (
            <div style={{
              padding: '25px',
              textAlign: 'center'
            }}>
              Cargando eventos de seguridad…
            </div>
          ) : securityEvents.length === 0 ? (
            <div style={{
              padding: '25px',
              textAlign: 'center',
              color: '#718096'
            }}>
              No hay eventos de seguridad registrados.
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gap: '8px'
            }}>
              {securityEvents.map(event => (
                <div
                  key={event.id}
                  style={{
                    padding: '12px',
                    border: `1px solid ${borderCol}`,
                    borderRadius: '8px'
                  }}
                >
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: '8px',
                    flexWrap: 'wrap'
                  }}>
                    <strong style={{ color: textCol }}>
                      {event.eventType}
                    </strong>
                    <span style={{ fontSize: '11px' }}>
                      {event.severity} · {event.createdAt
                        ? new Date(event.createdAt).toLocaleString()
                        : ''}
                    </span>
                  </div>

                  <div style={{
                    fontSize: '12px',
                    marginTop: '5px',
                    color: textCol
                  }}>
                    👤 {event.user?.name || 'Sistema'} ·
                    {' '}🌐 {event.ipAddress || 'N/D'} ·
                    {' '}ID {event.requestId || 'N/D'}
                  </div>

                  {event.message && (
                    <div style={{
                      fontSize: '12px',
                      marginTop: '5px',
                      color: isDarkMode
                        ? '#cbd5e1'
                        : '#4a5568'
                    }}>
                      {event.message}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {/* ⚙️ CENTRO DE AUTOMATIZACIÓN */}
      {activeTab === 'executive' && can('gestionar_empresa') && automationSettings && (
        <section style={{ background: cardBg, padding: '22px', borderRadius: '12px', border: `1px solid ${borderCol}`, marginBottom: '30px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' }}>
            <div>
              <h3 style={{ margin: 0, color: textCol }}>⚙️ Centro de automatización</h3>
              <p style={{ margin: '5px 0 0', color: isDarkMode ? '#94a3b8' : '#718096', fontSize: '12px' }}>Procesos que generan o actualizan alertas y notificaciones. Consultar el Dashboard no ejecuta cambios.</p>
            </div>
            <button onClick={saveAutomationSettings} disabled={automationSaving} style={{ padding: '8px 12px', border: 0, borderRadius: '6px', background: '#805ad5', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>{automationSaving ? 'Guardando...' : '💾 Guardar configuración'}</button>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(230px,1fr))', gap: '9px', color: textCol, fontSize: '13px' }}>
            {[['enabled','Automatización general'],['longShiftEnabled','Jornadas prolongadas'],['missingAttendanceEnabled','Faltas de entrada/salida'],['weeklyLimitEnabled','Exceso semanal'],['noClassifiedEnabled','Horas no clasificadas'],['pendingAdjustmentsEnabled','Aclaraciones pendientes'],['pendingClarificationsEnabled','Alertas de jornada pendientes']].map(([key,label]) => (
              <label key={key} style={{ display:'flex', gap:'8px', alignItems:'center', padding:'10px', border:`1px solid ${borderCol}`, borderRadius:'7px' }}>
                <input type="checkbox" checked={Boolean(automationSettings[key])} onChange={e => setAutomationSettings(prev => ({ ...prev, [key]: e.target.checked }))} />
                {label}
              </label>
            ))}
          </div>

          <div style={{ display:'flex', gap:'12px', marginTop:'12px', flexWrap:'wrap' }}>
            <label style={{ color:textCol, fontSize:'12px' }}>Intervalo (seg): <input type="number" min="30" max="3600" value={automationSettings.pollIntervalSeconds || 60} onChange={e => setAutomationSettings(prev => ({ ...prev, pollIntervalSeconds:Number(e.target.value) }))} style={{ width:'90px', padding:'6px' }} /></label>
            <label style={{ color:textCol, fontSize:'12px' }}>Umbral de jornada prolongada (h): <input type="number" min="1" max="48" step="0.5" value={automationSettings.longShiftThresholdHours || 10} onChange={e => setAutomationSettings(prev => ({ ...prev, longShiftThresholdHours:Number(e.target.value) }))} style={{ width:'80px', padding:'6px' }} /></label>
          </div>

          {automationRunResult && (
            <div style={{ marginTop:'15px', padding:'12px 14px', borderRadius:'8px', background:isDarkMode ? '#13251b' : '#f0fff4', border:`1px solid ${isDarkMode ? '#285a3c' : '#c6f6d5'}`, color:textCol, fontSize:'12px' }}>
              <strong>Última ejecución manual:</strong> {automationRunResult.created ?? 0} notificaciones activas/creadas · {automationRunResult.pending ?? 0} pendientes.
            </div>
          )}
        </section>
      )}

      {/* 🚨 FASE 6 · CENTRO DE INCIDENCIAS */}
      {activeTab === 'incidents' && can('descargar_reportes') && (
        <section style={{ background: cardBg, padding: '25px', borderRadius: '12px', border: `1px solid ${borderCol}`, marginBottom: '30px' }}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:'12px',flexWrap:'wrap',marginBottom:'18px'}}>
            <div><h2 style={{margin:0,color:textCol}}>🚨 Centro de Incidencias</h2><p style={{margin:'5px 0 0',color:isDarkMode?'#94a3b8':'#718096',fontSize:'13px'}}>Incidencias derivadas del mismo motor que alimenta jornadas y reportes.</p></div>
            <button type="button" onClick={fetchIncidents} style={{padding:'8px 12px',border:0,borderRadius:'6px',background:'#e53e3e',color:'#fff',fontWeight:'bold',cursor:'pointer'}}>🔄 Sincronizar</button>
          </div>
          <div style={{display:'flex',gap:'10px',flexWrap:'wrap',marginBottom:'15px'}}>
            <select value={incidentStatusFilter} onChange={e=>setIncidentStatusFilter(e.target.value)} style={{padding:'8px',borderRadius:'6px',border:`1px solid ${borderCol}`,background:inputBg,color:textCol}}><option>Todos</option><option>PENDIENTE</option><option>EN_REVISION</option><option>ATENDIDA</option><option>CERRADA</option><option>CERRADA_AUTOMATICA</option></select>
            <select value={incidentTypeFilter} onChange={e=>setIncidentTypeFilter(e.target.value)} style={{padding:'8px',borderRadius:'6px',border:`1px solid ${borderCol}`,background:inputBg,color:textCol}}><option>Todos</option><option>FALTA_ENTRADA</option><option>FALTA_SALIDA</option><option>RETARDO</option><option>SALIDA_ANTICIPADA</option><option>JORNADA_PROLONGADA</option><option>JORNADA_PROLONGADA_ALERTA</option><option>EXCESO_SEMANAL</option><option>HORAS_NO_CLASIFICADAS</option><option>PAREJA_INVALIDA</option></select>
          </div>
          {incidentLoading ? <div style={{padding:'25px',textAlign:'center'}}>Cargando incidencias…</div> : incidentRows.length===0 ? <div style={{padding:'25px',textAlign:'center',color:'#718096'}}>No hay incidencias con estos filtros.</div> : <div style={{display:'grid',gap:'10px'}}>{incidentRows.map(i=><div key={i.id} style={{padding:'15px',borderRadius:'9px',border:`1px solid ${borderCol}`,background:isDarkMode?'#111827':'#fff'}}>
            <div style={{display:'flex',justifyContent:'space-between',gap:'10px',flexWrap:'wrap'}}><strong style={{color:textCol}}>{i.title}</strong><span style={{fontSize:'11px',fontWeight:'bold'}}>{i.status} · {i.severity}</span></div>
            <div style={{fontSize:'13px',color:textCol,marginTop:'6px'}}>👤 {i.employee?.name || `Usuario ${i.userId}`} · {i.eventDate ? new Date(i.eventDate).toLocaleString() : 'Sin fecha'}</div>
            <div style={{fontSize:'12px',color:isDarkMode?'#cbd5e1':'#4a5568',marginTop:'6px'}}>{i.description}</div>
            <div style={{fontSize:'11px',color:'#718096',marginTop:'6px'}}>Referencia: {i.referenceKey}</div>
            {['PENDIENTE','EN_REVISION'].includes(i.status) && <div style={{display:'flex',gap:'7px',marginTop:'10px',flexWrap:'wrap'}}>{i.status==='PENDIENTE' && <button onClick={()=>handleIncidentStatus(i.id,'EN_REVISION')} style={{padding:'6px 10px',border:0,borderRadius:'5px',background:'#dd6b20',color:'#fff',cursor:'pointer'}}>👁 En revisión</button>}<button onClick={()=>handleIncidentStatus(i.id,'ATENDIDA')} style={{padding:'6px 10px',border:0,borderRadius:'5px',background:'#319795',color:'#fff',cursor:'pointer'}}>✓ Resolver / Atender</button><button onClick={()=>handleIncidentStatus(i.id,'CERRADA')} style={{padding:'6px 10px',border:0,borderRadius:'5px',background:'#4a5568',color:'#fff',cursor:'pointer'}}>Cerrar</button></div>}
            {i.status==='ATENDIDA' && <div style={{display:'flex',gap:'7px',marginTop:'10px',flexWrap:'wrap'}}><button onClick={()=>handleIncidentStatus(i.id,'CERRADA')} style={{padding:'6px 10px',border:0,borderRadius:'5px',background:'#4a5568',color:'#fff',cursor:'pointer'}}>✓ Cerrar incidencia</button></div>}
            {i.resolution && <div style={{marginTop:'8px',fontSize:'12px'}}><strong>Resolución:</strong> {i.resolution}</div>}
          </div>)}</div>}
        </section>
      )}

      {/* 🚡️ FASE 6 · CENTRO DE AUDITORÍA */}
      {activeTab === 'audit' && can('descargar_reportes') && (
        <section style={{background:cardBg,padding:'25px',borderRadius:'12px',border:`1px solid ${borderCol}`,marginBottom:'30px'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:'12px',flexWrap:'wrap',marginBottom:'18px'}}><div><h2 style={{margin:0,color:textCol}}>🚡️ Centro de Auditoría</h2><p style={{margin:'5px 0 0',color:isDarkMode?'#94a3b8':'#718096',fontSize:'13px'}}>Trazabilidad: registro → ajuste → jornada → incidencia → resolución.</p></div><button onClick={fetchAuditCenter} style={{padding:'8px 12px',border:0,borderRadius:'6px',background:'#805ad5',color:'#fff',fontWeight:'bold',cursor:'pointer'}}>🔄 Actualizar</button></div>
          <div style={{display:'flex',gap:'10px',flexWrap:'wrap',marginBottom:'15px'}}>
            <select value={auditActionFilter} onChange={e=>setAuditActionFilter(e.target.value)} style={{padding:'8px',borderRadius:'6px',border:`1px solid ${borderCol}`,background:inputBg,color:textCol}}><option>Todos</option>{(auditCatalogs.actions||[]).map(x=><option key={x}>{x}</option>)}</select>
            <select value={auditEntityFilter} onChange={e=>setAuditEntityFilter(e.target.value)} style={{padding:'8px',borderRadius:'6px',border:`1px solid ${borderCol}`,background:inputBg,color:textCol}}><option>Todos</option>{(auditCatalogs.entityTypes||[]).map(x=><option key={x}>{x}</option>)}</select>
            <select value={auditUserFilter} onChange={e=>setAuditUserFilter(e.target.value)} style={{padding:'8px',borderRadius:'6px',border:`1px solid ${borderCol}`,background:inputBg,color:textCol}}><option value="Todos">Todos los empleados</option>{(auditCatalogs.users||[]).map(u=><option key={u.id} value={u.id}>{u.name}</option>)}</select>
          </div>
          {auditLoading ? <div style={{padding:'25px',textAlign:'center'}}>Cargando auditoría…</div> : auditRows.length===0 ? <div style={{padding:'25px',textAlign:'center',color:'#718096'}}>Aún no hay movimientos de auditoría registrados.</div> : <div style={{display:'grid',gap:'9px'}}>{auditRows.map(a=><div key={a.id} style={{padding:'14px',border:`1px solid ${borderCol}`,borderRadius:'8px'}}><div style={{display:'flex',justifyContent:'space-between',gap:'10px',flexWrap:'wrap'}}><strong style={{color:textCol}}>{a.action} · {a.entityType} #{a.entityId || '—'}</strong><span style={{fontSize:'11px',color:'#718096'}}>{a.createdAt ? new Date(a.createdAt).toLocaleString() : ''}</span></div><div style={{fontSize:'12px',marginTop:'5px',color:textCol}}>👤 Afectado: {a.employee?.name || 'N/A'} · ✍️ Operador: {a.actor?.name || 'Sistema'}</div>{a.reason && <div style={{fontSize:'12px',marginTop:'5px'}}><strong>Motivo:</strong> {a.reason}</div>}<details style={{marginTop:'7px',fontSize:'11px'}}><summary>Ver datos de auditoría</summary><pre style={{whiteSpace:'pre-wrap',maxHeight:'220px',overflow:'auto'}}>{JSON.stringify({before:a.beforeData,after:a.afterData,metadata:a.metadata},null,2)}</pre></details></div>)}</div>}
        </section>
      )}

      {/* 📁 FASE 6 · EXPEDIENTE LABORAL */}
      {activeTab === 'laborFile' && isAdministrativeRole && (
        <section style={{background:cardBg,padding:'25px',borderRadius:'12px',border:`1px solid ${borderCol}`,marginBottom:'30px'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',gap:'12px',flexWrap:'wrap'}}><div><h2 style={{margin:0,color:textCol}}>📁 Expediente laboral</h2><p style={{margin:'5px 0 0',color:isDarkMode?'#94a3b8':'#718096',fontSize:'13px'}}>Consulta integral del empleado sin duplicar el motor de cálculo.</p></div><div style={{display:'flex',gap:'8px'}}><select value={laborFileUserId} onChange={e=>setLaborFileUserId(e.target.value)} style={{padding:'8px',borderRadius:'6px',border:`1px solid ${borderCol}`,background:inputBg,color:textCol}}><option value="">Selecciona empleado</option>{adminData.map(u=><option key={u.id} value={u.id}>{u.name}</option>)}</select><button onClick={()=>fetchLaborFile()} disabled={!laborFileUserId||laborFileLoading} style={{padding:'8px 12px',border:0,borderRadius:'6px',background:'#3182ce',color:'#fff',fontWeight:'bold',cursor:'pointer'}}>📂 Abrir</button></div></div>
          {laborFileLoading && <div style={{padding:'25px',textAlign:'center'}}>Cargando expediente…</div>}
          {laborFile && <div style={{marginTop:'20px'}}><div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(170px,1fr))',gap:'10px'}}>{[['Empleado',laborFile.usuario?.name],['Correo',laborFile.usuario?.email],['Rol',laborFile.usuario?.role],['No. empleado',laborFile.profile?.employeeNumber||'—'],['Departamento',laborFile.department?.name||'—'],['Puesto',laborFile.position?.name||'—'],['Estado',laborFile.profile?.status||'ACTIVO']].map(([k,v])=><div key={k} style={{padding:'12px',border:`1px solid ${borderCol}`,borderRadius:'8px'}}><div style={{fontSize:'11px',color:'#718096'}}>{k}</div><strong style={{color:textCol}}>{v||'—'}</strong></div>)}</div><h3 style={{color:textCol,marginTop:'20px'}}>Resumen semanal</h3><div style={{display:'grid',gap:'8px'}}>{(laborFile.resumenSemanal||[]).map(w=><div key={`${w.userId}-${w.semana}`} style={{padding:'10px',border:`1px solid ${borderCol}`,borderRadius:'7px',fontSize:'12px'}}><strong>{w.semana}</strong> · Trabajadas: {formatMinutesFromMinutes(w.horasTrabajadasMinutos)} · Ordinarias: {formatMinutesFromMinutes(w.horasOrdinariasMinutos)} · Dobles: {formatMinutesFromMinutes(w.horasDoblesMinutos)} · Triples: {formatMinutesFromMinutes(w.horasTriplesMinutos)} · No clasificadas: {formatMinutesFromMinutes(w.horasNoClasificadasMinutos)}</div>)}</div><h3 style={{color:textCol,marginTop:'20px'}}>Incidencias de cálculo</h3><div style={{display:'grid',gap:'7px'}}>{(laborFile.incompletas||[]).map((x,idx)=><div key={idx} style={{padding:'9px',border:`1px solid ${borderCol}`,borderRadius:'6px',fontSize:'12px'}}>{x.tipo} · {x.fecha} · {x.motivo}</div>)}</div></div>}
        </section>
      )}

      {/* 📈 FASE 6 · RESUMEN AVANZADO */}
      {activeTab === 'advancedSummary' && can('descargar_reportes') && (
        <section style={{background:cardBg,padding:'25px',borderRadius:'12px',border:`1px solid ${borderCol}`,marginBottom:'30px'}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:'18px'}}><div><h2 style={{margin:0,color:textCol}}>📈 Resumen avanzado</h2><p style={{margin:'5px 0 0',color:isDarkMode?'#94a3b8':'#718096',fontSize:'13px'}}>Indicadores consolidados directamente del motor de jornadas.</p></div><button onClick={fetchAdvancedSummary} style={{padding:'8px 12px',border:0,borderRadius:'6px',background:'#319795',color:'#fff',fontWeight:'bold',cursor:'pointer'}}>🔄 Actualizar</button></div>
          {advancedSummaryLoading ? <div style={{padding:'25px',textAlign:'center'}}>Calculando…</div> : advancedSummary ? <><div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(180px,1fr))',gap:'10px',marginBottom:'18px'}}><div style={{padding:'15px',border:`1px solid ${borderCol}`,borderRadius:'8px'}}><div style={{fontSize:'11px'}}>Empleados con jornadas</div><strong style={{fontSize:'24px',color:textCol}}>{advancedSummary.empleados?.length||0}</strong></div><div style={{padding:'15px',border:`1px solid ${borderCol}`,borderRadius:'8px'}}><div style={{fontSize:'11px'}}>Jornadas completas</div><strong style={{fontSize:'24px',color:textCol}}>{advancedSummary.jornadas?.length||0}</strong></div><div style={{padding:'15px',border:`1px solid ${borderCol}`,borderRadius:'8px'}}><div style={{fontSize:'11px'}}>Incompletas</div><strong style={{fontSize:'24px',color:textCol}}>{advancedSummary.incompletas?.length||0}</strong></div></div><div style={{display:'grid',gap:'8px'}}>{(advancedSummary.empleados||[]).map(e=><div key={e.userId} style={{padding:'12px',border:`1px solid ${borderCol}`,borderRadius:'8px',display:'flex',justifyContent:'space-between',gap:'10px',flexWrap:'wrap'}}><strong style={{color:textCol}}>{e.nombre}</strong><span>{e.jornadas} jornadas · {e.duracion}</span></div>)}</div></> : <div style={{padding:'25px',textAlign:'center',color:'#718096'}}>No hay datos para mostrar.</div>}
        </section>
      )}

      {/* 📝 PESTAÑA: ACLARACIONES Y AUDITORÍA */}
      {activeTab === 'adjustments' && (
        <section style={{ background: cardBg, padding: '25px', borderRadius: '12px', border: `1px solid ${borderCol}`, boxShadow: '0 4px 12px rgba(0,0,0,0.05)', marginBottom: '30px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '18px' }}>
            <div>
              <h2 style={{ margin: 0, color: textCol, fontSize: '20px' }}>📝 Aclaraciones y Auditoría de Asistencia</h2>
              <p style={{ margin: '5px 0 0', color: isDarkMode ? '#94a3b8' : '#718096', fontSize: '13px' }}>Las marcas originales son inmutables. Cada aclaración se registra como un movimiento independiente y requiere aceptación.</p>
            </div>
            <button type="button" onClick={fetchAdminAdjustments} style={{ padding: '8px 12px', border: 'none', borderRadius: '6px', background: '#3182ce', color: '#fff', cursor: 'pointer', fontWeight: 'bold' }}>🔄 Actualizar</button>
          </div>

          {/* 🚨 HISTORIAL DE ALERTAS DE JORNADA PROLONGADA */}
          {can('gestionar_aclaraciones') && (
            <div style={{ marginBottom: '20px', padding: '15px', borderRadius: '10px', border: `1px solid ${borderCol}`, backgroundColor: isDarkMode ? '#131924' : '#f8fafc' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '12px' }}>
                <div>
                  <h3 style={{ margin: 0, color: textCol, fontSize: '16px' }}>🚨 Historial de jornadas prolongadas atendidas</h3>
                  <p style={{ margin: '4px 0 0', color: isDarkMode ? '#94a3b8' : '#718096', fontSize: '11px' }}>
                    Las alertas atendidas dejan de estar pendientes, pero permanecen aquí como evidencia y auditoría.
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <select
                    value={longShiftClarificationUserFilter}
                    onChange={e => setLongShiftClarificationUserFilter(e.target.value)}
                    style={{ padding: '7px 9px', borderRadius: '6px', border: `1px solid ${borderCol}`, background: inputBg, color: textCol, fontSize: '12px' }}
                  >
                    <option value="Todos">Todos los empleados</option>
                    {adminData.map(u => (
                      <option key={u.id} value={u.id}>{u.name} · #{u.id}</option>
                    ))}
                  </select>
                  <button type="button" onClick={fetchLongShiftClarificationHistory} disabled={longShiftClarificationHistoryLoading} style={{ padding: '7px 10px', border: 'none', borderRadius: '6px', background: '#805ad5', color: '#fff', cursor: longShiftClarificationHistoryLoading ? 'wait' : 'pointer', fontWeight: 'bold', fontSize: '11px' }}>
                    {longShiftClarificationHistoryLoading ? '⏳ Cargando...' : '🔄 Actualizar historial'}
                  </button>
                </div>
              </div>

              {longShiftClarificationHistoryLoading && longShiftClarificationHistory.length === 0 ? (
                <div style={{ padding: '15px', textAlign: 'center', color: '#718096', fontSize: '12px' }}>⏳ Cargando historial...</div>
              ) : longShiftClarificationHistory.length === 0 ? (
                <div style={{ padding: '15px', textAlign: 'center', color: '#718096', fontSize: '12px' }}>No hay aclaraciones de jornadas prolongadas registradas.</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  {longShiftClarificationHistory.map(item => {
                    const empleado = item.usuario?.name || item.user?.name || item.employeeName || `Usuario #${item.userId}`;
                    const administrador = item.aclaradoPor?.name || item.clarifiedBy?.name || item.clarifiedByUser?.name || `Usuario #${item.clarifiedByUserId}`;
                    const fecha = item.clarifiedAt || item.createdAt;
                    return (
                      <div key={item.id} style={{ padding: '11px', borderRadius: '8px', border: `1px solid ${borderCol}`, background: cardBg }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                          <strong style={{ color: textCol, fontSize: '12px' }}>
                            #{item.id} · {empleado}
                          </strong>
                          <span style={{ padding: '3px 7px', borderRadius: '5px', background: '#e6fffa', color: '#2f855a', fontWeight: 'bold', fontSize: '10px' }}>
                            {item.status || 'ATENDIDA'}
                          </span>
                        </div>
                        <div style={{ marginTop: '5px', fontSize: '11px', color: textCol }}>
                          Jornada relacionada con Entrada #{item.entryAttendanceId} · Umbral: <strong>{formatMinutesFromMinutes(Number(item.thresholdHours || 0) * 60)}</strong>
                        </div>
                        <div style={{ marginTop: '5px', fontSize: '11px', color: textCol }}>
                          Motivo: {item.reason || '—'}
                        </div>
                        <div style={{ marginTop: '5px', fontSize: '11px', color: textCol }}>
                          Observación: {item.observation || '—'}
                        </div>
                        <div style={{ marginTop: '6px', fontSize: '10px', color: '#718096' }}>
                          Atendida por: {administrador} · {fecha ? formatearTimestampMerida(fecha) : 'Fecha no disponible'}
                          {item.companyId ? ` · ${operationalCompany?.name || companySettings?.companyName || 'Empresa'}` : ''}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          <form onSubmit={handleCreateAdminAdjustment} style={{ padding: '15px', borderRadius: '10px', border: `1px solid ${borderCol}`, backgroundColor: isDarkMode ? '#131924' : '#f8fafc', marginBottom: '20px' }}>
            <h3 style={{ marginTop: 0, color: textCol, fontSize: '15px' }}>➕ Emitir ajuste de horas</h3>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '10px', alignItems: 'end' }}>
              <div><label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: textCol, marginBottom: '4px' }}>Empleado</label><select value={adminAdjustmentUserId} onChange={e => { setAdminAdjustmentUserId(e.target.value); setAdminAdjustmentOriginalId(''); }} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: `1px solid ${borderCol}`, background: inputBg, color: textCol }}><option value="">Seleccionar...</option>{adminData.filter(u => u.role === 'Empleado').map(u => <option key={u.id} value={u.id}>{u.name} · #{u.id}</option>)}</select></div>
              <div><label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: textCol, marginBottom: '4px' }}>Registro relacionado</label><select value={adminAdjustmentOriginalId} onChange={e => { const id = e.target.value; setAdminAdjustmentOriginalId(id); const rec = adminData.find(u => String(u.id) === String(adminAdjustmentUserId))?.checkins?.find(c => String(c.id) === String(id)); if (rec) setAdminAdjustmentType(rec.type === 'Entrada' ? 'Salida' : 'Entrada'); }} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: `1px solid ${borderCol}`, background: inputBg, color: textCol }}><option value="">Sin registro relacionado</option>{(adminData.find(u => String(u.id) === String(adminAdjustmentUserId))?.checkins || []).slice().sort((a, b) => obtenerClaveCronologicaTimestamp(b.timestamp) - obtenerClaveCronologicaTimestamp(a.timestamp) || Number(b.id || 0) - Number(a.id || 0)).slice(0,30).map(c => <option key={c.id} value={c.id}>#{c.id} · {c.type} · {formatearTimestampMerida(c.timestamp)}</option>)}</select></div>
              <div><label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: textCol, marginBottom: '4px' }}>Tipo propuesto</label><select value={adminAdjustmentType} onChange={e => setAdminAdjustmentType(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: `1px solid ${borderCol}`, background: inputBg, color: textCol }}><option value="Entrada">📥 Entrada</option><option value="Salida">📤 Salida</option></select></div>
              <div><label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: textCol, marginBottom: '4px' }}>Fecha y hora propuesta</label><input type="datetime-local" value={adminAdjustmentDateTime} onChange={e => setAdminAdjustmentDateTime(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', padding: '8px', borderRadius: '6px', border: `1px solid ${borderCol}`, background: inputBg, color: textCol }} /></div>
            </div>
            <div style={{ marginTop: '10px' }}><label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: textCol, marginBottom: '4px' }}>Motivo / aclaración</label><textarea value={adminAdjustmentReason} onChange={e => setAdminAdjustmentReason(e.target.value)} rows={2} placeholder="Ej. El empleado olvidó registrar la salida y confirmó la hora con su supervisor." style={{ width: '100%', boxSizing: 'border-box', padding: '8px', borderRadius: '6px', border: `1px solid ${borderCol}`, background: inputBg, color: textCol, resize: 'vertical' }} /></div>
            <button type="submit" disabled={adjustmentSubmitting} style={{ marginTop: '10px', padding: '9px 14px', border: 'none', borderRadius: '6px', background: adjustmentSubmitting ? '#718096' : '#319795', color: '#fff', cursor: adjustmentSubmitting ? 'wait' : 'pointer', fontWeight: 'bold' }}>{adjustmentSubmitting ? '⏳ Enviando...' : '📨 Enviar al empleado para aceptación'}</button>
          </form>

          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '15px' }}>
            <select value={adjustmentStatusFilter} onChange={e => setAdjustmentStatusFilter(e.target.value)} style={{ padding: '8px', borderRadius: '6px', border: `1px solid ${borderCol}`, background: inputBg, color: textCol }}><option value="Todos">Todos los estados</option><option value="PENDIENTE">Pendientes</option><option value="ACEPTADA">Aceptadas</option><option value="RECHAZADA">Rechazadas</option></select>
            <select value={adjustmentUserFilter} onChange={e => setAdjustmentUserFilter(e.target.value)} style={{ padding: '8px', borderRadius: '6px', border: `1px solid ${borderCol}`, background: inputBg, color: textCol }}><option value="Todos">Todos los empleados</option>{adminData.map(u => <option key={u.id} value={u.id}>{u.name} · #{u.id}</option>)}</select>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {adjustmentsAdmin.map(a => {
              const pendienteEmpleado = a.status === 'PENDIENTE' && ['administrador','superadministrador','superadministradorempresarial'].includes(String(jwtDecode(token)?.role || '').trim().toLowerCase()) && Number(a.requestedByUserId || a.solicitante?.id || 0) !== Number(jwtDecode(token)?.userId || jwtDecode(token)?.UserId || jwtDecode(token)?.id || 0);
              return <div key={a.id} style={{ padding: '13px', borderRadius: '8px', border: `1px solid ${a.status === 'PENDIENTE' ? '#dd6b20' : borderCol}`, background: isDarkMode ? '#131924' : '#fff' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                  <div><strong style={{ color: textCol }}>A-{a.id} · {a.usuario?.name || `Usuario #${a.userId}`}</strong><div style={{ fontSize: '11px', color: '#718096', marginTop: '3px' }}>{a.originalAttendanceId ? `Relacionado con registro #${a.originalAttendanceId}` : 'Sin registro original · alta de marca'}</div></div>
                  <span style={{ padding: '4px 8px', borderRadius: '5px', fontSize: '10px', fontWeight: 'bold', background: a.status === 'PENDIENTE' ? '#fffaf0' : a.status === 'ACEPTADA' ? '#e6fffa' : '#fff5f5', color: a.status === 'PENDIENTE' ? '#c05621' : a.status === 'ACEPTADA' ? '#2f855a' : '#c53030' }}>{a.status}</span>
                </div>
                <div style={{ marginTop: '8px', fontSize: '12px', color: textCol }}><strong>{a.proposedType}</strong> · {formatearTimestampMerida(a.proposedTimestamp)}</div>
                <div style={{ marginTop: '6px', fontSize: '12px', color: '#4a5568' }}>Motivo: {a.reason}</div>
                <div style={{ marginTop: '5px', fontSize: '10px', color: '#718096' }}>Solicitado por: {a.solicitante?.name || `Usuario #${a.requestedByUserId}`}</div>
                {a.status !== 'PENDIENTE' && (
                  <div style={{ marginTop: '4px', fontSize: '10px', color: '#718096' }}>
                    {a.revisadoPor?.name
                      ? `Revisado por: ${a.revisadoPor.name}`
                      : a.reviewedByUserId
                        ? `Revisado por: Usuario #${a.reviewedByUserId}`
                        : 'Revisado por: información no disponible'}
                    {a.reviewedAt ? ` · ${formatearTimestampMerida(a.reviewedAt)}` : ''}
                  </div>
                )}
                {pendienteEmpleado && <div style={{ marginTop: '10px' }}><textarea value={adjustmentReviewComments[a.id] || ''} onChange={e => setAdjustmentReviewComments(prev => ({ ...prev, [a.id]: e.target.value }))} rows={2} placeholder="Comentario de revisión (opcional)" style={{ width: '100%', boxSizing: 'border-box', padding: '7px', borderRadius: '6px', border: `1px solid ${borderCol}`, background: inputBg, color: textCol }} /><div style={{ display: 'flex', gap: '8px', marginTop: '7px' }}><button type="button" onClick={() => handleReviewAdjustment(a.id, 'ACEPTADA')} style={{ padding: '7px 10px', border: 'none', borderRadius: '5px', background: '#38a169', color: '#fff', cursor: 'pointer', fontWeight: 'bold' }}>✅ Aceptar</button><button type="button" onClick={() => handleReviewAdjustment(a.id, 'RECHAZADA')} style={{ padding: '7px 10px', border: 'none', borderRadius: '5px', background: '#e53e3e', color: '#fff', cursor: 'pointer', fontWeight: 'bold' }}>❌ Rechazar</button></div></div>}
              </div>;
            })}
            {adjustmentsAdmin.length === 0 && <div style={{ padding: '25px', textAlign: 'center', color: '#718096' }}>No hay aclaraciones con estos filtros.</div>}
          </div>
        </section>
      )}

      {/* 🔐 PESTAÑA 5: AUTORIZACIONES DE ADMINISTRADORES */}
      {activeTab === 'authorizations' && isSuperAdmin && (
        <section style={{ background: cardBg, padding: '25px', borderRadius: '12px', border: `1px solid ${borderCol}`, boxShadow: '0 4px 6px rgba(0,0,0,0.02)' }}>
          <h2 style={{ color: textCol, marginTop: 0, marginBottom: '6px', fontSize: '20px' }}>🔐 Autorizaciones de Administradores</h2>
          <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: isDarkMode ? '#94a3b8' : '#718096' }}>
            Como SuperAdministrador puedes decidir qué operaciones administrativas puede realizar cada Administrador. Estas reglas también se validan en el servidor.
          </p>
          {permissionsLoading ? (
            <p style={{ color: textCol }}>Cargando administradores...</p>
          ) : administrators.length === 0 ? (
            <p style={{ color: '#a0aec0' }}>No hay cuentas con rol Administrador para configurar.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
              {administrators.map(admin => {
                const perms = admin.permissions || {};
                const items = [
                  ['gestionar_empleados', '👤 Gestionar empleados'],
                  ['gestionar_horarios', '🕒 Gestionar horarios y políticas'],
                  ['gestionar_sitios', '🏢 Gestionar sitios y geocercas'],
                  ['ver_monitor', '🎯 Ver monitores'],
                  ['ver_calendario', '📅 Ver calendario'],
                  ['descargar_reportes', '📊 Descargar reportes'],
                  ['gestionar_aclaraciones', '📝 Gestionar aclaraciones'],
                  ['gestionar_legislacion', '⚖️ Gestionar legislación por año'],
                  ['gestionar_empresa', '🏢 Gestionar empresa y logo'],
                  ['gestionar_biometria', '🔐 Gestionar política biométrica'],
                  ['ver_expedientes', '📁 Ver expedientes de Capital Humano'],
                  ['editar_expedientes', '✏️ Editar expedientes de Capital Humano']
                ];
                return (
                  <div key={admin.id} style={{ border: `1px solid ${borderCol}`, borderRadius: '10px', padding: '15px', backgroundColor: isDarkMode ? '#131924' : '#f8fafc' }}>
                    <div style={{ marginBottom: '10px' }}><strong style={{ color: textCol }}>{admin.name}</strong> <span style={{ color: '#718096', fontSize: '12px' }}>ID {admin.id} · Administrador</span></div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '8px' }}>
                      {items.map(([key, label]) => (
                        <label key={key} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px', border: `1px solid ${borderCol}`, borderRadius: '6px', cursor: 'pointer', color: textCol }}>
                          <input type="checkbox" checked={Boolean(perms[key])} onChange={e => handlePermissionToggle(admin.id, key, e.target.checked)} />
                          <span>{label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      )}

      {/* ⚙️ CONFIGURACIÓN EMPRESARIAL: alcance limitado a la empresa actual */}
      {activeTab === 'config' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
          {can('gestionar_empresa') && <CompanySettingsManagement token={token} isDarkMode={isDarkMode} />}

          {/* ⚖️ SECCIÓN 0: LEGISLACIÓN POR AÑO */}
          {can('gestionar_legislacion') && (
            <section style={{ background: cardBg, padding: '25px', borderRadius: '12px', border: `1px solid ${borderCol}`, boxShadow: '0 4px 6px rgba(0,0,0,0.02)' }}>
              <div style={{ marginBottom: '18px' }}>
                <h2 style={{ color: textCol, margin: 0, fontSize: '20px' }}>⚖️ Jornada y límites legales por año</h2>
                <p style={{ margin: '5px 0 0', fontSize: '13px', color: isDarkMode ? '#94a3b8' : '#718096' }}>
                  Los límites no están fijos en el código. Puedes agregar años futuros o modificar una regla cuando cambie la legislación. Los cálculos usan la regla correspondiente al año de la jornada.
                </p>
              </div>

              <form onSubmit={handleSaveLegalRule} style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '12px', alignItems: 'end', marginBottom: '18px', padding: '14px', borderRadius: '8px', backgroundColor: isDarkMode ? '#131924' : '#f8fafc', border: `1px solid ${borderCol}` }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: textCol, marginBottom: '5px' }}>Año</label>
                  <input type="number" min="2000" max="2100" value={legalRuleYear} onChange={e => seleccionarReglaLegal(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', padding: '9px', borderRadius: '6px', border: `1px solid ${borderCol}`, background: inputBg, color: textCol }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: textCol, marginBottom: '5px' }}>Máximo semanal</label>
                  <input type="number" min="1" max="168" step="0.01" value={legalRuleWeeklyHours} onChange={e => setLegalRuleWeeklyHours(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', padding: '9px', borderRadius: '6px', border: `1px solid ${borderCol}`, background: inputBg, color: textCol }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: textCol, marginBottom: '5px' }}>Máx. horas extra / semana</label>
                  <input type="number" min="0" max="168" step="0.01" value={legalRuleWeeklyOvertimeMax} onChange={e => setLegalRuleWeeklyOvertimeMax(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', padding: '9px', borderRadius: '6px', border: `1px solid ${borderCol}`, background: inputBg, color: textCol }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: textCol, marginBottom: '5px' }}>Máx. horas triples</label>
                  <input type="number" min="0" max="168" step="0.01" value={legalRuleTripleMax} onChange={e => setLegalRuleTripleMax(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', padding: '9px', borderRadius: '6px', border: `1px solid ${borderCol}`, background: inputBg, color: textCol }} />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: textCol, marginBottom: '5px' }}>Notas</label>
                  <input value={legalRuleNotes} onChange={e => setLegalRuleNotes(e.target.value)} placeholder="Referencia o nota interna" style={{ width: '100%', boxSizing: 'border-box', padding: '9px', borderRadius: '6px', border: `1px solid ${borderCol}`, background: inputBg, color: textCol }} />
                </div>
                <button type="submit" disabled={legalRuleSaving} style={{ padding: '10px', border: 'none', borderRadius: '6px', background: legalRuleSaving ? '#718096' : '#805ad5', color: '#fff', fontWeight: 'bold', cursor: legalRuleSaving ? 'wait' : 'pointer' }}>{legalRuleSaving ? '⏳ Guardando...' : '💾 Guardar / agregar año'}</button>
              </form>

              {legalRulesLoading ? <div style={{ color: '#718096', fontSize: '12px' }}>⏳ Cargando reglas legales...</div> : (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                    <thead><tr style={{ borderBottom: `2px solid ${borderCol}` }}><th style={{ padding: '8px', textAlign: 'left' }}>Año</th><th style={{ padding: '8px', textAlign: 'left' }}>Máx. semanal</th><th style={{ padding: '8px', textAlign: 'left' }}>Extras / semana</th><th style={{ padding: '8px', textAlign: 'left' }}>Triples</th><th style={{ padding: '8px', textAlign: 'left' }}>Notas</th><th style={{ padding: '8px' }}>Acciones</th></tr></thead>
                    <tbody>{legalRules.map(rule => <tr key={rule.year} style={{ borderBottom: `1px solid ${borderCol}` }}><td style={{ padding: '8px', fontWeight: 'bold', color: textCol }}>{rule.year}</td><td style={{ padding: '8px', color: textCol }}>{rule.weeklyMaxHours} h</td><td style={{ padding: '8px', color: textCol }}>{rule.weeklyOvertimeMaxHours ?? rule.overtimeDoubleWeeklyHours} h</td><td style={{ padding: '8px', color: textCol }}>{rule.tripleMaxHours ?? 4} h</td><td style={{ padding: '8px', color: '#718096' }}>{rule.notes || '—'}</td><td style={{ padding: '8px', display: 'flex', gap: '6px' }}><button type="button" onClick={() => seleccionarReglaLegal(rule.year)} style={{ padding: '5px 8px', border: 'none', borderRadius: '5px', background: '#3182ce', color: '#fff', cursor: 'pointer' }}>✏️ Editar</button>{Number(rule.year) > new Date().getFullYear() && <button type="button" onClick={() => handleDeleteLegalRule(rule.year)} style={{ padding: '5px 8px', border: 'none', borderRadius: '5px', background: '#e53e3e', color: '#fff', cursor: 'pointer' }}>🗑️</button>}</td></tr>)}</tbody>
                  </table>
                </div>
              )}
            </section>
          )}

          {/* Datos completos de empresa se administran en CompanySettingsManagement. */}
                    {can('gestionar_biometria') && (
            <section style={{ background: cardBg, padding: '25px', borderRadius: '12px', border: `1px solid ${borderCol}`, boxShadow: '0 4px 6px rgba(0,0,0,0.02)' }}>
              <div style={{ marginBottom: '15px' }}>
                <h2 style={{ color: textCol, margin: 0, fontSize: '20px' }}>🔐 Política de biometría para marcar asistencia</h2>
                <p style={{ margin: '5px 0 0', fontSize: '13px', color: isDarkMode ? '#94a3b8' : '#718096' }}>Por defecto es obligatoria. La opción se aplica inmediatamente a Entrada y Salida.</p>
              </div>
              <form onSubmit={handleSaveBiometricPolicy} style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 1fr) minmax(300px, 2fr) auto', gap: '12px', alignItems: 'end' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '12px', fontWeight: 'bold', color: textCol, marginBottom: '5px' }}>Política</label>
                  <select value={biometricPolicyDraft} onChange={e => setBiometricPolicyDraft(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', padding: '10px', borderRadius: '6px', border: `1px solid ${borderCol}`, background: inputBg, color: textCol }}>
                    <option value="OBLIGATORIA">🔒 Obligatoria</option>
                    <option value="OPCIONAL">🟡 Opcional (permite contingencia)</option>
                    <option value="DESHABILITADA">⚪ Deshabilitada</option>
                  </select>
                </div>
                <div style={{ fontSize: '12px', color: textCol, lineHeight: 1.5 }}>
                  <strong>Obligatoria:</strong> sin WebAuthn válido no se registra. <strong>Opcional:</strong> intenta biometría, pero permite continuar si falla. <strong>Deshabilitada:</strong> no solicita biometría.
                </div>
                <button type="submit" disabled={biometricPolicySaving} style={{ padding: '10px 15px', border: 'none', borderRadius: '6px', background: biometricPolicySaving ? '#718096' : '#805ad5', color: '#fff', fontWeight: 'bold', cursor: biometricPolicySaving ? 'wait' : 'pointer' }}>{biometricPolicySaving ? '⏳ Guardando...' : '💾 Guardar política'}</button>
              </form>
            </section>
          )}

          {/* El alta y edición de empleados vive en Personal → Empleados. */}

          {/* SECCIÓN B: CATÁLOGO DE SITIOS DE REGISTRO CON MAPA INTERACTIVO (LEAFLET) */}
          <section style={{ background: cardBg, padding: '25px', borderRadius: '12px', border: "1px solid " + borderCol, boxShadow: '0 4px 6px rgba(0,0,0,0.02)' }}>
            <h2 style={{ color: textCol, marginTop: 0, marginBottom: '5px', fontSize: '20px' }}>🏢 Catálogo de Sitios de Registro y Geocercas</h2>
            <p style={{ margin: '0 0 20px 0', fontSize: '13px', color: isDarkMode ? '#94a3b8' : '#718096' }}>Haz clic directamente en el mapa para capturar las coordenadas de forma automática o introduce los datos manualmente.</p>

            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              {/* Formulario de Sitios */}
              <form onSubmit={handleCreateSiteSubmit} style={{ flex: '1', minWidth: '280px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
                <div style={{ padding: '9px 12px', borderRadius: '7px', backgroundColor: editingSiteId ? (isDarkMode ? '#172554' : '#eff6ff') : (isDarkMode ? '#131924' : '#f7fafc'), border: `1px solid ${editingSiteId ? '#3182ce' : borderCol}`, color: textCol, fontSize: '13px', fontWeight: 'bold' }}>
                  {editingSiteId ? '✏️ Editando geocerca existente' : '🏢 Nueva geocerca'}
                </div>
                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: textCol }}>Nombre de la Sucursal / Proyecto:</label>
                  <input type="text" value={newSiteName} onChange={(e) => setNewSiteName(e.target.value)} required style={{ width: '100%', padding: '8px', borderRadius: '6px', border: "1px solid " + borderCol, backgroundColor: inputBg, color: textCol, boxSizing: 'border-box' }} placeholder="Ej. Sucursal Mérida Centro"/>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: textCol }}>Latitud:</label>
                    <input type="text" value={newSiteLat} onChange={(e) => setNewSiteLat(e.target.value)} required style={{ width: '100%', padding: '8px', borderRadius: '6px', border: "1px solid " + borderCol, backgroundColor: inputBg, color: textCol, boxSizing: 'border-box' }}/>
                  </div>
                  <div>
                    <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: textCol }}>Longitud:</label>
                    <input type="text" value={newSiteLon} onChange={(e) => setNewSiteLon(e.target.value)} required style={{ width: '100%', padding: '8px', borderRadius: '6px', border: "1px solid " + borderCol, backgroundColor: inputBg, color: textCol, boxSizing: 'border-box' }}/>
                  </div>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: textCol }}>Radio de Geocerca (Metros):</label>
                  <select value={newSiteRadius} onChange={(e) => setNewSiteRadius(parseInt(e.target.value))} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: "1px solid " + borderCol, backgroundColor: inputBg, color: textCol, boxSizing: 'border-box', fontWeight: 'bold' }}>
                    <option value={30}>📏 30 Metros</option>
                    <option value={50}>📏 50 Metros</option>
                    <option value={100}>📏 100 Metros (Predeterminado)</option>
                    <option value={200}>📏 200 Metros</option>
                    <option value={500}>📏 500 Metros</option>
                  </select>
                </div>

                <div>
                  <label style={{ display: 'block', marginBottom: '5px', fontSize: '13px', fontWeight: 'bold', color: textCol }}>Dirección / referencia (opcional):</label>
                  <input type="text" value={newSiteAddress} onChange={(e) => setNewSiteAddress(e.target.value)} style={{ width: '100%', padding: '8px', borderRadius: '6px', border: '1px solid ' + borderCol, backgroundColor: inputBg, color: textCol, boxSizing: 'border-box' }} placeholder="Ej. Calle 60 #123, Centro"/>
                </div>
                <div style={{ display: 'flex', gap: '18px', flexWrap: 'wrap', fontSize: '13px', color: textCol }}>
                  <label><input type="checkbox" checked={newSiteActive} onChange={e => setNewSiteActive(e.target.checked)} /> Sitio activo</label>
                  <label><input type="checkbox" checked={newSiteDefault} onChange={e => setNewSiteDefault(e.target.checked)} /> Sitio predeterminado</label>
                </div>

                <div style={{ display: 'flex', gap: '8px', marginTop: '5px' }}>
                  <button type="submit" disabled={siteSaving} style={{ flex: 1, padding: '10px', backgroundColor: siteSaving ? '#94a3b8' : (editingSiteId ? '#3182ce' : '#48bb78'), color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: siteSaving ? 'wait' : 'pointer', fontSize: '14px' }}>
                    {siteSaving ? '⏳ Guardando...' : (editingSiteId ? '💾 Guardar cambios' : '🏢 Guardar Sitio en Catálogo')}
                  </button>
                  {editingSiteId && (
                    <button type="button" onClick={handleCancelEditSite} disabled={siteSaving} style={{ padding: '10px 14px', backgroundColor: '#718096', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer', fontSize: '14px' }}>
                      Cancelar
                    </button>
                  )}
                </div>
              </form>

              {/* 🌐 CONTENEDOR DIV DE LEAFLET INTEGRADO - CON ESTILOS CSS OBLIGATORIOS FIJOS */}
              <div style={{ flex: '1.5', minWidth: '320px', height: '320px', borderRadius: '8px', border: `2px solid ${borderCol}`, overflow: 'hidden', position: 'relative', zIndex: 10 }}>
                <style>{`
                  .leaflet-container { width: 100%; height: 100%; background: #f8f9fa; }
                  .leaflet-pane { z-index: 1 !important; }
                  .leaflet-top, .leaflet-bottom { z-index: 2 !important; }
                `}</style>
                <div ref={mapContainerRef} style={{ width: '100%', height: '100%' }}></div>
              </div>
            </div>

            {/* Listado de Sitios Creados */}
            <div style={{ marginTop: '20px', maxHeight: '150px', overflowY: 'auto', borderTop: `1px solid ${borderCol}`, paddingTop: '15px' }}>
              <span style={{ fontSize: '13px', fontWeight: 'bold', color: textCol, display: 'block', marginBottom: '8px' }}>🏢 Sitios Guardados Actualmente:</span>
              <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                {sitesList.map(s => (
                  <div key={s.id} style={{ padding: '9px 12px', borderRadius: '8px', backgroundColor: isDarkMode ? '#131924' : '#f8f9fa', border: `1px solid ${editingSiteId === s.id ? '#3182ce' : borderCol}`, fontSize: '12px', display: 'flex', alignItems: 'center', gap: '10px', width: '100%', boxSizing: 'border-box' }}>
                    <span style={{ color: textCol, flex: 1 }}>📍 <strong>{s.name}</strong> <span style={{ color: '#718096' }}>({s.radius} m) · {s.active === false ? 'INACTIVO' : 'ACTIVO'}{s.isDefault ? ' · PREDETERMINADO' : ''}</span>{s.address ? <small style={{ display: 'block', color: '#718096', marginTop: 3 }}>{s.address}</small> : null}</span>
                    <button type="button" onClick={() => handleEditSiteClick(s)} style={{ padding: '5px 9px', border: 'none', borderRadius: '5px', background: '#3182ce', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>✏️ Editar</button>
                    <button type="button" onClick={() => handleDeleteSiteClick(s.id)} style={{ padding: '5px 9px', border: 'none', borderRadius: '5px', background: '#e53e3e', color: '#fff', fontWeight: 'bold', cursor: 'pointer' }}>🗑️</button>
                  </div>
                ))}
                {sitesList.length === 0 && (
                  <span style={{ fontSize: '12px', color: '#a0aec0' }}>No hay sucursales registradas en el catálogo.</span>
                )}
              </div>
            </div>
          </section>
          {/* La asignación individual de jornadas, sitios y biometría vive en Jornadas → Horarios y jornadas. */}
        </div>
      )}

                </>
              )}
            </div>
          </div>

          {!isGlobalSuperAdmin && (
            <nav
              className="dashboard-mobile-bottomnav"
              style={{ '--dashboard-mobile-bg': isDarkMode ? '#0f172a' : '#ffffff', '--dashboard-mobile-border': borderCol, color: textCol }}
            >
              <button type="button" className={activeTab === 'personalRecords' ? 'active' : ''} onClick={() => mobileGo('personalRecords')}>
                <span className="nav-icon">📋</span><span>Registros</span>
              </button>
              <button type="button" className={activeTab === 'monitor' ? 'active' : ''} onClick={() => mobileGo('monitor')}>
                <span className="nav-icon">🖥️</span><span>Monitor</span>
              </button>
              <button type="button" className={activeTab === 'leave' ? 'active' : ''} onClick={() => mobileGo('leave')}>
                <span className="nav-icon">🏖️</span><span>Permisos</span>
              </button>
              <button type="button" className={activeTab === 'account' ? 'active' : ''} onClick={() => mobileGo('account')}>
                <span className="nav-icon">👤</span><span>Perfil</span>
              </button>
            </nav>
          )}
          </>
        );
      })()}
    </div>
  );
};

// =====================================================================
// COMPONENTE PRINCIPAL MAESTRO: DASHBOARD DEL EMPLEADO Y RELOJ BIOMÉTRICO
// =====================================================================
const Dashboard = ({ token }) => {
  const [currentUserName, setCurrentUserName] = useState('');
  const [attendanceBusy, setAttendanceBusy] = useState(false);
  const [jornadaStatus, setJornadaStatus] = useState({ state: 'CARGANDO', nextAllowedType: null, closure: null, maxJornadaHours: 14 });
  const [jornadaConfirmationBusy, setJornadaConfirmationBusy] = useState(false);
  // Estado operativo único del botón: siempre deriva de la respuesta vigente del servidor.
  const tipoRegistroActual = jornadaStatus?.nextAllowedType === 'Entrada'
    ? 'Entrada'
    : jornadaStatus?.nextAllowedType === 'Salida'
      ? 'Salida'
      : null;
  const [checkins, setCheckins] = useState([]);
  const [filterType, setFilterType] = useState('Todos');
  const [isDarkMode, setIsDarkMode] = useState(() => localStorage.getItem('theme') === 'dark');
  const [personalPreferencesLoaded, setPersonalPreferencesLoaded] = useState(false);
  const [companySettings, setCompanySettings] = useState({ companyName: 'Reloj Checador 3.0', logoDataUrl: null, logoFileName: null, biometricPolicy: 'OBLIGATORIA' });
  const [monitorViewMode, setMonitorViewMode] = useState('cards');
  const [biometricStatus, setBiometricStatus] = useState({ policy: 'OBLIGATORIA', required: true, credentials: [] });
  const [biometricBusy, setBiometricBusy] = useState(false);
  const [personalView, setPersonalView] = useState('personalRecords');
  const [personalRecordsSubView, setPersonalRecordsSubView] = useState('records');
  // Solo la respuesta más reciente del estado de jornada puede modificar el botón.
  const jornadaStatusRequestRef = useRef(0);

  // 🕒 RELOJ DIGITAL EN VIVO DE ALTA PRECISIÓN Y PERIODO PERSONAL
  const [currentTime, setCurrentTime] = useState(new Date());
  const [personalDaysRange, setPersonalDaysRange] = useState(7); // 7 días por defecto

  // 📝 Aclaraciones del empleado
  const [myAdjustments, setMyAdjustments] = useState([]);
  const [myLongShiftClarificationHistory, setMyLongShiftClarificationHistory] = useState([]);
  const [myLongShiftClarificationHistoryLoading, setMyLongShiftClarificationHistoryLoading] = useState(false);
  const [employeeAdjustmentSubmitting, setEmployeeAdjustmentSubmitting] = useState(false);
  const [employeeAdjustmentOriginalId, setEmployeeAdjustmentOriginalId] = useState('');
  const [employeeAdjustmentType, setEmployeeAdjustmentType] = useState('Salida');
  const [employeeAdjustmentDateTime, setEmployeeAdjustmentDateTime] = useState('');
  const [employeeAdjustmentReason, setEmployeeAdjustmentReason] = useState('');

  useEffect(() => {
    localStorage.setItem('theme', isDarkMode ? 'dark' : 'light');
    document.body.style.backgroundColor = isDarkMode ? '#0f172a' : '#ffffff';
  }, [isDarkMode]);

  // Actualización del segundero del reloj digital en caliente
  useEffect(() => {
    const timerReloj = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timerReloj);
  }, []);

  // 🎯 ACÁ ESTÁ EL REEMPLAZO BLINDADO: Extractor de IDs del Token con tolerancia universal
  const user = useMemo(() => {
    try {
      if (token) {
        const decoded = jwtDecode(token);
        // El JWT emitido por el backend contiene userId. No usamos un
        // fallback fijo (por ejemplo, ID 1), porque eso puede hacer que
        // el navegador intente registrar asistencia para otra cuenta.
        const idDetectado = decoded.userId;
        const idNumerico = Number(idDetectado);
        if (!Number.isInteger(idNumerico) || idNumerico <= 0) {
          throw new Error('El token no contiene un userId válido.');
        }
        return { 
          id: idNumerico, 
          role: String(decoded.role || 'Empleado').trim(),
          companyId: Number(decoded.companyId) || null
        };
      }
    } catch (e) {
      console.error("Error decodificando token en el dashboard:", e);
    }
    return { id: null, role: 'Empleado', companyId: null };
  }, [token]);
  // Estado del desplegable de alertas personales. Debe vivir en Dashboard,
  // porque tanto el portal móvil como el encabezado personal se renderizan aquí.
  const [personalAlertPopoverOpen, setPersonalAlertPopoverOpen] = useState(false);
  const personalAlertAnchorRef = useRef(null);
  const [personalAlertPopoverStyle, setPersonalAlertPopoverStyle] = useState({});

  // Alertas personales del usuario. Se mantienen en Dashboard porque el portal
  // personal también existe para empleados y no depende de AdminPanel.
  const [personalNotificationRows, setPersonalNotificationRows] = useState([]);
  const [personalNotificationLoading, setPersonalNotificationLoading] = useState(false);
  // 📅 EXPORTACIÓN PERSONAL: el usuario selecciona un inicio y un fin.
  // Cada fecha identifica la semana completa a la que pertenece: el inicio
  // se lleva al lunes y el fin al domingo. Así nunca se exporta una semana
  // parcial por accidente.
  const fechaHoyPersonal = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; })();
  const [personalExportFromDate, setPersonalExportFromDate] = useState(fechaHoyPersonal);
  const [personalExportToDate, setPersonalExportToDate] = useState(fechaHoyPersonal);
  const [personalExportLoading, setPersonalExportLoading] = useState(false);
  const [personalExportRequest, setPersonalExportRequest] = useState({ status: 'SIN_SOLICITUD' });
  const [personalExportRequestLoading, setPersonalExportRequestLoading] = useState(false);

  const getPersonalExportWeekBounds = useCallback((value) => {
    const base = new Date(`${String(value || '').slice(0, 10)}T12:00:00`);
    if (Number.isNaN(base.getTime())) return null;
    const day = base.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    const start = new Date(base);
    start.setDate(base.getDate() + diff);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    const iso = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { start, end, startIso: iso(start), endIso: iso(end) };
  }, []);

  const getPersonalExportPeriod = useCallback(() => {
    const inicio = getPersonalExportWeekBounds(personalExportFromDate);
    const fin = getPersonalExportWeekBounds(personalExportToDate);
    if (!inicio || !fin) return null;
    if (inicio.startIso > fin.startIso) return null;
    const hoy = new Date();
    hoy.setHours(12, 0, 0, 0);
    const hoyIso = `${hoy.getFullYear()}-${String(hoy.getMonth() + 1).padStart(2, '0')}-${String(hoy.getDate()).padStart(2, '0')}`;
    const finReal = fin.endIso > hoyIso ? hoy : fin.end;
    const isoLocal = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    if (inicio.start.getTime() > hoy.getTime()) return null;
    return {
      start: inicio.start,
      end: finReal,
      startIso: inicio.startIso,
      endIso: isoLocal(finReal),
      currentWeek: fin.start.getTime() <= hoy.getTime() && fin.end.getTime() >= hoy.getTime()
    };
  }, [personalExportFromDate, personalExportToDate, getPersonalExportWeekBounds]);

  const performPersonalWeeklyPdfDownload = useCallback(async (periodoAutorizado = null) => {
    // La descarga debe usar siempre el periodo guardado en la solicitud autorizada,
    // nunca las fechas que estén seleccionadas actualmente en la pantalla.
    const periodo = periodoAutorizado || getPersonalExportPeriod();
    if (!periodo || !periodo.startIso || !periodo.endIso) {
      alert('La fecha inicial debe pertenecer a una semana igual o anterior a la semana de la fecha final.');
      return;
    }
    setPersonalExportLoading(true);
    try {
      const response = await axios.get(obtenerUrlBaseDashboard('/attendance', '/reports/my-weekly-pdf'), {
        params: { desde: periodo.startIso, hasta: periodo.endIso },
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: false
      });
      const contentType = String(response.headers?.['content-type'] || '');
      if (contentType.includes('application/json')) {
        const contenido = await response.data.text();
        let mensaje = 'No fue posible generar la exportación personal.';
        try { mensaje = JSON.parse(contenido)?.message || mensaje; } catch (_) {}
        throw new Error(mensaje);
      }
      const blob = new Blob([response.data], { type: contentType || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const disposition = String(response.headers?.['content-disposition'] || '');
      const match = disposition.match(/filename\*?=(?:UTF-8'')?["']?([^;"']+)["']?/i);
      const nombre = match?.[1] ? decodeURIComponent(match[1]) : `Reporte_Personal_${periodo.startIso}_${periodo.endIso}.pdf`;
      const url = window.URL.createObjectURL(blob);
      const enlace = document.createElement('a');
      enlace.href = url;
      enlace.download = nombre;
      document.body.appendChild(enlace);
      enlace.click();
      enlace.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error al descargar exportación personal:', error);
      let mensaje = error?.message || 'No fue posible generar la exportación personal.';
      if (error?.response?.data instanceof Blob) {
        try {
          const texto = await error.response.data.text();
          mensaje = JSON.parse(texto)?.message || mensaje;
        } catch (_) {}
      }
      alert(mensaje);
    } finally {
      setPersonalExportLoading(false);
    }
  }, [token, getPersonalExportPeriod]);

  const fetchPersonalExportRequest = useCallback(async () => {
    if (!token || !user?.id) return;
    try {
      const response = await axios.get(obtenerUrlBaseDashboard('/attendance', '/reports/my-export-request'), { headers: { Authorization: `Bearer ${token}` }, withCredentials: false });
      setPersonalExportRequest(response.data || { status: 'SIN_SOLICITUD' });
    } catch (error) {
      console.error('Error consultando solicitud de exportación:', error);
    }
  }, [token, user?.id]);

  const downloadPersonalWeeklyPdf = useCallback(async () => {
    const status = String(personalExportRequest?.status || 'SIN_SOLICITUD').toUpperCase();
    const tienePeriodoAutorizado = status === 'AUTORIZADA'
      && /^\d{4}-\d{2}-\d{2}$/.test(String(personalExportRequest?.desde || ''))
      && /^\d{4}-\d{2}-\d{2}$/.test(String(personalExportRequest?.hasta || ''));
    if (tienePeriodoAutorizado) {
      await performPersonalWeeklyPdfDownload({
        startIso: String(personalExportRequest.desde),
        endIso: String(personalExportRequest.hasta)
      });
      await fetchPersonalExportRequest();
      return;
    }
    if (status === 'PENDIENTE') {
      alert('Tu solicitud de descarga está esperando la revisión de un administrador.');
      return;
    }
    const periodo = getPersonalExportPeriod();
    if (!periodo) { alert('Selecciona un periodo válido antes de solicitar la exportación.'); return; }
    setPersonalExportRequestLoading(true);
    try {
      const response = await axios.post(obtenerUrlBaseDashboard('/attendance', '/reports/my-export-request'), { desde: periodo.startIso, hasta: periodo.endIso }, { headers: { Authorization: `Bearer ${token}` }, withCredentials: false });
      setPersonalExportRequest({ ...response.data, status: 'PENDIENTE' });
      alert(response.data?.message || 'Solicitud enviada a administración.');
      await fetchPersonalExportRequest();
    } catch (error) {
      alert(error.response?.data?.message || 'No fue posible solicitar la exportación.');
      await fetchPersonalExportRequest();
    } finally { setPersonalExportRequestLoading(false); }
  }, [personalExportRequest, performPersonalWeeklyPdfDownload, fetchPersonalExportRequest, getPersonalExportPeriod, token]);

  useEffect(() => {
    if (personalView !== 'personalRecords' || personalRecordsSubView !== 'export') return;
    fetchPersonalExportRequest();
  }, [personalView, personalRecordsSubView, fetchPersonalExportRequest]);


  const fetchPersonalNotifications = useCallback(async () => {
    if (!token || !user?.id) return;
    setPersonalNotificationLoading(true);
    try {
      const response = await axios.get(obtenerUrlBaseDashboard('/attendance', '/notifications'), {
        params: user?.companyId ? { companyId: Number(user.companyId) } : {},
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: false
      });
      const filas = Array.isArray(response.data) ? response.data : [];
      const filasPersonales = filas.filter(item => {
        // Los administradores reciben la bandeja de solicitudes de toda la empresa.
        // Los demás usuarios sólo reciben sus propias alertas.
        const rolActual = String(user?.role || '').trim().toLowerCase();
        const esAdministradorActual = ['administrador', 'superadministradorempresarial', 'superadministrador'].includes(rolActual);
        if (!['NUEVA', 'LEIDA'].includes(String(item?.status || '').toUpperCase())) return false;
        if (!esAdministradorActual && Number(item?.targetUserId) !== Number(user.id)) return false;
        const tipo = String(item?.type || '').toUpperCase();
        if (tipo !== 'CIERRE_JORNADA_CONFIRMACION') return true;
        const estado = String(item?.status || '').toUpperCase();
        const estadoCierre = String(item?.metadata?.status || '').toUpperCase();
        return !['CERRADA', 'ACUERDO', 'DESACUERDO'].includes(estado)
          && !['ACUERDO', 'DESACUERDO'].includes(estadoCierre);
      });
      setPersonalNotificationRows(filasPersonales);
    } catch (error) {
      console.error('Error cargando alertas personales:', error);
    } finally {
      setPersonalNotificationLoading(false);
    }
  }, [token, user?.id, user?.companyId]);

  const updatePersonalNotificationStatus = async (id, status) => {
    try {
      await axios.put(obtenerUrlBaseDashboard('/attendance', `/notifications/${id}`), { status }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: false
      });
      await fetchPersonalNotifications();
    } catch (error) {
      alert(error.response?.data?.message || 'No fue posible actualizar la alerta.');
    }
  };

  useEffect(() => {
    fetchPersonalNotifications();
    const refreshTimer = setInterval(fetchPersonalNotifications, 30000);
    const refreshOnFocus = () => fetchPersonalNotifications();
    window.addEventListener('focus', refreshOnFocus);
    return () => {
      clearInterval(refreshTimer);
      window.removeEventListener('focus', refreshOnFocus);
    };
  }, [fetchPersonalNotifications]);



  const normalizedUserRole = String(user?.role || '').trim().toLowerCase();
  const isGlobalSuperAdminPersonal = normalizedUserRole === 'superadministrador';
  const isCompanySuperAdminPersonal = normalizedUserRole === 'superadministradorempresarial';
  const isAdministrativeRolePersonal = normalizedUserRole === 'administrador' || isGlobalSuperAdminPersonal || isCompanySuperAdminPersonal;

  const clavePreferenciasPersonal = `relojChecador.userPreferences.${user?.id || 'anonimo'}`;
  const guardarPreferenciaPersonal = useCallback((clave, valor) => {
    if (!user?.id) return;
    try {
      const actual = JSON.parse(localStorage.getItem(clavePreferenciasPersonal) || '{}');
      localStorage.setItem(clavePreferenciasPersonal, JSON.stringify({ ...actual, [clave]: valor }));
    } catch (_) {}
  }, [clavePreferenciasPersonal, user?.id]);

  useEffect(() => {
    if (!user?.id) return;
    try {
      const preferencias = JSON.parse(localStorage.getItem(clavePreferenciasPersonal) || '{}');
      if (preferencias.theme === 'dark' || preferencias.theme === 'light') setIsDarkMode(preferencias.theme === 'dark');
      if (preferencias.monitorView === 'cards' || preferencias.monitorView === 'list') setMonitorViewMode(preferencias.monitorView);
    } catch (_) {}
    setPersonalPreferencesLoaded(true);
  }, [clavePreferenciasPersonal, user?.id]);

  useEffect(() => {
    if (!user?.id || !personalPreferencesLoaded) return;
    guardarPreferenciaPersonal('theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode, personalPreferencesLoaded, user?.id, guardarPreferenciaPersonal]);

  useEffect(() => {
    if (!user?.id || !personalPreferencesLoaded) return;
    guardarPreferenciaPersonal('monitorView', monitorViewMode);
  }, [monitorViewMode, personalPreferencesLoaded, user?.id, guardarPreferenciaPersonal]);

  // 👤 Obtiene el nombre real de la cuenta autenticada.
  // El JWT solo contiene ID y rol, por lo que consultamos el perfil
  // protegido del usuario para mostrar su nombre en el encabezado.
  useEffect(() => {
    let activo = true;

    const cargarNombreUsuario = async () => {
      if (!token) {
        if (activo) setCurrentUserName('');
        return;
      }

      try {
        const response = await axios.get('/api/users/me/permissions', {
          headers: {
            Authorization: `Bearer ${token}`
          },
          withCredentials: false
        });

        if (activo) {
          setCurrentUserName(String(response.data?.name || '').trim());
        }
      } catch (error) {
        console.error('Error al cargar el nombre del usuario autenticado:', error);
        if (activo) setCurrentUserName('');
      }
    };

    cargarNombreUsuario();

    return () => {
      activo = false;
    };
  }, [token]);

  // 🏢 Carga la identidad corporativa para mostrarla en todo el Dashboard.
  useEffect(() => {
    let activo = true;
    const cargarEmpresa = async () => {
      try {
        const response = await axios.get('/api/settings', { headers: { Authorization: `Bearer ${token}` }, withCredentials: false });
        if (activo && response.data?.company) setCompanySettings(response.data.company);
      } catch (error) {
        console.error('Error al cargar configuración de empresa:', error);
      }
    };
    if (token) cargarEmpresa();
    return () => { activo = false; };
  }, [token]);

  // 🔐 Estado de biometría del usuario autenticado.
  const cargarEstadoBiometrico = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get('/api/biometric/status', { headers: { Authorization: `Bearer ${token}` }, withCredentials: false });
      setBiometricStatus(response.data || { policy: 'OBLIGATORIA', required: true, credentials: [] });
    } catch (error) {
      console.error('Error cargando estado biométrico:', error);
    }
  }, [token]);

  useEffect(() => { cargarEstadoBiometrico(); }, [cargarEstadoBiometrico]);

  const registrarBiometria = async () => {
    if (!browserSupportsWebAuthn()) {
      alert('Este navegador/dispositivo no soporta WebAuthn/biometría compatible.');
      return;
    }
    setBiometricBusy(true);
    try {
      const optionsResponse = await axios.get('/api/biometric/registration-options', { headers: { Authorization: `Bearer ${token}` }, withCredentials: false });
      const registrationResponse = await startRegistration({ optionsJSON: optionsResponse.data });
      await axios.post('/api/biometric/register', registrationResponse, { headers: { Authorization: `Bearer ${token}` }, withCredentials: false });
      alert('🔐 Biometría registrada correctamente. Ya puede usar la biometría para fichar.');
    } catch (error) {
      console.error('Error registrando biometría:', error);
      alert(error?.response?.data?.message || 'No fue posible registrar la biometría.');
    }
  };

  const axiosAuthConfig = {
    withCredentials: false,
    headers: {
      Authorization: `Bearer ${token}`
    }
  };

  
const fetchHistory = useCallback(async () => {
  try {
    const response = await axios.get(
      obtenerUrlBaseDashboard('/attendance', '/me'),
      {
        ...axiosAuthConfig,
        params: { _ts: Date.now() },
        headers: {
          ...axiosAuthConfig.headers,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        withCredentials: false
      }
    );

    const registros = Array.isArray(response.data)
      ? response.data
      : [];

    const registrosOrdenados = [...registros].sort(compararMarcasCronologicamente);
    setCheckins(registrosOrdenados);

    // IMPORTANTE: el historial NO determina el botón Entrada/Salida.
    //
    // Las marcas mostradas pueden ser físicas, auditadas, sustituidas o
    // generadas automáticamente. Su procedencia es información de auditoría
    // y nunca debe mezclarse con el estado operativo de la jornada.
    // El único lugar que decide el siguiente tipo de registro es
    // /jornada-status, mediante fetchJornadaStatus().

  } catch (error) {
    console.error(
      'Error al obtener historial reactivo:',
      error
    );

    // IMPORTANTE:
    // Una falla en GET /attendance no debe
    // deshacer un registro que ya fue exitoso.
  }
}, [token]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const fetchJornadaStatus = useCallback(async () => {
    const requestId = ++jornadaStatusRequestRef.current;

    // Mientras se obtiene una nueva instantánea del servidor no conservamos
    // visualmente un Entrada/Salida viejo. El botón queda en estado CARGANDO
    // hasta recibir una respuesta válida de /jornada-status.
    setJornadaStatus(prev => ({
      ...prev,
      state: 'CARGANDO',
      nextAllowedType: null
    }));

    try {
      const response = await axios.get(obtenerUrlBaseDashboard('/attendance', '/jornada-status'), {
        ...axiosAuthConfig,
        params: { _ts: Date.now() },
        headers: { ...axiosAuthConfig.headers, 'Cache-Control': 'no-cache, no-store, must-revalidate' },
        withCredentials: false
      });

      // Si otra consulta más reciente ya terminó, esta respuesta quedó obsoleta
      // y no puede devolver el botón a un estado anterior.
      if (requestId !== jornadaStatusRequestRef.current) return;

      const status = response.data || {};
      setJornadaStatus(status);

      // ÚNICA fuente de verdad para el botón: jornadaStatus.nextAllowedType.
      // No copiamos este valor a otro estado local; así el historial, el origen
      // de la marca y cualquier respuesta anterior no pueden desincronizarlo.
    } catch (error) {
      if (requestId !== jornadaStatusRequestRef.current) return;
      console.error('Error al obtener estado de jornada:', error);
      setJornadaStatus(prev => ({
        ...prev,
        state: 'ERROR',
        nextAllowedType: null
      }));
    }
  }, [token]);

  useEffect(() => {
    fetchJornadaStatus();
  }, [fetchJornadaStatus]);

  const confirmarCierreJornadaPorId = async (closureId, decision, { actualizarNotificacionId = null } = {}) => {
    const idNumerico = Number(closureId);
    if (!idNumerico || jornadaConfirmationBusy) return false;

    setJornadaConfirmationBusy(true);
    try {
      const comment = decision === 'DESACUERDO'
        ? (window.prompt('Si deseas, agrega una observación para tu desacuerdo:') || '').trim()
        : '';

      const response = await axios.post(
        obtenerUrlBaseDashboard('/attendance', `/jornada-closures/${idNumerico}/confirm`),
        { decision, comment },
        axiosAuthConfig
      );

      // La confirmación ya fue aceptada por el backend. Liberamos de inmediato
      // la siguiente Entrada usando la respuesta confirmada, antes de hacer
      // consultas secundarias. Esto evita que una respuesta antigua de
      // /jornada-status, una carrera entre pestañas o una notificación todavía
      // marcada como pendiente vuelva a bloquear visualmente el fichaje.
      const cierreConfirmado = response.data?.closure || null;
      setJornadaStatus(prev => ({
        ...prev,
        state: 'CERRADA',
        nextAllowedType: 'Entrada',
        closure: cierreConfirmado,
        lastAttendance: response.data?.exitAttendance || prev?.lastAttendance || null
      }));

      // El historial y el estado del servidor siguen actualizándose, pero la
      // respuesta confirmada del endpoint es la fuente inmediata para liberar
      // el botón de Entrada.
      await fetchHistory();
      await fetchJornadaStatus();

      // Si la consulta de estado todavía devuelve la instantánea anterior por
      // una condición de carrera, no permitimos que vuelva a mostrar el cierre
      // como pendiente cuando acabamos de recibir una confirmación exitosa.
      if (String(cierreConfirmado?.status || '').toUpperCase() === 'ACUERDO' ||
          String(cierreConfirmado?.status || '').toUpperCase() === 'DESACUERDO') {
        setJornadaStatus(prev => ({
          ...prev,
          state: 'CERRADA',
          nextAllowedType: 'Entrada',
          closure: cierreConfirmado,
          lastAttendance: response.data?.exitAttendance || prev?.lastAttendance || null
        }));
      }

      if (actualizarNotificacionId) {
        try {
          await updateNotificationStatus(actualizarNotificacionId, 'CERRADA');
        } catch (_) {
          // La confirmación de la jornada ya fue exitosa; no la revertimos si
          // únicamente falla el cambio visual de estado de la notificación.
        }
      }

      alert(response.data?.message || 'Confirmación registrada.');
      return true;
    } catch (error) {
      // Si el backend informa que el cierre ya fue confirmado, no tratamos
      // esa respuesta como un nuevo error operativo. Puede ocurrir cuando la
      // primera confirmación sí se guardó pero la pantalla todavía mostraba
      // la notificación antigua. Refrescamos el estado y el historial para
      // que la Salida automática quede visible.
      const cierreRespuesta = error?.response?.data?.closure;
      const estadoCierre = String(cierreRespuesta?.status || '').toUpperCase();
      if (Number(error?.response?.status) === 409 && ['ACUERDO', 'DESACUERDO'].includes(estadoCierre)) {
        try {
          const cierreYaConfirmado = cierreRespuesta;
          // Un 409 aquí significa que la operación ya fue resuelta previamente.
          // No volvemos a consultar /jornada-status antes de liberar la Entrada,
          // porque una instantánea antigua podría volver a pintar el bloqueo.
          setJornadaStatus(prev => ({
            ...prev,
            state: 'CIERRE_CONFIRMADO',
            nextAllowedType: 'Entrada',
            closure: cierreYaConfirmado,
            lastAttendance: prev?.lastAttendance || null
          }));
          await fetchHistory();
          if (actualizarNotificacionId) {
            try {
              await updateNotificationStatus(actualizarNotificacionId, 'CERRADA');
            } catch (_) {}
          }
          return true;
        } catch (_) {
          // Si el refresco falla, conservamos el mensaje informativo del
          // backend abajo en lugar de ocultar un posible problema de red.
        }
      }
      alert(error?.response?.data?.message || 'No fue posible confirmar el cierre de la jornada.');
      return false;
    } finally {
      setJornadaConfirmationBusy(false);
    }
  };

  const confirmJornadaClosure = async decision => {
    const closureId = Number(jornadaStatus?.closure?.id);
    return confirmarCierreJornadaPorId(closureId, decision);
  };

  const confirmNotificationJornadaClosure = async (notification, decision) => {
    const closureId = Number(notification?.referenceId || notification?.metadata?.id);
    if (!closureId) {
      alert('La alerta no contiene un identificador válido del cierre de jornada.');
      return;
    }
    return confirmarCierreJornadaPorId(closureId, decision, { actualizarNotificacionId: Number(notification?.id) || null });
  };

  const fetchMyAdjustments = useCallback(async () => {
    try {
      const response = await axios.get(obtenerUrlBaseDashboard('/attendance', '/adjustments/my'), {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: false
      });
      setMyAdjustments(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error al obtener aclaraciones personales:', error);
    }
  }, [token]);

  const fetchMyLongShiftClarificationHistory = useCallback(async () => {
    if (!token) return;

    setMyLongShiftClarificationHistoryLoading(true);
    try {
      const response = await axios.get(
        obtenerUrlBaseDashboard('/attendance', '/long-shift-alerts/clarifications/my'),
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: false
        }
      );
      setMyLongShiftClarificationHistory(
        Array.isArray(response.data) ? response.data : []
      );
    } catch (error) {
      console.error('Error al obtener historial personal de jornadas prolongadas:', error);
      setMyLongShiftClarificationHistory([]);
    } finally {
      setMyLongShiftClarificationHistoryLoading(false);
    }
  }, [token]);

  const handleCreateEmployeeAdjustment = async (e) => {
    e.preventDefault();
    if (!employeeAdjustmentDateTime) return alert('Indica la fecha y hora que propones aclarar.');
    if (employeeAdjustmentReason.trim().length < 5) return alert('Explica el motivo de la aclaración.');
    setEmployeeAdjustmentSubmitting(true);
    try {
      await axios.post(obtenerUrlBaseDashboard('/attendance', '/adjustments'), {
        originalAttendanceId: employeeAdjustmentOriginalId || null,
        proposedType: employeeAdjustmentType,
        proposedTimestamp: employeeAdjustmentDateTime,
        reason: employeeAdjustmentReason.trim(), ...( ['administrador','superadministrador','superadministradorempresarial'].includes(String(user?.role || '').trim().toLowerCase()) ? { userId: user.id } : {})
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: false
      });
      alert(['administrador','superadministrador','superadministradorempresarial'].includes(String(user?.role || '').trim().toLowerCase()) ? '📝 Aclaración enviada para revisión de otro administrador.' : '📝 Aclaración enviada al administrador.');
      setEmployeeAdjustmentOriginalId('');
      setEmployeeAdjustmentDateTime('');
      setEmployeeAdjustmentReason('');
      fetchMyAdjustments();
    } catch (error) {
      console.error('Error al enviar aclaración:', error);
      alert(error.response?.data?.message || 'No fue posible enviar la aclaración.');
    } finally {
      setEmployeeAdjustmentSubmitting(false);
    }
  };

  const handleEmployeeReviewAdjustment = async (adjustmentId, decision) => {
    try {
      await axios.put(obtenerUrlBaseDashboard('/attendance', `/adjustments/${adjustmentId}/review`), {
        decision
      }, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: false
      });
      // Una aceptación genera/activa una marca de Attendance; refrescamos
      // inmediatamente el historial para que aparezca en Registros de horas.
      await fetchHistory();
      await fetchMyAdjustments();
    } catch (error) {
      console.error('Error al responder al ajuste:', error);
      alert(error.response?.data?.message || 'No fue posible responder al ajuste.');
    }
  };

  useEffect(() => {
    fetchMyAdjustments();
    fetchMyLongShiftClarificationHistory();

    const intervaloAclaraciones = setInterval(() => {
      fetchMyAdjustments();
      fetchMyLongShiftClarificationHistory();
    }, 30000);

    return () => clearInterval(intervaloAclaraciones);
  }, [fetchMyAdjustments, fetchMyLongShiftClarificationHistory]);

  const filteredCheckins = useMemo(() => {
    let filtradas = checkins;
    if (filterType !== 'Todos') {
      filtradas = filtradas.filter(c => c.type === filterType);
    }
    const ahora = new Date();

    const partesAhora = new Intl.DateTimeFormat('en-CA', {
      timeZone: ZONA_HORARIA_ASISTENCIA,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23'
    }).formatToParts(ahora);

    const valoresAhora = {};
    partesAhora.forEach(({ type, value }) => {
      if (type !== 'literal') valoresAhora[type] = value;
    });

    const fechaLimite = new Date(
      `${valoresAhora.year}-${valoresAhora.month}-${valoresAhora.day}T00:00:00`
    );
    fechaLimite.setDate(fechaLimite.getDate() - personalDaysRange);

    const limiteTexto =
      `${fechaLimite.getFullYear()}-${String(fechaLimite.getMonth() + 1).padStart(2, '0')}-${String(fechaLimite.getDate()).padStart(2, '0')}`;

    return filtradas
      .filter(c => {
        const partes = obtenerPartesTimestamp(c.timestamp);
        return partes ? partes.fechaPlana >= limiteTexto : false;
      })
      .slice()
      .sort((a, b) => {
        // El historial personal debe respetar exactamente la fecha/hora que
        // se muestra al usuario. No usamos el orden en que llegaron los
        // registros ni el ID, porque una aclaración puede crear hoy una
        // marca que pertenece a una fecha anterior.
        return compararMarcasCronologicamente(a, b);
      });
  }, [checkins, filterType, personalDaysRange]);

  // 🚀 REGISTRO ÁGIL BIOMÉTRICO + GPS VELOZ EN SEGUNDO PLANO
  const handleRegister = async (e) => {
    e.preventDefault();
    if (attendanceBusy) return;
    setAttendanceBusy(true);

    const typeToSend = tipoRegistroActual;
    if (!['Entrada', 'Salida'].includes(typeToSend)) {
      setAttendanceBusy(false);
      alert('El estado de la jornada todavía no está listo para registrar una marca. Actualiza la información e inténtalo nuevamente.');
      return;
    }

    let biometricAssertion = null;
    const policy = String(biometricStatus.policy || companySettings.biometricPolicy || 'OBLIGATORIA').toUpperCase();
    const credentials = Array.isArray(biometricStatus.credentials) ? biometricStatus.credentials : [];

    if (policy !== 'DESHABILITADA') {
      if (!browserSupportsWebAuthn()) {
        if (policy === 'OBLIGATORIA') {
          alert('🔒 La biometría es obligatoria, pero este dispositivo/navegador no soporta WebAuthn.');
          setAttendanceBusy(false);
          return;
        }
      } else if (credentials.length === 0) {
        if (policy === 'OBLIGATORIA') {
          alert('🔐 Primero registra tu biometría con el botón "Registrar biometría".');
          setAttendanceBusy(false);
          return;
        }
      } else {
        try {
          setBiometricBusy(true);
          const optionsResponse = await axios.get('/api/biometric/authentication-options', { headers: { Authorization: `Bearer ${token}` }, withCredentials: false });
          biometricAssertion = await startAuthentication({ optionsJSON: optionsResponse.data });
        } catch (error) {
          console.error('Error en verificación biométrica:', error);
          if (policy === 'OBLIGATORIA') {
            alert(error?.response?.data?.message || error?.message || 'No fue posible verificar la biometría.');
            setBiometricBusy(false);
            return;
          }
          biometricAssertion = null;
        } finally {
          setBiometricBusy(false);
        }
      }
    }

    if (!navigator.geolocation) {
      alert('Tu dispositivo no soporta el registro de ubicación.');
      setAttendanceBusy(false);
      return;
    }

    const gpsOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 0
    };

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lon = position.coords.longitude;

        console.log('📍 GPS obtenido:', {
          latitude: lat,
          longitude: lon
        });

        try {
          const response = await axios.post(
            obtenerUrlBaseDashboard('/attendance', ''),
            {
              type: typeToSend,
              latitude: lat,
              longitude: lon,
              biometricAssertion
            },
            {
              ...axiosAuthConfig,
              withCredentials: false
            }
          );

          console.log('✅ Asistencia registrada:', response.data);

          alert(
            `¡${typeToSend} registrada con éxito!\n` +
            `Estatus de Geocerca: ${response.data.status || 'Válido'}`
          );

          // El historial se actualiza por separado y el botón queda bajo una
          // única fuente de verdad: /jornada-status.
          await fetchHistory();
          await fetchJornadaStatus();
          setAttendanceBusy(false);

        } catch (error) {
          console.error('❌ Error al registrar asistencia:', error);

          const mensaje =
            error?.response?.data?.message ||
            error?.response?.data?.error ||
            'No se pudo registrar la asistencia.';

          alert(`❌ ${mensaje}`);

          // Sincroniza el botón si el backend rechazó una secuencia inválida.
          await fetchHistory();
          await fetchJornadaStatus();
          setAttendanceBusy(false);
        }
      },
      (error) => {
        console.error('❌ Error de GPS:', error);

        let mensaje = 'No se pudo obtener la ubicación.';

        switch (error.code) {
          case error.PERMISSION_DENIED:
            mensaje =
              'Permiso de ubicación denegado. Activa la ubicación para registrar la asistencia.';
            break;

          case error.POSITION_UNAVAILABLE:
            mensaje =
              'La ubicación no está disponible. Verifica el GPS o la configuración de ubicación.';
            break;

          case error.TIMEOUT:
            mensaje =
              'Se agotó el tiempo para obtener la ubicación. Intenta nuevamente.';
            break;

          default:
            mensaje =
              'No se pudo obtener una ubicación precisa.';
            break;
        }

        alert(`❌ ${mensaje}`);
        setAttendanceBusy(false);
      },
      gpsOptions
    );
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    window.location.reload();
  };

  const openPersonalAlerts = () => {
    setPersonalAlertPopoverOpen(false);
    setPersonalView?.('personalNotifications');
  };

  const updatePersonalAlertPopoverPosition = useCallback(() => {
    if (!personalAlertAnchorRef.current) return;
    const rect = personalAlertAnchorRef.current.getBoundingClientRect();
    const mobile = window.innerWidth <= 768;
    if (mobile) {
      // El popup se ancla debajo del boton real de la campana.
      // Asi no tapa el icono, incluso si cambia la altura de la barra superior
      // por fuente, navegador o area segura del dispositivo.
      const top = Math.max(8, Math.round(rect.bottom + 12));
      const available = Math.max(240, Math.min(420, Math.round(window.innerHeight - top - 12)));
      setPersonalAlertPopoverStyle({ position: 'fixed', top: `${top}px`, left: '4%', right: 'auto', width: '92%', maxWidth: '520px', bottom: 'auto', height: `${Math.min(Math.round(window.innerHeight * 0.46), available)}px`, minHeight: `${Math.min(240, available)}px`, maxHeight: `${available}px`, zIndex: 2147483647 });
    } else {
      const width = Math.min(390, Math.max(280, window.innerWidth - 24));
      const left = Math.max(12, Math.min(rect.right - width, window.innerWidth - width - 12));
      setPersonalAlertPopoverStyle({ position: 'fixed', top: `${Math.min(rect.bottom + 8, window.innerHeight - 120)}px`, left: `${left}px`, width: `${width}px`, maxHeight: 'min(480px, calc(100vh - 90px))', zIndex: 20000 });
    }
  }, []);

  const togglePersonalAlerts = () => {
    setPersonalAlertPopoverOpen(value => { const next = !value; if (next) requestAnimationFrame(updatePersonalAlertPopoverPosition); return next; });
  };

  const openPersonalAlertFromPopover = () => {
    setPersonalAlertPopoverOpen(false);
    setPersonalView?.('personalNotifications');
  };

  useEffect(() => {
    if (!personalAlertPopoverOpen) return undefined;
    const reposition = () => updatePersonalAlertPopoverPosition();
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => { window.removeEventListener('resize', reposition); window.removeEventListener('scroll', reposition, true); };
  }, [personalAlertPopoverOpen, updatePersonalAlertPopoverPosition]);

  const renderPersonalAlertPopover = () => {
    const filas = Array.isArray(personalNotificationRows) ? personalNotificationRows.slice(0, 6) : [];
    return (
      <div className="dashboard-alert-popover dashboard-alert-popover-viewport" style={{ ...personalAlertPopoverStyle, position: 'fixed', top: personalAlertPopoverStyle?.top || '62px', left: personalAlertPopoverStyle?.left || '4%', right: personalAlertPopoverStyle?.right || 'auto', width: personalAlertPopoverStyle?.width || '92%', maxWidth: personalAlertPopoverStyle?.maxWidth || '520px', bottom: personalAlertPopoverStyle?.bottom || undefined, height: personalAlertPopoverStyle?.height || '46vh', minHeight: personalAlertPopoverStyle?.minHeight || '240px', maxHeight: personalAlertPopoverStyle?.maxHeight || '420px', overflowY: 'auto', zIndex: 2147483647 }} onClick={e => e.stopPropagation()}>
        <div className="dashboard-alert-popover-header">
          <strong>🔔 {isAdministrativeRolePersonal ? 'Alertas y solicitudes' : 'Mis alertas'}</strong>
          <span style={{display:'inline-flex',alignItems:'center',gap:8}}>{filas.filter(n => String(n?.status || '').toUpperCase() === 'NUEVA').length} nuevas <button type="button" aria-label="Cerrar alertas" title="Cerrar" onClick={() => setPersonalAlertPopoverOpen(false)} style={{border:0,background:'transparent',fontSize:18,lineHeight:1,cursor:'pointer',padding:2}}>×</button></span>
        </div>
        {personalNotificationLoading ? (
          <div className="dashboard-alert-popover-empty">Cargando alertas…</div>
        ) : !filas.length ? (
          <div className="dashboard-alert-popover-empty">No hay alertas pendientes.</div>
        ) : (
          <div>
            {filas.map(item => (
              <button key={item.id} type="button" className="dashboard-alert-popover-item" onClick={openPersonalAlertFromPopover}>
                <span className="dashboard-alert-popover-dot" />
                <span style={{ minWidth: 0, flex: 1 }}>
                  <strong>{item.title || item.type || 'Alerta'}</strong>
                  <span>{item.message || 'Tienes una nueva alerta.'}</span>
                </span>
              </button>
            ))}
          </div>
        )}
        <button type="button" className="dashboard-alert-popover-footer" onClick={openPersonalAlerts}>Ver todas las alertas →</button>
      </div>
    );
  };

  const textCol = isDarkMode ? '#f8fafc' : '#2d3748';
  const cardBg = isDarkMode ? '#1e293b' : '#ffffff';
  const borderCol = isDarkMode ? '#334155' : '#e2e8f0';
  const personalInputBg = isDarkMode ? '#334155' : '#ffffff';

  const renderPersonalPortal = (embedded = false) => {
    if (isGlobalSuperAdminPersonal && personalView !== 'account') {
      return (
        <div className="dashboard-mobile-safe dashboard-personal-history" style={{ fontFamily: '"Segoe UI", Roboto, sans-serif', maxWidth: embedded ? 'none' : '1200px', margin: embedded ? 0 : '0 auto', padding: embedded ? 0 : '30px', color: textCol }}>
          <section style={{ background: cardBg, padding: '25px', borderRadius: '12px', border: `1px solid ${borderCol}` }}>
            <h2 style={{ marginTop: 0, color: textCol }}>🏢 Administración global</h2>
            <p style={{ color: isDarkMode ? '#94a3b8' : '#718096' }}>La cuenta SuperAdministrador Global no pertenece a una empresa y no registra asistencia personal.</p>
            <button type="button" onClick={() => setPersonalView?.('account')} style={{ padding: '9px 12px', border: 'none', borderRadius: '7px', background: '#3182ce', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Ir a Mi perfil</button>
          </section>
        </div>
      );
    }

    return (
    <div className="dashboard-mobile-safe dashboard-personal-history" style={{ fontFamily: '"Segoe UI", Roboto, sans-serif', maxWidth: embedded ? 'none' : '1200px', margin: embedded ? 0 : '0 auto', padding: embedded ? 0 : '30px', color: textCol, transition: 'all 0.3s' }}>
      <style>{responsiveStyles}</style>

      {!embedded && (
        <>
          <div className="dashboard-mobile-topbar" style={{ '--dashboard-mobile-bg': isDarkMode ? '#0f172a' : '#ffffff', '--dashboard-mobile-border': borderCol, color: textCol }}>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div className="dashboard-mobile-topbar-title">{personalView === 'account' ? 'Mi perfil' : personalView === 'personalLeave' ? 'Mis permisos' : personalView === 'personalAdjustments' ? 'Mis aclaraciones' : personalView === 'personalNotifications' ? 'Mis alertas' : 'Mis registros'}</div>
              <div className="dashboard-mobile-topbar-subtitle">{companySettings.companyName || 'ANA'}</div>
            </div>
            <div className="dashboard-alert-anchor" ref={personalAlertAnchorRef}>
              <button type="button" className="dashboard-mobile-iconbutton dashboard-alert-button" onClick={togglePersonalAlerts} aria-expanded={personalAlertPopoverOpen} aria-label="Alertas personales" title="Alertas personales">🔔{personalNotificationRows.filter(n => String(n?.status || '').toUpperCase() === 'NUEVA').length > 0 && <span className="dashboard-alert-badge">{personalNotificationRows.filter(n => String(n?.status || '').toUpperCase() === 'NUEVA').length > 99 ? '99+' : personalNotificationRows.filter(n => String(n?.status || '').toUpperCase() === 'NUEVA').length}</span>}</button>
              {personalAlertPopoverOpen && createPortal(renderPersonalAlertPopover(), document.body)}
            </div>
            <button type="button" className="dashboard-mobile-iconbutton" onClick={() => setPersonalView?.('account')} aria-label="Mi perfil">👤</button>
          </div>
        </>
      )}

      {!embedded && <header className="dashboard-personal-desktop-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: `1px solid ${borderCol}`, paddingBottom: '20px', marginBottom: '30px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '14px', minWidth: 0 }}>
          {companySettings.logoDataUrl ? <img src={companySettings.logoDataUrl} alt="Logo" style={{ width: '72px', height: '52px', objectFit: 'contain', flexShrink: 0 }} /> : <div style={{ fontSize: '34px', width: '72px', textAlign: 'center', flexShrink: 0 }}>🕒</div>}
          <div>
          <h1 style={{ margin: 0, fontSize: '28px', color: isDarkMode ? '#f1f5f9' : '#1a202c' }}>{companySettings.companyName || 'Reloj Checador 3.0'}</h1>
          <p style={{ margin: '5px 0 0 0', color: isDarkMode ? '#94a3b8' : '#718096' }}>Bienvenido, <strong>{currentUserName || `Usuario ID ${user.id}`}</strong> · ID: <strong>{user.id}</strong> <span style={{ textTransform: 'uppercase', fontSize: '12px', backgroundColor: isDarkMode ? '#334155' : '#e2e8f0', color: textCol, padding: '2px 6px', borderRadius: '4px', marginLeft: '5px', fontWeight: 'bold' }}>{user.role}</span></p>
          </div>
        </div>
        {myAdjustments.some(a => a.status === 'PENDIENTE' && ['administrador','superadministrador','superadministradorempresarial'].includes(String(a.requestedByRole || '').toLowerCase())) && (
          <div style={{ marginLeft: 'auto', marginRight: '10px', padding: '8px 12px', borderRadius: '8px', background: '#fffaf0', color: '#c05621', border: '1px solid #dd6b20', fontSize: '12px', fontWeight: 'bold' }}>
            🔔 Tienes una aclaración pendiente de aceptar
          </div>
        )}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
          <div className="dashboard-alert-anchor" ref={personalAlertAnchorRef}>
          <button type="button" className="dashboard-desktop-alert-button dashboard-alert-button" onClick={togglePersonalAlerts} aria-expanded={personalAlertPopoverOpen} aria-label="Abrir alertas" title="Abrir alertas">
            <span aria-hidden="true">🔔</span>
            <span>Alertas</span>
            {personalNotificationRows.filter(n => String(n?.status || '').toUpperCase() === 'NUEVA').length > 0 && <span className="dashboard-desktop-alert-badge">{personalNotificationRows.filter(n => String(n?.status || '').toUpperCase() === 'NUEVA').length > 99 ? '99+' : personalNotificationRows.filter(n => String(n?.status || '').toUpperCase() === 'NUEVA').length}</span>}
          </button>
          
        </div>
          <button onClick={handleLogout} style={{ padding: '10px 18px', backgroundColor: '#e53e3e', color: 'white', border: 'none', borderRadius: '6px', fontWeight: 'bold', cursor: 'pointer' }}>
            Cerrar Sesión
          </button>
        </div>
      </header>}

      <div className={embedded ? '' : 'dashboard-personal-desktop-shell'}>
        {!embedded && (
          <aside className="dashboard-personal-sidebar" style={{ borderColor: borderCol, background: isDarkMode ? '#111827' : '#f8fafc', color: textCol }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', paddingBottom: '12px', marginBottom: '12px', borderBottom: `1px solid ${borderCol}` }}>
              {companySettings.logoDataUrl ? (
                <img src={companySettings.logoDataUrl} alt="Logo" style={{ width: '42px', height: '36px', objectFit: 'contain', flexShrink: 0 }} />
              ) : (
                <div style={{ width: '42px', textAlign: 'center', fontSize: '25px' }}>🕒</div>
              )}
              <div style={{ minWidth: 0 }}>
                <div style={{ fontWeight: 800, fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{companySettings.companyName || 'ANA'}</div>
                <div style={{ fontSize: '10px', color: isDarkMode ? '#94a3b8' : '#718096', marginTop: '2px' }}>{currentUserName || `Usuario ID ${user.id}`}</div>
              </div>
            </div>
            <div style={{ fontSize: '10px', fontWeight: 800, color: isDarkMode ? '#94a3b8' : '#718096', textTransform: 'uppercase', marginBottom: '6px' }}>Navegación</div>
            <nav style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {[
                { id: 'personalRecords', icon: '🏠', label: 'Inicio · Mis registros' },
                { id: 'personalLeave', icon: '🏖️', label: 'Mis permisos' },
                { id: 'personalAdjustments', icon: '📝', label: 'Mis aclaraciones' },
                { id: 'personalNotifications', icon: '🔔', label: 'Mis alertas' },
                { id: 'account', icon: '👤', label: 'Mi perfil' }
              ].map(item => (
                <button key={item.id} type="button" onClick={() => setPersonalView(item.id)} style={{ width: '100%', border: 'none', borderRadius: '8px', padding: '10px 11px', display: 'flex', alignItems: 'center', gap: '9px', textAlign: 'left', cursor: 'pointer', background: personalView === item.id ? (isDarkMode ? '#2563eb' : '#3182ce') : 'transparent', color: personalView === item.id ? '#fff' : textCol, fontWeight: personalView === item.id ? 700 : 500, fontSize: '12px' }}>
                  <span>{item.icon}</span><span>{item.label}</span>
                </button>
              ))}
            </nav>
            <div style={{ marginTop: '16px', paddingTop: '12px', borderTop: `1px solid ${borderCol}` }}>
              <button type="button" onClick={handleLogout} style={{ width: '100%', padding: '10px 11px', border: 'none', borderRadius: '8px', background: '#e53e3e', color: '#fff', fontWeight: 800, cursor: 'pointer', fontSize: '12px' }}>🚪 Cerrar sesión</button>
            </div>
          </aside>
        )}
        <div className={embedded ? '' : 'dashboard-personal-content'}>

      {personalView === 'account' ? (
        <section style={{ background: cardBg, padding: '25px', borderRadius: '12px', border: `1px solid ${borderCol}`, boxShadow: '0 4px 6px rgba(0,0,0,0.02)' }}>
          <h2 style={{ marginTop: 0, color: textCol }}>👤 Mi perfil</h2>
          <p style={{ color: isDarkMode ? '#94a3b8' : '#718096', fontSize: '13px' }}>Configuración personal de la cuenta autenticada.</p>
          <div style={{ display: 'grid', gap: '14px' }}>
            <div style={{ padding: '15px', borderRadius: '9px', border: `1px solid ${borderCol}`, background: isDarkMode ? '#131924' : '#f8fafc' }}>
              <div style={{ fontWeight: 800, color: textCol }}>👤 Datos de la cuenta</div>
              <div style={{ marginTop: '6px', fontSize: '13px', color: textCol }}>Nombre: <strong>{currentUserName || 'Usuario'}</strong></div>
              <div style={{ marginTop: '4px', fontSize: '13px', color: textCol }}>ID: <strong>{user.id}</strong></div>
              <div style={{ marginTop: '4px', fontSize: '13px', color: textCol }}>Rol: <strong>{user.role}</strong></div>
            </div>
            <div style={{ padding: '15px', borderRadius: '9px', border: `1px solid ${biometricStatus.credentials?.length ? '#38a169' : borderCol}`, background: isDarkMode ? '#131924' : '#f8fafc' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <div>
                  <div style={{ fontWeight: 800, color: textCol }}>🔐 Biometría</div>
                  <div style={{ marginTop: '4px', fontSize: '12px', color: isDarkMode ? '#94a3b8' : '#718096' }}>{biometricStatus.credentials?.length ? 'Registrada y disponible para fichar.' : 'Aún no hay una credencial biométrica registrada.'}</div>
                  <div style={{ marginTop: '3px', fontSize: '11px', color: '#718096' }}>Política efectiva: {biometricStatus.policy === 'OBLIGATORIA' ? 'Obligatoria' : biometricStatus.policy === 'OPCIONAL' ? 'Opcional' : 'Deshabilitada'} · Credenciales: {biometricStatus.credentials?.length || 0}</div>
                </div>
                {biometricStatus.policy !== 'DESHABILITADA' && (
                  <button type="button" onClick={registrarBiometria} disabled={biometricBusy} style={{ padding: '8px 12px', border: 'none', borderRadius: '6px', background: biometricBusy ? '#718096' : '#805ad5', color: '#fff', cursor: biometricBusy ? 'wait' : 'pointer', fontWeight: 'bold' }}>
                    {biometricBusy ? '⏳ Procesando...' : '🔐 Registrar / agregar biometría'}
                  </button>
                )}
              </div>
            </div>
            <div style={{ padding: '15px', borderRadius: '9px', border: `1px solid ${borderCol}`, background: isDarkMode ? '#131924' : '#f8fafc' }}>
              <div style={{ fontWeight: 800, color: textCol }}>🎨 Preferencias personales</div>
              <div style={{ marginTop: '5px', fontSize: '11px', color: isDarkMode ? '#94a3b8' : '#718096' }}>Estas preferencias se guardan en este dispositivo para tu cuenta.</div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '12px', marginTop: '12px' }}>
                <label style={{ fontSize: '12px', fontWeight: 700, color: textCol }}>Apariencia de la aplicación
                  <select value={isDarkMode ? 'dark' : 'light'} onChange={e => { const dark = e.target.value === 'dark'; setIsDarkMode(dark); guardarPreferenciaPersonal('theme', dark ? 'dark' : 'light'); }} style={{ display: 'block', width: '100%', marginTop: '5px', padding: '8px', borderRadius: '7px', border: `1px solid ${borderCol}`, background: cardBg, color: textCol }}>
                    <option value="light">☀️ Claro</option>
                    <option value="dark">🌙 Oscuro</option>
                  </select>
                </label>
                {isAdministrativeRolePersonal && (
                  <label style={{ fontSize: '12px', fontWeight: 700, color: textCol }}>Vista predeterminada del Monitor
                    <select value={monitorViewMode} onChange={e => { setMonitorViewMode(e.target.value); guardarPreferenciaPersonal('monitorView', e.target.value); }} style={{ display: 'block', width: '100%', marginTop: '5px', padding: '8px', borderRadius: '7px', border: `1px solid ${borderCol}`, background: cardBg, color: textCol }}>
                      <option value="cards">▦ Tarjetas</option>
                      <option value="list">☷ Lista</option>
                    </select>
                  </label>
                )}
              </div>
            </div>
          </div>
          <div style={{ marginTop: '14px', padding: '15px', borderRadius: '9px', border: `1px solid ${borderCol}`, background: isDarkMode ? '#131924' : '#f8fafc' }}>
            <div style={{ fontWeight: 800, color: textCol }}>🚪 Sesión</div>
            <button type="button" onClick={handleLogout} style={{ marginTop: '9px', width: '100%', padding: '10px 12px', border: 'none', borderRadius: '7px', background: '#e53e3e', color: '#fff', fontWeight: 800, cursor: 'pointer' }}>Cerrar sesión</button>
          </div>
        </section>
      ) : (
      <main style={{ display: 'flex', flexDirection: 'column', gap: '30px' }}>
        {personalView === 'personalRecords' && (
          <>
        <section style={{ background: cardBg, padding: '14px 18px', borderRadius: '12px', border: `1px solid ${borderCol}`, boxShadow: '0 4px 6px rgba(0,0,0,0.02)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <button type="button" onClick={() => setPersonalRecordsSubView('records')} style={{ padding: '8px 12px', border: 0, borderRadius: '7px', background: personalRecordsSubView === 'records' ? '#3182ce' : (isDarkMode ? '#334155' : '#edf2f7'), color: personalRecordsSubView === 'records' ? '#fff' : textCol, fontWeight: 800, cursor: 'pointer' }}>📋 Registros</button>
            <button type="button" onClick={() => setPersonalRecordsSubView('export')} style={{ padding: '8px 12px', border: 0, borderRadius: '7px', background: personalRecordsSubView === 'export' ? '#3182ce' : (isDarkMode ? '#334155' : '#edf2f7'), color: personalRecordsSubView === 'export' ? '#fff' : textCol, fontWeight: 800, cursor: 'pointer' }}>📤 Exportar</button>
          </div>
        </section>

        {personalRecordsSubView === 'export' ? (
          <section style={{ background: cardBg, padding: '25px', borderRadius: '12px', border: `1px solid ${borderCol}`, boxShadow: '0 4px 6px rgba(0,0,0,0.02)' }}>
            <div style={{ marginBottom: '18px' }}>
              <h2 style={{ margin: 0, color: textCol }}>📤 Exportar mis registros</h2>
              <p style={{ margin: '6px 0 0', color: isDarkMode ? '#94a3b8' : '#718096', fontSize: '12px' }}>Selecciona una fecha inicial y una fecha final. Cada una se interpreta como una referencia de semana: la inicial se convierte al lunes de su semana y la final al domingo de su semana.</p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(220px,1fr))', gap: '12px', alignItems: 'end' }}>
              <label style={{ fontSize: '12px', fontWeight: 800, color: textCol }}>Desde
                <input type="date" max={fechaHoyPersonal} value={personalExportFromDate} onChange={e => setPersonalExportFromDate(e.target.value)} style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: '5px', padding: '9px', borderRadius: '7px', border: `1px solid ${borderCol}`, background: personalInputBg, color: textCol }} />
              </label>
              <label style={{ fontSize: '12px', fontWeight: 800, color: textCol }}>Hasta
                <input type="date" max={fechaHoyPersonal} value={personalExportToDate} onChange={e => setPersonalExportToDate(e.target.value)} style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: '5px', padding: '9px', borderRadius: '7px', border: `1px solid ${borderCol}`, background: personalInputBg, color: textCol }} />
              </label>
              <div style={{ padding: '10px 12px', borderRadius: '8px', background: isDarkMode ? '#172033' : '#f8fafc', border: `1px solid ${borderCol}`, color: textCol, fontSize: '12px' }}>
                {(() => { const p = getPersonalExportPeriod(); return p ? <><strong>Periodo que se exportará:</strong><br />{p.start.toLocaleDateString('es-MX')} — {p.end.toLocaleDateString('es-MX')}{p.currentWeek && <><br /><strong style={{ color: '#b7791f' }}>Semana en curso: el conteo todavía no cierra.</strong></>}</> : 'Selecciona un rango válido o una semana que ya haya comenzado.'; })()}
              </div>
            </div>
            <div style={{ marginTop: '18px', padding: '13px', borderRadius: '8px', border: `1px solid ${borderCol}`, background: isDarkMode ? '#131924' : '#f8fafc', color: textCol, fontSize: '12px' }}>
              <strong>El PDF incluirá:</strong> cada registro personal, jornadas con horas laboradas, resumen de cada semana, horas ordinarias, horas excedentes (dobles y triples), horas no clasificadas y revisiones pendientes. Los cálculos se toman del mismo motor de jornadas que utiliza el sistema.
            </div>
            {String(personalExportRequest?.status || '').toUpperCase() === 'AUTORIZADA' && personalExportRequest?.desde && personalExportRequest?.hasta && (
              <div style={{ marginTop: '12px', padding: '10px 12px', borderRadius: '8px', background: isDarkMode ? '#123126' : '#f0fff4', color: textCol, fontSize: '12px' }}>
                <strong>Periodo autorizado para descargar:</strong> {personalExportRequest.desde} — {personalExportRequest.hasta}. Se descargará este periodo aunque cambies las fechas seleccionadas arriba.
              </div>
            )}
            {personalExportRequest?.message && ['AUTORIZADA','RECHAZADA','DESCARGADA'].includes(String(personalExportRequest.status || '').toUpperCase()) && (
              <div style={{ marginTop: '12px', padding: '10px 12px', borderRadius: '8px', background: isDarkMode ? '#172033' : '#f0fff4', color: textCol, fontSize: '12px' }}>{personalExportRequest.message}</div>
            )}
            <button type="button" onClick={downloadPersonalWeeklyPdf} disabled={personalExportLoading || personalExportRequestLoading || String(personalExportRequest?.status || '').toUpperCase() === 'PENDIENTE'} style={{ marginTop: '16px', padding: '10px 16px', border: 0, borderRadius: '7px', background: personalExportLoading || personalExportRequestLoading ? '#718096' : (String(personalExportRequest?.status || '').toUpperCase() === 'AUTORIZADA' && personalExportRequest?.desde && personalExportRequest?.hasta) ? '#2f855a' : '#3182ce', color: '#fff', fontWeight: 800, cursor: personalExportLoading || personalExportRequestLoading ? 'wait' : 'pointer' }}>
              {personalExportLoading ? '⏳ Generando PDF...' : personalExportRequestLoading ? '⏳ Enviando solicitud...' : String(personalExportRequest?.status || '').toUpperCase() === 'PENDIENTE' ? '🕒 Esperando autorización' : (String(personalExportRequest?.status || '').toUpperCase() === 'AUTORIZADA' && personalExportRequest?.desde && personalExportRequest?.hasta) ? '📥 Descargar PDF autorizado' : String(personalExportRequest?.status || '').toUpperCase() === 'DESCARGADA' ? '📤 Exportar otro periodo' : '📤 Exportar'}
            </button>
          </section>
        ) : (
        <>
        <section style={{ background: isDarkMode ? '#1e293b' : '#f7fafc', padding: '35px', borderRadius: '12px', border: `1px solid ${borderCol}`, textAlign: 'center' }}>
          <div style={{ marginBottom: '20px' }}>
            <div style={{ fontSize: '42px', fontWeight: 'bold', fontFamily: 'monospace', letterSpacing: '2px', color: isDarkMode ? '#38bdf8' : '#3182ce' }}>
              {currentTime.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false })}
            </div>
            <div style={{ fontSize: '14px', fontWeight: 'bold', color: isDarkMode ? '#94a3b8' : '#4a5568', textTransform: 'capitalize', marginTop: '4px' }}>
              {currentTime.toLocaleDateString('es-MX', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}
            </div>
          </div>

          {jornadaStatus.state === 'PENDIENTE_CONFIRMACION' && jornadaStatus.closure && (
            <div style={{ marginBottom: '18px', padding: '16px', borderRadius: '9px', border: '1px solid #dd6b20', background: isDarkMode ? '#3b2415' : '#fffaf0', textAlign: 'left' }}>
              <div style={{ fontWeight: 800, color: isDarkMode ? '#fed7aa' : '#9c4221' }}>⚠️ Jornada cerrada automáticamente</div>
              <div style={{ marginTop: 6, fontSize: 13, color: textCol }}>
                Tu Entrada #{jornadaStatus.closure.entryAttendanceId} superó el máximo configurado de <strong>{Number(jornadaStatus.maxJornadaHours || 14)} horas</strong>.
                Antes de registrar una nueva Entrada debes indicar si estás de acuerdo o en desacuerdo con el cierre.
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
                <button type="button" onClick={() => confirmJornadaClosure('ACUERDO')} disabled={jornadaConfirmationBusy} style={{ padding: '9px 14px', border: 0, borderRadius: 7, background: '#38a169', color: '#fff', fontWeight: 800, cursor: 'pointer' }}>✓ Acuerdo</button>
                <button type="button" onClick={() => confirmJornadaClosure('DESACUERDO')} disabled={jornadaConfirmationBusy} style={{ padding: '9px 14px', border: 0, borderRadius: 7, background: '#c53030', color: '#fff', fontWeight: 800, cursor: 'pointer' }}>✗ Desacuerdo</button>
              </div>
            </div>
          )}

          <h3 style={{ marginTop: 0, color: isDarkMode ? '#cbd5e0' : '#4a5568', marginBottom: '20px' }}>
            {jornadaStatus.state === 'PENDIENTE_CONFIRMACION' ? 'Confirma el cierre de tu jornada para continuar' : '¿Deseas registrar tu marca actual?'}
          </h3>
          <form onSubmit={handleRegister}>
            <button key={`estado-jornada-${jornadaStatus.state}-${jornadaStatus.nextAllowedType || 'SIN_TIPO'}`} type="submit" disabled={biometricBusy || attendanceBusy || jornadaConfirmationBusy || jornadaStatus.state === 'PENDIENTE_CONFIRMACION' || ['CARGANDO', 'ERROR'].includes(jornadaStatus.state)} data-jornada-state={jornadaStatus.state} data-next-allowed-type={tipoRegistroActual || ''} data-ana-build="2026-09-30-jornada-v4" title={`Estado servidor: ${jornadaStatus.state || "SIN_ESTADO"} · Siguiente: ${jornadaStatus.nextAllowedType || "SIN_TIPO"}`} style={{ padding: '15px 40px', fontSize: '18px', fontWeight: 'bold', color: 'white', backgroundColor: attendanceBusy || jornadaStatus.state === 'PENDIENTE_CONFIRMACION' || ['CARGANDO', 'ERROR'].includes(jornadaStatus.state) ? '#718096' : (tipoRegistroActual === 'Entrada' ? '#319795' : tipoRegistroActual === 'Salida' ? '#dd6b20' : '#718096'), border: 'none', borderRadius: '8px', cursor: attendanceBusy || jornadaStatus.state === 'PENDIENTE_CONFIRMACION' || ['CARGANDO', 'ERROR'].includes(jornadaStatus.state) ? 'not-allowed' : 'pointer', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
              {jornadaStatus.state === 'PENDIENTE_CONFIRMACION'
                ? 'Confirmación requerida'
                : jornadaStatus.state === 'CARGANDO'
                  ? 'Consultando jornada…'
                  : jornadaStatus.state === 'ERROR'
                    ? 'No se pudo consultar la jornada'
                    : attendanceBusy
                      ? '⏳ Registrando…'
                      : tipoRegistroActual === 'Entrada'
                        ? '📥 Registrar Entrada'
                        : tipoRegistroActual === 'Salida'
                          ? '📤 Registrar Salida'
                          : 'Esperando estado de jornada'}
            </button>
          </form>
        </section>

        <section style={{ background: cardBg, padding: '25px', borderRadius: '12px', border: `1px solid ${borderCol}`, boxShadow: '0 4px 6px rgba(0,0,0,0.02)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', flexWrap: 'wrap', gap: '15px' }}>
            <h2 style={{ marginTop: 0, fontSize: '20px', color: textCol }}>Tu Historial Personal de Asistencias</h2>
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <div>
                <label style={{ marginRight: '6px', fontSize: '13px', fontWeight: 'bold', color: isDarkMode ? '#94a3b8' : '#4a5568' }}>Ver periodo:</label>
                {/* 🎯 SELECTOR ADAPTATIVO UNIVERSAL (EVITA EL ERROR DE VARIABLES EN ADMINPANEL) */}
                {/* 🎯 SELECTOR ADAPTATIVO UNIVERSAL (EVITA EL ERROR DE VARIABLES EN ADMINPANEL) */}
                <select 
                  value={typeof personalDaysRange !== 'undefined' ? personalDaysRange : (typeof daysRange !== 'undefined' ? daysRange : 7)} 
                  onChange={(e) => {
                    const val = parseInt(e.target.value);
                    if (typeof setPersonalDaysRange === 'function') setPersonalDaysRange(val);
                    else if (typeof setDaysRange === 'function') setDaysRange(val);
                  }} 
                  style={{ padding: '6px 12px', borderRadius: '6px', border: `1px solid ${borderCol}`, backgroundColor: cardBg, color: textCol, cursor: 'pointer', fontWeight: 'bold' }}
                >
                  <option value={7}>Últimos 7 días</option>
                  <option value={15}>Últimos 15 días</option>
                  <option value={30}>Últimos 30 días</option>
                </select>


              </div>
              <div>
                <label style={{ marginRight: '6px', fontSize: '13px', fontWeight: 'bold', color: isDarkMode ? '#94a3b8' : '#4a5568' }}>Tipo:</label>
                <select value={filterType} onChange={(e) => setFilterType(e.target.value)} style={{ padding: '6px 12px', borderRadius: '6px', border: `1px solid ${borderCol}`, backgroundColor: cardBg, color: textCol, cursor: 'pointer' }}>
                  <option value="Todos">🔄 Mostrar Todas</option>
                  <option value="Entrada">📥 Solo Entradas</option>
                  <option value="Salida">📤 Solo Salidas</option>
                </select>
              </div>
            </div>
          </div>

          <table className="dashboard-personal-history-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: isDarkMode ? '#1e293b' : '#f7fafc', textAlign: 'left', borderBottom: `2px solid ${borderCol}`, color: isDarkMode ? '#cbd5e0' : '#4a5568' }}>
                <th style={{ padding: '12px' }}>ID Registro</th>
                <th style={{ padding: '12px' }}>Auditoría</th>
                <th style={{ padding: '12px' }}>Tipo</th>
                <th style={{ padding: '12px' }}>Fecha y Hora de Marcación</th>
                <th style={{ padding: '12px' }}>📍 Ubicación (GPS)</th>
              </tr>
            </thead>
            <tbody>
              {filteredCheckins.length === 0 ? (
                <tr><td colSpan="5" style={{ padding: '20px', textAlign: 'center', color: '#a0aec0' }}>No se encontraron marcaciones en el periodo seleccionado.</td></tr>
              ) : (
                filteredCheckins.map((checkin, index) => (
                  <tr key={index} style={{ borderBottom: `1px solid ${borderCol}` }}>
                    <td style={{ padding: '12px', color: isDarkMode ? '#94a3b8' : '#718096' }}>{checkin.id}</td>
                    <td style={{ padding: '12px' }}>{(() => { const origen = obtenerEtiquetaOrigenMarca(checkin); return <span style={{ color: origen.color, fontWeight: 'bold', fontSize: '11px' }}>{origen.texto}</span>; })()}</td>
                    <td style={{ padding: '12px' }}>
                      <span style={{ padding: '4px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 'bold', backgroundColor: checkin.type === 'Entrada' ? '#e6fffa' : '#fff5f5', color: checkin.type === 'Entrada' ? '#319795' : '#e53e3e' }}>{checkin.type}</span>
                    </td>
                    <td style={{ padding: '12px', color: textCol }}>{formatearTimestampMerida(checkin.timestamp)}</td>
                    <td style={{ padding: '12px' }}>
                      {checkin.latitude !== undefined && checkin.latitude !== null && checkin.longitude !== undefined && checkin.longitude !== null && Number.isFinite(Number(checkin.latitude)) && Number.isFinite(Number(checkin.longitude)) ? (
                        <button 
                          onClick={(e) => {
                            e.preventDefault();
                            const latLimpia = String(checkin.latitude).replace(/https?:\/\/(www\.)?google\.com\/?/gi, '').replace(/[^0-9.-]/g, '');
                            const lonLimpia = String(checkin.longitude).replace(/https?:\/\/(www\.)?google\.com\/?/gi, '').replace(/[^0-9.-]/g, '');
                            const esMovil = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
                            let urlOficialMaps = esMovil ? `geo:${latLimpia},${lonLimpia}?q=${latLimpia},${lonLimpia}` : `https://google.com/maps?q=${latLimpia},${lonLimpia}`;
                            window.open(urlOficialMaps, '_blank', 'noopener,noreferrer');
                          }}
                          style={{ background: 'none', border: 'none', color: '#3182ce', fontWeight: 'bold', textDecoration: 'underline', fontSize: '14px', cursor: 'pointer', padding: 0 }}
                        >
                          🗺️ Ver en Mapa
                        </button>
                      ) : (
                        <span style={{ color: '#a0aec0', fontSize: '14px' }}>Sin GPS</span>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </section>
        </>
        )}
        </>
        )}

        {personalView === 'personalNotifications' && (
          <>
            <section style={{ background: cardBg, padding: '25px', borderRadius: '12px', border: `1px solid ${borderCol}`, boxShadow: '0 4px 6px rgba(0,0,0,0.02)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '15px' }}>
                <div>
                  <h2 style={{ margin: 0, color: textCol }}>🔔 {isAdministrativeRolePersonal ? 'Alertas y solicitudes' : 'Mis alertas'}</h2>
                  <p style={{ margin: '5px 0 0', fontSize: '12px', color: isDarkMode ? '#94a3b8' : '#718096' }}>{isAdministrativeRolePersonal ? 'Solicitudes y aclaraciones de los usuarios de tu empresa.' : 'Avisos personales relacionados con tu jornada y tu cuenta.'}</p>
                </div>
                <button type="button" onClick={fetchPersonalNotifications} disabled={personalNotificationLoading} style={{ padding: '8px 12px', border: 'none', borderRadius: '7px', background: '#3182ce', color: '#fff', fontWeight: 700, cursor: personalNotificationLoading ? 'wait' : 'pointer' }}>{personalNotificationLoading ? '⏳ Actualizando...' : '🔄 Actualizar'}</button>
              </div>
              {personalNotificationRows.length === 0 ? (
                <div style={{ padding: '20px', textAlign: 'center', border: `1px dashed ${borderCol}`, borderRadius: '9px', color: '#718096' }}>{isAdministrativeRolePersonal ? 'No hay solicitudes o alertas pendientes o recientes en la empresa.' : 'No tienes alertas personales pendientes o recientes.'}</div>
              ) : (
                <div style={{ display: 'grid', gap: '9px' }}>
                  {personalNotificationRows.map(item => (
                    <div key={item.id} style={{ padding: '12px', borderRadius: '9px', border: `1px solid ${item.status === 'NUEVA' ? '#f6ad55' : borderCol}`, background: isDarkMode ? '#131924' : '#f8fafc' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', flexWrap: 'wrap' }}>
                        <strong style={{ color: textCol, fontSize: '13px' }}>{item.title || item.type || 'Alerta'}</strong>
                        <span style={{ fontSize: '10px', fontWeight: 800, color: item.status === 'NUEVA' ? '#c05621' : '#718096' }}>{item.status || 'NUEVA'}</span>
                      </div>
                      <div style={{ marginTop: '5px', fontSize: '12px', color: textCol }}>{item.message || 'Tienes una nueva alerta.'}</div>
                      <div style={{ marginTop: '7px', display: 'flex', justifyContent: 'space-between', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '10px', color: '#718096' }}>{item.createdAt ? formatearTimestampMerida(item.createdAt) : ''}</span>
                        {item.status === 'NUEVA' && <button type="button" onClick={() => updatePersonalNotificationStatus(item.id, 'LEIDA')} style={{ padding: '5px 8px', border: 'none', borderRadius: '6px', background: '#718096', color: '#fff', fontWeight: 700, fontSize: '10px', cursor: 'pointer' }}>Marcar como leída</button>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </>
        )}

        {personalView === 'personalLeave' && (
          <>
        {/* FASE 11 · BLOQUE 2 · PORTAL DEL EMPLEADO */}
        <LeaveManagement
          token={token}
          isDarkMode={isDarkMode}
          companyId={user?.companyId || null}
          canManage={false}
        />
          </>
        )}

        {personalView === 'personalAdjustments' && (
          <>
        <section style={{ background: cardBg, padding: '25px', borderRadius: '12px', border: `1px solid ${borderCol}`, boxShadow: '0 4px 6px rgba(0,0,0,0.02)' }}>
          {/* 🚨 HISTORIAL PERSONAL DE JORNADAS PROLONGADAS */}
          <div style={{ marginTop: '20px', paddingTop: '18px', borderTop: `1px solid ${borderCol}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
              <div>
                <h3 style={{ margin: 0, color: textCol, fontSize: '16px' }}>🚨 Historial de jornadas prolongadas</h3>
                <div style={{ fontSize: '11px', color: '#718096', marginTop: '3px' }}>
                  Las alertas atendidas permanecen disponibles como historial de tu jornada.
                </div>
              </div>
              <button
                type="button"
                onClick={fetchMyLongShiftClarificationHistory}
                disabled={myLongShiftClarificationHistoryLoading}
                style={{ padding: '7px 10px', border: 'none', borderRadius: '6px', background: '#805ad5', color: '#fff', cursor: myLongShiftClarificationHistoryLoading ? 'wait' : 'pointer', fontWeight: 'bold', fontSize: '11px' }}
              >
                {myLongShiftClarificationHistoryLoading ? '⏳ Cargando...' : '🔄 Actualizar'}
              </button>
            </div>

            {myLongShiftClarificationHistoryLoading && myLongShiftClarificationHistory.length === 0 ? (
              <div style={{ padding: '12px', textAlign: 'center', color: '#718096', fontSize: '11px' }}>⏳ Cargando historial...</div>
            ) : myLongShiftClarificationHistory.length === 0 ? (
              <div style={{ padding: '12px', textAlign: 'center', color: '#a0aec0', fontSize: '11px' }}>No tienes jornadas prolongadas atendidas registradas.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {myLongShiftClarificationHistory.map(item => {
                  const administrador = item.aclaradoPor?.name || item.clarifiedBy?.name || item.clarifiedByUser?.name || `Usuario #${item.clarifiedByUserId}`;
                  const fecha = item.clarifiedAt || item.createdAt;
                  return (
                    <div key={item.id} style={{ padding: '10px', borderRadius: '7px', border: `1px solid ${borderCol}`, background: isDarkMode ? '#131924' : '#f8fafc' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                        <strong style={{ color: textCol, fontSize: '12px' }}>
                          Jornada · Entrada #{item.entryAttendanceId}
                        </strong>
                        <span style={{ fontSize: '10px', fontWeight: 'bold', color: '#2f855a' }}>
                          {item.status || 'ATENDIDA'}
                        </span>
                      </div>
                      <div style={{ marginTop: '4px', fontSize: '11px', color: textCol }}>
                        Umbral de alerta: <strong>{formatMinutesFromMinutes(Number(item.thresholdHours || 0) * 60)}</strong>
                      </div>
                      <div style={{ marginTop: '4px', fontSize: '11px', color: textCol }}>
                        Motivo: {item.reason || '—'}
                      </div>
                      <div style={{ marginTop: '4px', fontSize: '11px', color: textCol }}>
                        Observación: {item.observation || '—'}
                      </div>
                      <div style={{ marginTop: '5px', fontSize: '10px', color: '#718096' }}>
                        Atendida por: {administrador} · {fecha ? formatearTimestampMerida(fecha) : 'Fecha no disponible'}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* 📝 ACLARACIONES DEL EMPLEADO */}
          <div style={{ marginTop: '20px', paddingTop: '18px', borderTop: `1px solid ${borderCol}` }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', flexWrap: 'wrap', marginBottom: '10px' }}>
              <div><h3 style={{ margin: 0, color: textCol, fontSize: '16px' }}>📝 Aclaraciones de asistencia</h3><div style={{ fontSize: '11px', color: '#718096', marginTop: '3px' }}>Las marcas originales no se modifican. Las aclaraciones se agregan como registros complementarios.</div></div>
              <button type="button" onClick={() => { setEmployeeAdjustmentOriginalId(''); setEmployeeAdjustmentType('Salida'); }} style={{ padding: '7px 10px', border: 'none', borderRadius: '6px', background: '#3182ce', color: '#fff', cursor: 'pointer', fontWeight: 'bold' }}>➕ Nueva aclaración</button>
            </div>

            <form onSubmit={handleCreateEmployeeAdjustment} style={{ padding: '12px', borderRadius: '8px', border: `1px solid ${borderCol}`, background: isDarkMode ? '#131924' : '#f8fafc' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '9px', alignItems: 'end' }}>
                <div><label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: textCol, marginBottom: '4px' }}>Registro relacionado</label><select value={employeeAdjustmentOriginalId} onChange={e => { const id = e.target.value; setEmployeeAdjustmentOriginalId(id); const rec = checkins.find(c => String(c.id) === String(id)); if (rec) setEmployeeAdjustmentType(rec.type === 'Entrada' ? 'Salida' : 'Entrada'); }} style={{ width: '100%', padding: '7px', borderRadius: '6px', border: `1px solid ${borderCol}`, background: cardBg, color: textCol }}><option value="">Sin registro relacionado</option>{checkins.slice().sort((a, b) => obtenerClaveCronologicaTimestamp(b.timestamp) - obtenerClaveCronologicaTimestamp(a.timestamp) || Number(b.id || 0) - Number(a.id || 0)).slice(0,30).map(c => <option key={c.id} value={c.id}>#{c.id} · {c.type} · {formatearTimestampMerida(c.timestamp)}</option>)}</select></div>
                <div><label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: textCol, marginBottom: '4px' }}>Marca a aclarar</label><select value={employeeAdjustmentType} onChange={e => setEmployeeAdjustmentType(e.target.value)} style={{ width: '100%', padding: '7px', borderRadius: '6px', border: `1px solid ${borderCol}`, background: cardBg, color: textCol }}><option value="Entrada">📥 Entrada</option><option value="Salida">📤 Salida</option></select></div>
                <div><label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: textCol, marginBottom: '4px' }}>Fecha y hora propuesta</label><input type="datetime-local" value={employeeAdjustmentDateTime} onChange={e => setEmployeeAdjustmentDateTime(e.target.value)} style={{ width: '100%', boxSizing: 'border-box', padding: '7px', borderRadius: '6px', border: `1px solid ${borderCol}`, background: cardBg, color: textCol }} /></div>
                <div><label style={{ display: 'block', fontSize: '11px', fontWeight: 'bold', color: textCol, marginBottom: '4px' }}>Motivo</label><input value={employeeAdjustmentReason} onChange={e => setEmployeeAdjustmentReason(e.target.value)} placeholder="Ej. Olvidé registrar mi salida" style={{ width: '100%', boxSizing: 'border-box', padding: '7px', borderRadius: '6px', border: `1px solid ${borderCol}`, background: cardBg, color: textCol }} /></div>
              </div>
              <button type="submit" disabled={employeeAdjustmentSubmitting} style={{ marginTop: '9px', padding: '8px 12px', border: 'none', borderRadius: '6px', background: employeeAdjustmentSubmitting ? '#718096' : '#319795', color: '#fff', cursor: employeeAdjustmentSubmitting ? 'wait' : 'pointer', fontWeight: 'bold' }}>{employeeAdjustmentSubmitting ? '⏳ Enviando...' : (['administrador','superadministrador','superadministradorempresarial'].includes(String(user?.role || '').trim().toLowerCase()) ? '📨 Enviar aclaración a otro administrador' : '📨 Enviar aclaración al administrador')}</button>
            </form>

            <div style={{ marginTop: '12px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {myAdjustments.map(a => {
                const pendingFromAdmin = a.status === 'PENDIENTE' && ['administrador','superadministrador','superadministradorempresarial'].includes(String(a.requestedByRole || '').toLowerCase());
                return <div key={a.id} style={{ padding: '10px', borderRadius: '7px', border: `1px solid ${pendingFromAdmin ? '#dd6b20' : borderCol}`, background: isDarkMode ? '#131924' : '#fff' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}><strong style={{ color: textCol }}>A-{a.id} · {a.proposedType} · {formatearTimestampMerida(a.proposedTimestamp)}</strong><span style={{ fontSize: '10px', fontWeight: 'bold', color: a.status === 'PENDIENTE' ? '#c05621' : a.status === 'ACEPTADA' ? '#2f855a' : '#c53030' }}>{a.status}</span></div>
                  <div style={{ marginTop: '4px', fontSize: '11px', color: '#718096' }}>{a.originalAttendanceId ? `Relacionado con registro #${a.originalAttendanceId}` : 'Ajuste sin registro original'}</div>
                  <div style={{ marginTop: '4px', fontSize: '11px', color: textCol }}>Motivo: {a.reason}</div>
                  {pendingFromAdmin && <div style={{ display: 'flex', gap: '7px', marginTop: '8px' }}><button type="button" onClick={() => handleEmployeeReviewAdjustment(a.id, 'ACEPTADA')} style={{ padding: '6px 9px', border: 'none', borderRadius: '5px', background: '#38a169', color: '#fff', cursor: 'pointer', fontWeight: 'bold' }}>✅ Aceptar ajuste</button><button type="button" onClick={() => handleEmployeeReviewAdjustment(a.id, 'RECHAZADA')} style={{ padding: '6px 9px', border: 'none', borderRadius: '5px', background: '#e53e3e', color: '#fff', cursor: 'pointer', fontWeight: 'bold' }}>❌ Rechazar</button></div>}
                </div>;
              })}
              {myAdjustments.length === 0 && <div style={{ padding: '12px', textAlign: 'center', color: '#a0aec0', fontSize: '11px' }}>No tienes aclaraciones registradas.</div>}
            </div>
          </div>
        </section>
          </>
        )}
      </main>
      )}
        </div>
      </div>

      {!embedded && (
        <nav className="dashboard-mobile-bottomnav dashboard-personal-bottomnav" style={{ '--dashboard-mobile-bg': isDarkMode ? '#0f172a' : '#ffffff', '--dashboard-mobile-border': borderCol, color: textCol }}>
          <button type="button" className={personalView === 'personalRecords' ? 'active' : ''} onClick={() => setPersonalView('personalRecords')}><span className="nav-icon">📋</span><span>Registros</span></button>
          <button type="button" className={personalView === 'personalAdjustments' ? 'active' : ''} onClick={() => setPersonalView('personalAdjustments')}><span className="nav-icon">📝</span><span>Aclaraciones</span>{myAdjustments.filter(a => String(a?.status || '').toUpperCase() === 'PENDIENTE').length > 0 && <span className="dashboard-alert-badge" style={{ position: 'absolute', marginTop: '-22px', marginLeft: '20px' }}>{myAdjustments.filter(a => String(a?.status || '').toUpperCase() === 'PENDIENTE').length > 99 ? '99+' : myAdjustments.filter(a => String(a?.status || '').toUpperCase() === 'PENDIENTE').length}</span>}</button>
          <button type="button" className={personalView === 'personalNotifications' ? 'active' : ''} onClick={() => setPersonalView('personalNotifications')}><span className="nav-icon">🔔</span><span>Alertas</span>{personalNotificationRows.filter(n => String(n?.status || '').toUpperCase() === 'NUEVA').length > 0 && <span className="dashboard-alert-badge" style={{ position: 'absolute', marginTop: '-22px', marginLeft: '20px' }}>{personalNotificationRows.filter(n => String(n?.status || '').toUpperCase() === 'NUEVA').length > 99 ? '99+' : personalNotificationRows.filter(n => String(n?.status || '').toUpperCase() === 'NUEVA').length}</span>}</button>
          <button type="button" className={personalView === 'personalLeave' ? 'active' : ''} onClick={() => setPersonalView('personalLeave')}><span className="nav-icon">🏖️</span><span>Permisos</span></button>
          <button type="button" className={personalView === 'account' ? 'active' : ''} onClick={() => setPersonalView('account')}><span className="nav-icon">👤</span><span>Perfil</span></button>
        </nav>
      )}

    </div>
    );
  };

  return (
    <div className="dashboard-mobile-safe" style={{ marginTop: '40px' }}>
      {isAdministrativeRolePersonal ? (
        <AdminPanel
          token={token}
          isDarkMode={isDarkMode}
          companySettings={companySettings}
          onCompanySettingsChanged={setCompanySettings}
          companyId={user?.companyId}
          user={user}
          jornadaConfirmationBusy={jornadaConfirmationBusy}
          confirmNotificationJornadaClosure={confirmNotificationJornadaClosure}
          onPersonalView={setPersonalView}
          personalView={personalView}
          personalContent={renderPersonalPortal}
          monitorViewMode={monitorViewMode}
          setMonitorViewMode={setMonitorViewMode}
        />
      ) : (
        renderPersonalPortal()
      )}
    </div>
  );

};

export default Dashboard;
